import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import API from "./api";
import BirthLocationMapField from "./BirthLocationMapField";
import {
  CurrencyAmountInput,
  normalizeOccupationCurrencyCode,
} from "./currencyOptions";
import HeightInputField from "./HeightInputField";
import SearchableSelect from "./SearchableSelect";
import {
  getContactValueFieldLabel,
  getIndianPhoneInputValue,
  IndianPhoneInput,
  isIndianPhoneContactType,
  normalizeIndianPhone,
  toStoredIndianPhoneValue,
  validateIndianPhoneValue,
} from "./indiaPhone";
import { hasPermission } from "./rbac";
import SakhaAssignmentsField from "./SakhaAssignmentsField";
import TimeInput12Hour from "./TimeInput12Hour";
import { uploadProfileDocuments } from "./uploadDocuments";
import {
  MAX_DOCUMENT_UPLOAD_FILE_SIZE_BYTES,
  MAX_UPLOAD_FILE_SIZE_BYTES,
  MAX_UPLOAD_FILE_SIZE_LABEL,
  prepareFilesForUpload,
} from "./uploadFileCompression";

const emptyEducation = {
  education_category_id: "",
  degree_name: "",
  education_description: "",
  institution_name: "",
  education_country_id: "",
  education_city_id: "",
  education_location: "",
  passing_year: "",
  education_status: "completed",
  is_highest_degree: false,
  sort_order: "",
};

const emptyRelativeEducation = {
  education_category_id: "",
  degree_name: "",
  education_description: "",
  institution_name: "",
  education_country_id: "",
  education_city_id: "",
  education_location: "",
  passing_year: "",
  is_highest_degree: true,
  sort_order: 1,
};

const emptyRelative = {
  relation_type_id: "",
  relation_type: "",
  family_side: "none",
  first_name: "",
  middle_name: "",
  last_name: "",
  full_name: "",
  gender: "",
  phone: "",
  email: "",
  contacts: [],
  city_id: "",
  marital_status: "",
  partner_name: "",
  is_future_prospect: false,
  future_prospect_birth_date: "",
  future_prospect_eligible_year: "",
  is_deceased: false,
  occupation_type_id: "",
  occupation_type: "",
  occupation_details: "",
  company_name: "",
  work_profile: "",
  status: "active",
  sort_order: "",
  education: [{ ...emptyRelativeEducation }],
};

const emptyOccupation = {
  occupation_type_id: "",
  occupation_type: "",
  company_name: "",
  occupation_details: "",
  work_profile: "",
  annual_income_amount: "",
  annual_income_currency_code: "INR",
  annual_turnover_amount: "",
  annual_turnover_currency_code: "INR",
  is_primary: false,
  sort_order: "",
  status: "active",
};

const emptyRelativeOccupation = {
  ...emptyOccupation,
  is_primary: true,
  sort_order: 1,
};

const emptyContact = {
  contact_type: "mobile",
  contact_value: "",
  normalized_value: "",
  contact_person_name: "",
  contact_person_relation: "self",
  is_primary: false,
  is_verified: false,
};

const emptyAddress = {
  address_type: "current",
  country_id: "",
  address_line1: "",
  address_line2: "",
  state_id: "",
  district_id: "",
  tehsil_id: "",
  city_id: "",
  village_id: "",
  visa_status_id: "",
  visa_name_id: "",
  pincode: "",
};

const emptyPersona = {
  tier: "",
  status: "active",
  completeness_pct: "",
  payment_amount: "",
  valid_from: "",
  valid_until: "",
};

const emptyPayment = {
  amount: "",
  payment_status: "paid",
  payment_method: "",
  transaction_ref: "",
  paid_at: "",
};

const FAMILY_RELATION_TYPES = new Set(["mother", "father", "sister", "brother"]);

export default function EditProfile({ profileId, token, user, onBack, onSaved, onViewProfile }) {
  const [form, setForm] = useState(null);
  const [originalForm, setOriginalForm] = useState(null);
  const [lookups, setLookups] = useState({
    genders: [],
    maritalStatuses: [],
    complexions: [],
    bloodGroups: [],
    occupationTypes: [],
    relationTypes: [],
    locations: [],
    cityTiers: [],
    tags: [],
    castes: [],
    sakhas: [],
    sampradays: [],
    educationCategories: [],
    degreeNames: [],
    documentTypes: [],
    referralSources: [],
    visaStatuses: [],
    visaNames: [],
  });
  const visibleGenders = useMemo(
    () =>
      safeArray(lookups.genders).filter(
        (g) => String(g?.name || g?.display_label || "").trim().toLowerCase() !== "other"
      ),
    [lookups.genders]
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saveNotice, setSaveNotice] = useState("");
  const [familyEditor, setFamilyEditor] = useState(null);
  const [relativeEditor, setRelativeEditor] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentDraft, setPaymentDraft] = useState({ ...emptyPayment });
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [addingPayment, setAddingPayment] = useState(false);

  // Duplicate check state before save
  const [duplicateChecked, setDuplicateChecked] = useState(false);
  const [duplicateChecking, setDuplicateChecking] = useState(false);
  const [duplicateMatches, setDuplicateMatches] = useState([]);
  const [duplicateOverride, setDuplicateOverride] = useState(false);
  const [activeSection, setActiveSection] = useState("duplicate");

  // Image state
  const [existingFiles, setExistingFiles] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [newImageFiles, setNewImageFiles] = useState([]);
  const [newDocumentFiles, setNewDocumentFiles] = useState([]);
  const [documentTypeId, setDocumentTypeId] = useState("");
  const [preparingImageFiles, setPreparingImageFiles] = useState(false);
  const [imagePreparationStatus, setImagePreparationStatus] = useState("");
  const [preparingDocumentFiles, setPreparingDocumentFiles] = useState(false);
  const [documentPreparationStatus, setDocumentPreparationStatus] = useState("");
  const [primaryFileId, setPrimaryFileId] = useState(null);
  const [newPrimaryIdx, setNewPrimaryIdx] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deletingDocumentId, setDeletingDocumentId] = useState(null);
  const [uploadingImgs, setUploadingImgs] = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [settingPrimaryId, setSettingPrimaryId] = useState(null);
  const [creatingSakha, setCreatingSakha] = useState(false);
  const fileInputRef = useRef(null);
  const documentInputRef = useRef(null);

  const h = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const hasGenericProfileEdit = hasPermission(user, "profiles", "edit");
  const canEditBasicDetails = hasGenericProfileEdit || hasPermission(user, "profiles", "edit", "basic_details");
  const canEditCommunity = hasGenericProfileEdit || hasPermission(user, "profiles", "edit", "community") || canEditBasicDetails;
  const canEditContacts = hasGenericProfileEdit || hasPermission(user, "profiles", "edit", "contacts") || canEditBasicDetails;
  const canEditAddresses = hasGenericProfileEdit || hasPermission(user, "profiles", "edit", "addresses") || canEditBasicDetails;
  const canEditEducationCareer = hasGenericProfileEdit || hasPermission(user, "profiles", "edit", "education_career") || canEditBasicDetails;
  const canEditFamilyDetails = hasGenericProfileEdit || hasPermission(user, "profiles", "edit", "family_details") || hasPermission(user, "profiles", "edit", "family_relatives");
  const canEditRelatives = hasGenericProfileEdit || hasPermission(user, "profiles", "edit", "family_relatives");
  const canEditPreferences = hasGenericProfileEdit || hasPermission(user, "profiles", "edit", "preferences");
  const canEditPersona = hasGenericProfileEdit || hasPermission(user, "profiles", "edit", "persona");
  const canEditPayments = hasGenericProfileEdit || hasPermission(user, "profiles", "edit", "payments");
  const canEditTags = hasGenericProfileEdit || hasPermission(user, "profiles", "edit", "tags");
  const canManagePhotos = hasGenericProfileEdit || hasPermission(user, "profiles", "edit", "photos");
  const canEditDocuments = hasGenericProfileEdit || hasPermission(user, "profiles", "edit", "documents");
  const canSetPrimaryPhoto = hasGenericProfileEdit || hasPermission(user, "profiles", "edit", "primary_photo");
  const canRunDuplicateCheck =
    hasPermission(user, "profiles", "search") ||
    hasGenericProfileEdit ||
    hasPermission(user, "profiles", "edit", "duplicate_check") ||
    canEditBasicDetails;
  const canEditProfile =
    hasGenericProfileEdit ||
    canEditBasicDetails ||
    canEditCommunity ||
    canEditContacts ||
    canEditAddresses ||
    canEditEducationCareer ||
    canEditFamilyDetails ||
    canEditPreferences ||
    canEditPersona ||
    canEditPayments ||
    canEditTags ||
    canManagePhotos ||
    canEditDocuments ||
    canSetPrimaryPhoto;
  const policyRestrictions = useMemo(() => {
    const items = [];
    if (!canEditProfile) {
      items.push("This profile is view-only for your role.");
      return items;
    }
    if (!canEditBasicDetails) items.push("Basic details are read-only.");
    if (!canEditCommunity) items.push("Community details are read-only.");
    if (!canEditContacts) items.push("Contacts are read-only.");
    if (!canEditAddresses) items.push("Addresses are read-only.");
    if (!canEditEducationCareer) items.push("Education and career are read-only.");
    if (!canEditFamilyDetails) items.push("Family details are read-only.");
    if (!canEditRelatives) items.push("Relative details are read-only.");
    if (!canEditPreferences) items.push("Partner preferences are read-only.");
    if (!canEditPersona) items.push("Persona/package details are read-only.");
    if (!canEditPayments) items.push("Payment history updates are read-only.");
    if (!canEditTags) items.push("Tag updates are read-only.");
    if (!canManagePhotos) items.push("Photo uploads and deletes are read-only.");
    if (!canEditDocuments) items.push("Document uploads are read-only.");
    if (!canRunDuplicateCheck) items.push("Duplicate check is read-only.");
    return items;
  }, [
    canEditAddresses,
    canEditBasicDetails,
    canEditCommunity,
    canEditContacts,
    canEditDocuments,
    canEditEducationCareer,
    canEditFamilyDetails,
    canEditRelatives,
    canEditPayments,
    canEditPersona,
    canEditPreferences,
    canEditProfile,
    canEditTags,
    canManagePhotos,
    canRunDuplicateCheck,
  ]);

  const canSaveSection = (sectionKey) => {
    switch (sectionKey) {
      case "duplicate":
      case "identity":
        return canEditBasicDetails;
      case "personal":
        return canEditCommunity;
      case "contacts":
        return canEditContacts;
      case "addresses":
        return canEditAddresses;
      case "education":
        return canEditEducationCareer;
      case "family":
        return canEditFamilyDetails;
      case "relatives":
        return canEditRelatives;
      case "preferences":
        return canEditPreferences;
      case "persona":
        return canEditPersona;
      case "payments":
        return canEditPayments;
      case "tags":
        return canEditTags;
      case "photos":
        return canManagePhotos;
      case "documents":
        return canEditDocuments;
      default:
        return canEditProfile;
    }
  };

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const [
          pRes,
          gRes,
          msRes,
          cxRes,
          bgRes,
          occupationTypeRes,
          relationTypeRes,
          locRes,
          cityTiersRes,
          tagRes,
          casteRes,
          sakhaRes,
          sampradayRes,
          eduCatRes,
          degreeNameRes,
          dtRes,
          referralSourcesRes,
          vsRes,
          vnRes,
        ] = await Promise.all([
          API.get(`/profiles/${profileId}`, h),
          API.get("/genders"),
          API.get("/marital-status"),
          API.get("/complexions"),
          API.get("/blood-groups"),
          API.get("/occupation-types"),
          API.get("/relation-types"),
          API.get("/locations"),
          API.get("/city-tiers"),
          API.get("/tags"),
          API.get("/castes"),
          API.get("/sakhas"),
          API.get("/sampradays"),
          API.get("/education-categories"),
          API.get("/degree-names"),
          API.get("/document-types"),
          API.get("/referral-sources", h),
          API.get("/visa-statuses"),
          API.get("/visa-names"),
        ]);

        if (!mounted) return;

        const lookupData = {
          genders: safeArray(gRes.data),
          maritalStatuses: safeArray(msRes.data),
          complexions: safeArray(cxRes.data),
          bloodGroups: safeArray(bgRes.data),
          occupationTypes: safeArray(occupationTypeRes.data),
          relationTypes: safeArray(relationTypeRes.data),
          locations: safeArray(locRes.data),
          cityTiers: safeArray(cityTiersRes.data),
          tags: safeArray(tagRes.data),
          castes: safeArray(casteRes.data),
          sakhas: safeArray(sakhaRes.data),
          sampradays: safeArray(sampradayRes.data),
          educationCategories: safeArray(eduCatRes.data),
          degreeNames: safeArray(degreeNameRes.data),
          documentTypes: safeArray(dtRes.data),
          referralSources: safeArray(referralSourcesRes.data),
          visaStatuses: safeArray(vsRes.data),
          visaNames: safeArray(vnRes.data),
        };
        const p = pRes.data;
        const mapped = mapProfileToForm(p, lookupData);
        setForm(mapped);
        setOriginalForm(mapped);
        setPaymentHistory(safeArray(p.payments));
        setPaymentDraft({ ...emptyPayment });
        setShowAddPayment(false);
        setExistingFiles(Array.isArray(p.media) ? p.media.filter((m) => m.media_type === "photo") : Array.isArray(p.files) ? p.files : []);
        setExistingDocuments(Array.isArray(p.media) ? p.media.filter((m) => m.media_type && m.media_type !== "photo") : []);
        setPrimaryFileId((p.media || p.files || []).find((f) => f.is_primary)?.id || null);
        setLookups(lookupData);
      } catch (err) {
        console.error("Edit profile load failed:", err);
        if (mounted) setError(err.response?.data?.error || "Failed to load profile data.");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [profileId, h]);

  useEffect(() => {
    if (!saveNotice) return undefined;
    const timer = window.setTimeout(() => setSaveNotice(""), 3000);
    return () => window.clearTimeout(timer);
  }, [saveNotice]);

  const filteredSakhas = useMemo(() => {
    if (!form?.caste_id) return lookups.sakhas;
    return lookups.sakhas.filter((s) => Number(s.caste_id) === Number(form.caste_id));
  }, [lookups.sakhas, form?.caste_id]);

  const familyRelationOptions = useMemo(
    () => safeArray(lookups.relationTypes).filter((relationType) => isFamilyRelationType(relationType.name || relationType.display_label)),
    [lookups.relationTypes]
  );

  const sideRelativeRelationOptions = useMemo(
    () => safeArray(lookups.relationTypes).filter((relationType) => !isFamilyRelationType(relationType.name || relationType.display_label)),
    [lookups.relationTypes]
  );

  const familyEntries = useMemo(
    () => safeArray(form?.relatives)
      .map((relative, index) => ({ relative: normalizeRelativeForSection(relative, lookups.relationTypes), index }))
      .filter((item) => isFamilySectionRelative(item.relative, lookups.relationTypes)),
    [form?.relatives, lookups.relationTypes]
  );

  const sideRelativeEntries = useMemo(
    () => safeArray(form?.relatives)
      .map((relative, index) => ({ relative: normalizeRelativeForSection(relative, lookups.relationTypes), index }))
      .filter((item) => !isFamilySectionRelative(item.relative, lookups.relationTypes)),
    [form?.relatives, lookups.relationTypes]
  );

  const preferredCityOptions = useMemo(
    () => {
      const cityTierMap = buildCityTierMap(lookups.cityTiers);
      return uniqueBy(
        lookups.locations.map((location) => ({
          id: Number(location.id),
          label: getCompactLocationOptionLabel(location),
          tierIds: cityTierMap.get(Number(location.id)) || [],
        })),
        "id"
      ).sort((a, b) => a.label.localeCompare(b.label));
    },
    [lookups.cityTiers, lookups.locations]
  );

  const preferredSakhaOptions = useMemo(
    () => filteredSakhas.map((sakha) => ({ id: Number(sakha.id), label: sakha.name })),
    [filteredSakhas]
  );

  const preferredEducationOptions = useMemo(
    () => lookups.educationCategories.map((category) => ({ id: Number(category.id), label: category.name })).sort((a, b) => a.label.localeCompare(b.label)),
    [lookups.educationCategories]
  );

  const preferredDegreeOptions = useMemo(
    () =>
      uniqueBy(
        lookups.degreeNames.map((degree) => {
          const label = degree.name || degree.display_label || "";
          return { id: label, label };
        }),
        "id"
      ).filter((option) => option.id && option.label).sort((a, b) => a.label.localeCompare(b.label)),
    [lookups.degreeNames]
  );

  const preferredOccupationOptions = useMemo(
    () =>
      lookups.occupationTypes.map((occupationType) => ({
        id: occupationType.name || occupationType.display_label,
        label: occupationType.name || occupationType.display_label,
      })),
    [lookups.occupationTypes]
  );

  const tagOptions = useMemo(
    () => lookups.tags.map((tag) => ({ id: Number(tag.id), label: tag.name })),
    [lookups.tags]
  );
  const profileFamilyMiddleName = form?.middle_name || "";
  const profileFamilyLastName = form?.last_name || "";

  useEffect(() => {
    const profileNameDefaults = {
      middle_name: profileFamilyMiddleName,
      last_name: profileFamilyLastName,
    };
    setForm((prev) => {
      if (!prev) return prev;
      const nextRelatives = normalizeRelativeSections(
        applyProfileFamilyNameDefaultsToRelatives(prev.relatives, profileNameDefaults, lookups.relationTypes),
        lookups.relationTypes
      );
      return nextRelatives === prev.relatives ? prev : { ...prev, relatives: nextRelatives };
    });
  }, [profileFamilyMiddleName, profileFamilyLastName, lookups.relationTypes]);

  const personaTierOptions = useMemo(() => {
    const currentTier = form?.persona?.tier ? String(form.persona.tier).trim() : "";
    const rawOptions = ["", currentTier, "basic", "premium", "elite", "vip", "premium_family"];
    return Array.from(new Set(rawOptions.filter((value, index) => index === 0 || value))).map((value) => ({
      value,
      label: value ? formatTierLabel(value) : "No package",
    }));
  }, [form?.persona?.tier]);

  const identityChanged = useMemo(() => {
    if (!form || !originalForm) return false;

    const fields = [
      "first_name",
      "middle_name",
      "last_name",
      "gender_id",
      "gender",
      "birth_date",
      "birth_time",
      "birth_place_city_id",
    ];

    return fields.some((f) => String(form[f] || "") !== String(originalForm[f] || ""));
  }, [form, originalForm]);

  const phoneticKey = useMemo(() => {
    if (!form) return "";
    return simpleProfilePhoneticKey(
      form.first_name,
      form.middle_name,
      form.last_name,
      [form.first_name, form.middle_name, form.last_name].filter(Boolean).join(" ")
    );
  }, [form]);

// Form helpers
  const set = (field) => (event) => {
    const value = event?.target ? event.target.value : event;
    setForm((prev) => {
      const next = { ...prev, [field]: value };

      if (field === "dob" || field === "birth_date") {
        next.dob = value;
        next.birth_date = value;
      }

      if (field === "city_id" || field === "current_location_id") {
        next.city_id = value;
        next.current_location_id = value;
      }

      if (field === "birth_place_city_id" || field === "birth_place_id") {
        next.birth_place_city_id = value;
        next.birth_place_id = value;
      }

      if (field === "height" || field === "height_cm") {
        next.height = value;
        next.height_cm = value;
      }

      if (field === "marital_status_id" && isSingleMaritalStatusId(value, lookups.maritalStatuses)) {
        next.has_children = false;
        next.marital_field_details = "";
      }

      if (field === "caste_id") {
        next.sakha_id = "";
        next.dadisa_sakha_id = "";
        next.nanisa_sakha_id = "";
        next.mamasa_sakha_id = "";
      }

      applyLocationCascade(next, field, value, lookups.locations);

      return next;
    });

    if (isDuplicateSignalField(field)) resetDuplicateCheck();
  };

  const setNested = (parent, field) => (event) => {
    const value = event?.target ? event.target.value : event;
    setForm((prev) => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value },
    }));
  };

  const setListItem = (list, index, field) => (event) => {
    const value = event?.target ? event.target.value : event;
    setForm((prev) => {
      const updated = [...prev[list]];
      let nextItem = { ...updated[index], [field]: value };
      if (list === "educations" && field === "education_category_id") {
        nextItem.degree_name = "";
      }
      if (list === "educations" && field === "education_city_id") {
        const city = lookups.locations.find((location) => Number(location.id) === Number(value));
        if (city) {
          nextItem.education_country_id = getLocationCountryId(city);
          nextItem.education_location = getLocationOptionLabel(city);
        } else if (!value) {
          nextItem.education_country_id = "";
          nextItem.education_location = "";
        }
      }
      if (list === "relatives" && (field === "relation_type_id" || field === "relation_type") && !shouldShowRelativeMaritalStatus(nextItem, lookups.relationTypes)) {
        nextItem = clearRelativeMaritalFields(nextItem);
      }
      if (list === "relatives" && (field === "relation_type_id" || field === "relation_type")) {
        nextItem = applyRelativeGenderMapping(nextItem, lookups.relationTypes);
      }
      if (list === "relatives" && field === "marital_status") {
        if (!isRelativeUnmarriedStatus(value)) {
          nextItem = clearRelativeFutureProspectFields(nextItem);
        }
        if (!isRelativeMarriedStatus(value)) {
          nextItem = clearRelativePartnerField(nextItem);
        }
      }
      if (list === "relatives") {
        nextItem = applyProfileFamilyNameDefaults(nextItem, prev, lookups.relationTypes);
        nextItem = applySiblingNameDefaults(nextItem, updated, index, lookups.relationTypes);
        nextItem = normalizeRelativeForSection(nextItem, lookups.relationTypes);
      }
      updated[index] = nextItem;
      return { ...prev, [list]: updated };
    });
  };

  const setOccupationField = (index, field) => (event) => {
    const value = event?.target ? event.target.value : event;
    setForm((prev) => {
      const occupations = [...prev.occupations];
      const nextOccupation = { ...occupations[index], [field]: value };

      if (field === "occupation_type_id" || field === "occupation_type") {
        const occupationTypeLabel =
          field === "occupation_type"
            ? value
            : getLookupName(lookups.occupationTypes, value) || occupations[index]?.occupation_type || "";

        if (isServiceOccupationTypeLabel(occupationTypeLabel)) {
          nextOccupation.annual_turnover_amount = "";
          nextOccupation.annual_turnover_currency_code = "INR";
        } else {
          nextOccupation.annual_income_amount = "";
          nextOccupation.annual_income_currency_code = "INR";
        }
      }

      occupations[index] = nextOccupation;
      return { ...prev, occupations };
    });
  };

  const setRelativeEducation = (relativeIndex, eduIndex, field) => (event) => {
    const value = event?.target ? event.target.value : event;
    setForm((prev) => {
      const relatives = [...prev.relatives];
      const education = [...(relatives[relativeIndex].education || [])];
      education[eduIndex] = { ...education[eduIndex], [field]: value };
      if (field === "education_category_id") {
        education[eduIndex].degree_name = "";
      }
      relatives[relativeIndex] = { ...relatives[relativeIndex], education };
      return { ...prev, relatives };
    });
  };

  const setRelativeOccupation = (relativeIndex, occupationIndex, field) => (event) => {
    const value = event?.target ? event.target.value : event;
    setForm((prev) => {
      const relatives = [...prev.relatives];
      const occupations = getEditableRelativeOccupations(relatives[relativeIndex]);
      const nextOccupation = { ...occupations[occupationIndex], [field]: value };

      if (field === "occupation_type_id" || field === "occupation_type") {
        const occupationTypeLabel =
          field === "occupation_type"
            ? value
            : getLookupName(lookups.occupationTypes, value) || occupations[occupationIndex]?.occupation_type || "";

        if (isServiceOccupationTypeLabel(occupationTypeLabel)) {
          nextOccupation.annual_turnover_amount = "";
          nextOccupation.annual_turnover_currency_code = "INR";
        } else {
          nextOccupation.annual_income_amount = "";
          nextOccupation.annual_income_currency_code = "INR";
        }
      }

      occupations[occupationIndex] = nextOccupation;
      relatives[relativeIndex] = {
        ...relatives[relativeIndex],
        occupations: normalizeRelativeOccupationRows(occupations),
      };
      return { ...prev, relatives };
    });
  };

  const addRelativeOccupation = (relativeIndex) => {
    setForm((prev) => {
      const relatives = [...prev.relatives];
      const occupations = getEditableRelativeOccupations(relatives[relativeIndex]);
      relatives[relativeIndex] = {
        ...relatives[relativeIndex],
        occupations: normalizeRelativeOccupationRows([
          ...occupations,
          { ...emptyOccupation, sort_order: occupations.length + 1 },
        ]),
      };
      return { ...prev, relatives };
    });
  };

  const removeRelativeOccupation = (relativeIndex, occupationIndex) => {
    setForm((prev) => {
      const relatives = [...prev.relatives];
      const occupations = getEditableRelativeOccupations(relatives[relativeIndex]).filter((_, index) => index !== occupationIndex);
      relatives[relativeIndex] = {
        ...relatives[relativeIndex],
        occupations: normalizeRelativeOccupationRows(occupations.length ? occupations : [{ ...emptyRelativeOccupation }]),
      };
      return { ...prev, relatives };
    });
  };

  const addListItem = (list, template) => {
    setForm((prev) => ({
      ...prev,
      [list]: [...prev[list], { ...template }],
    }));
  };

  const addFamilyMember = () => {
    const nextIndex = form.relatives.length;
    addListItem("relatives", { ...emptyRelative, family_side: "none", sort_order: nextIndex + 1 });
    setFamilyEditor({ index: nextIndex, isNew: true });
  };

  const openFamilyEditor = (index) => {
    setFamilyEditor({ index, isNew: false });
  };

  const closeFamilyEditor = () => {
    if (familyEditor?.isNew) {
      removeListItem("relatives", familyEditor.index);
    }
    setFamilyEditor(null);
  };

  const finalizeFamilyEditor = () => {
    setFamilyEditor(null);
  };

  const removeFamilyMember = (index) => {
    removeListItem("relatives", index);
    if (familyEditor?.index === index) setFamilyEditor(null);
  };

  const addSideRelative = () => {
    const nextIndex = form.relatives.length;
    addListItem("relatives", { ...emptyRelative, family_side: "paternal", sort_order: nextIndex + 1 });
    setRelativeEditor({ index: nextIndex, isNew: true });
  };

  const openRelativeEditor = (index) => {
    setRelativeEditor({ index, isNew: false });
  };

  const closeRelativeEditor = () => {
    if (relativeEditor?.isNew) {
      removeListItem("relatives", relativeEditor.index);
    }
    setRelativeEditor(null);
  };

  const finalizeRelativeEditor = () => {
    setRelativeEditor(null);
  };

  const removeSideRelative = (index) => {
    removeListItem("relatives", index);
    if (relativeEditor?.index === index) setRelativeEditor(null);
  };

  const removeListItem = (list, index) => {
    setForm((prev) => ({
      ...prev,
      [list]: prev[list].filter((_, i) => i !== index),
    }));
  };

  const setRelativeContact = (relativeIndex, contactIndex, field) => (event) => {
    const value = event?.target ? event.target.value : event;
    setForm((prev) => {
      const relatives = [...prev.relatives];
      const contacts = [...(relatives[relativeIndex].contacts || [])];
      contacts[contactIndex] = { ...contacts[contactIndex], [field]: value };
      relatives[relativeIndex] = { ...relatives[relativeIndex], contacts };
      return { ...prev, relatives };
    });
  };

  const addRelativeContact = (relativeIndex) => {
    setForm((prev) => {
      const relatives = [...prev.relatives];
      relatives[relativeIndex] = {
        ...relatives[relativeIndex],
        contacts: [...(relatives[relativeIndex].contacts || []), { contact_type: "mobile", contact_value: "" }],
      };
      return { ...prev, relatives };
    });
  };

  const removeRelativeContact = (relativeIndex, contactIndex) => {
    setForm((prev) => {
      const relatives = [...prev.relatives];
      relatives[relativeIndex] = {
        ...relatives[relativeIndex],
        contacts: (relatives[relativeIndex].contacts || []).filter((_, index) => index !== contactIndex),
      };
      return { ...prev, relatives };
    });
  };

  const setAddressField = (index, field) => (event) => {
    const value = event?.target ? event.target.value : event;
    setForm((prev) => {
      const addresses = [...prev.addresses];
      const next = { ...addresses[index], [field]: value };

      if (field === "country_id") {
        next.state_id = "";
        next.district_id = "";
        next.tehsil_id = "";
        next.city_id = "";
        next.village_id = "";
      }

      if (field === "state_id") {
        next.country_id =
          lookups.locations.find((loc) => Number(loc.state_id) === Number(value))?.country_id || next.country_id || "";
        next.district_id = "";
        next.tehsil_id = "";
        next.city_id = "";
        next.village_id = "";
      }

      if (field === "district_id") {
        next.tehsil_id = "";
        next.city_id = "";
        next.village_id = "";
      }

      if (field === "tehsil_id") {
        next.city_id = "";
        next.village_id = "";
      }

      if (field === "city_id") {
        const city = lookups.locations.find((loc) => Number(loc.id) === Number(value));
        if (city) {
          next.country_id = city.country_id || "";
          next.state_id = city.state_id || "";
          next.district_id = city.district_id || "";
          next.tehsil_id = city.tehsil_id || "";
          next.city_id = city.id || value;
          next.village_id = "";
        }
      }

      syncAddressVisaFields(next, lookups.locations);

      addresses[index] = next;
      return { ...prev, addresses };
    });
  };

  const addRelativeEducation = (relativeIndex) => {
    setForm((prev) => {
      const relatives = [...prev.relatives];
      relatives[relativeIndex] = {
        ...relatives[relativeIndex],
        education: [
          ...(relatives[relativeIndex].education || []),
          { ...emptyRelativeEducation, is_highest_degree: false },
        ],
      };
      return { ...prev, relatives };
    });
  };

  const removeRelativeEducation = (relativeIndex, eduIndex) => {
    setForm((prev) => {
      const relatives = [...prev.relatives];
      relatives[relativeIndex] = {
        ...relatives[relativeIndex],
        education: (relatives[relativeIndex].education || []).filter((_, i) => i !== eduIndex),
      };
      return { ...prev, relatives };
    });
  };

  const toggleTag = (id) => {
    const tagId = parseInt(id, 10);
    setForm((prev) => ({
      ...prev,
      tags: prev.tags.includes(tagId)
        ? prev.tags.filter((t) => t !== tagId)
        : [...prev.tags, tagId],
    }));
  };

  const createTag = async (label) => {
    const name = String(label || "").trim();
    if (!name) return;

    const { data } = await API.post("/tags", { name }, h);
    const tagId = Number(data?.id);
    const tagName = data?.name || name;
    if (!tagId) throw new Error("Tag was created but no tag ID was returned.");

    setLookups((prev) => {
      const tags = safeArray(prev.tags);
      const exists = tags.some((tag) => Number(tag.id) === tagId);
      const nextTags = exists ? tags : [...tags, { ...data, id: tagId, name: tagName }];
      return {
        ...prev,
        tags: nextTags.sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))),
      };
    });

    setForm((prev) => ({
      ...prev,
      tags: prev.tags.includes(tagId) ? prev.tags : [...prev.tags, tagId],
    }));
  };

  const togglePrefValue = (field, rawValue) => {
    const value =
      ["preferred_city_ids", "non_preferred_city_ids", "non_preferred_sakha_ids", "preferred_education_category_ids", "non_preferred_education_category_ids", "preferred_caste_ids"].includes(field)
        || field === "preferred_city_tier_ids"
        ? Number(rawValue)
        : rawValue;
    setForm((prev) => {
      const current = safeArray(prev.preferences[field] || []);
      const exists = current.some((item) => String(item) === String(value));
      return {
        ...prev,
        preferences: {
          ...prev.preferences,
          [field]: exists
            ? current.filter((item) => String(item) !== String(value))
            : [...current, value],
        },
      };
    });
  };

  const resetDuplicateCheck = () => {
    setDuplicateChecked(false);
    setDuplicateOverride(false);
    setDuplicateMatches([]);
  };

// Image handlers
  const handleFileSelect = async (event) => {
    if (!canManagePhotos) {
      setSaveError("Your role does not have permission to manage profile photos.");
      event.target.value = "";
      return;
    }

    const input = event.target;
    const allFiles = Array.from(input.files || []);
    setSaveError("");
    setSaveNotice("");

    if (!allFiles.length) {
      input.value = "";
      return;
    }

    setImagePreparationStatus("");
    const validSizeFiles = await prepareFilesForUpload(allFiles, {
      allowedTypes: ["image/jpeg", "image/png", "image/webp"],
      allowedTypeLabel: "JPEG, PNG and WEBP",
      fileKindLabel: "photo",
      onStatusChange: ({ active, message }) => {
        setPreparingImageFiles(active);
        setImagePreparationStatus(active ? message : "");
      },
    });

    const remaining = Math.max(0, 10 - existingFiles.length - newImageFiles.length);

    if (remaining <= 0) {
      alert("Maximum 10 profile photos are allowed.");
      input.value = "";
      return;
    }

    const entries = validSizeFiles.slice(0, remaining).map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
      type: file.type,
    }));

    if (!entries.length) {
      input.value = "";
      return;
    }

    setNewImageFiles((prev) => [...prev, ...entries]);
    setPreparingImageFiles(false);
    setImagePreparationStatus("");
    input.value = "";
  };

  const removeNewImage = (index) => {
    setNewImageFiles((prev) => {
      URL.revokeObjectURL(prev[index].previewUrl);
      return prev.filter((_, i) => i !== index);
    });

    if (newPrimaryIdx === index) setNewPrimaryIdx(null);
    else if (newPrimaryIdx > index) setNewPrimaryIdx((x) => x - 1);
  };

  const handleDocumentSelect = async (event) => {
    if (!canEditDocuments) {
      setSaveError("Your role does not have permission to upload documents.");
      event.target.value = "";
      return;
    }

    const input = event.target;
    const allFiles = Array.from(input.files || []);
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/plain",
      "image/jpeg",
      "image/png",
    ];

    setSaveError("");

    if (!allFiles.length) {
      input.value = "";
      return;
    }

    setDocumentPreparationStatus("");
    const validSizeFiles = await prepareFilesForUpload(allFiles, {
      allowedTypes,
      allowedTypeLabel: "PDF, DOC, DOCX, XLS, XLSX, TXT, JPEG and PNG",
      fileKindLabel: "document",
      maxBytes: MAX_DOCUMENT_UPLOAD_FILE_SIZE_BYTES,
      autoCompressOversized: true,
      onStatusChange: ({ active, message }) => {
        setPreparingDocumentFiles(active);
        setDocumentPreparationStatus(active ? message : "");
      },
    });

    const remaining = Math.max(0, 10 - newDocumentFiles.length);
    if (remaining <= 0) {
      alert("Maximum 10 documents can be uploaded at once.");
      input.value = "";
      return;
    }

    setNewDocumentFiles((prev) => [
      ...prev,
      ...validSizeFiles.slice(0, remaining).map((file) => ({
        file,
        name: file.name,
        size: file.size,
        type: file.type,
      })),
    ]);

    setPreparingDocumentFiles(false);
    setDocumentPreparationStatus("");
    input.value = "";
  };

  const removeNewDocument = (index) => {
    setNewDocumentFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteExisting = async (fileId) => {
    if (!window.confirm("Delete this photo?")) return;

    setDeletingId(fileId);

    try {
      await API.delete(`/profiles/${profileId}/images/${fileId}`, h);
      setExistingFiles((prev) => prev.filter((f) => f.id !== fileId));
      if (primaryFileId === fileId) setPrimaryFileId(null);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete image.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteExistingDocument = async (fileId) => {
    if (!window.confirm("Delete this document?")) return;

    setDeletingDocumentId(fileId);

    try {
      await API.delete(`/profiles/${profileId}/documents/${fileId}`, h);
      setExistingDocuments((prev) => prev.filter((doc) => doc.id !== fileId));
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete document.");
    } finally {
      setDeletingDocumentId(null);
    }
  };

  const handleSetExistingPrimary = async (fileId) => {
    if (!canSetPrimaryPhoto || fileId === primaryFileId || settingPrimaryId) return;

    setSettingPrimaryId(fileId);

    try {
      await API.patch(`/profiles/${profileId}/images/${fileId}/primary`, {}, h);
      setExistingFiles((prev) => prev.map((f) => ({ ...f, is_primary: f.id === fileId })));
      setPrimaryFileId(fileId);
      setNewPrimaryIdx(null);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to set primary image.");
    } finally {
      setSettingPrimaryId(null);
    }
  };

// Duplicate check
  const handleDuplicateCheck = async () => {
    if (!form) return;
    if (!canRunDuplicateCheck) {
      setSaveError("Your role does not have permission to run duplicate checks.");
      return;
    }

    const requiredError = validateDuplicateFields(form);
    if (requiredError) {
      setSaveError(requiredError);
      return;
    }

    setSaveError("");
    setDuplicateChecking(true);
    setDuplicateChecked(false);
    setDuplicateOverride(false);
    setDuplicateMatches([]);

    try {
      const genderLabel = getLookupName(lookups.genders, form.gender_id) || form.gender;
      const response = await API.get("/profiles/duplicate-check", {
        params: {
          first_name: form.first_name || undefined,
          middle_name: form.middle_name || undefined,
          last_name: form.last_name || undefined,
          gender: genderLabel || undefined,
          birth_date: form.birth_date || form.dob || undefined,
          birth_time: form.birth_time || undefined,
          exclude_profile_id: profileId,
          source: "edit_profile_duplicate_check",
          limit: 100,
        },
      });

      const unique = Array.isArray(response.data) ? response.data : [];

      const source = {
        first_name: form.first_name,
        middle_name: form.middle_name,
        last_name: form.last_name,
        gender: genderLabel,
        birth_date: form.birth_date || form.dob,
        birth_time: form.birth_time,
      };

      const scored = unique
        .map((candidate) => scoreDuplicate(source, candidate))
        .filter((item) => item.strictMatch && (item.score > 0 || (item.reasons || []).length > 0))
        .sort((a, b) => b.score - a.score);

      setDuplicateMatches(scored);
      setDuplicateChecked(true);
    } catch (err) {
      console.error("Duplicate check failed:", err);
      setSaveError(err.response?.data?.error || "Duplicate check failed.");
    } finally {
      setDuplicateChecking(false);
    }
  };

  const createSakha = async (label) => {
    const name = String(label || "")
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
    if (!name || creatingSakha) return null;
    if (!form?.caste_id) {
      alert("Select caste first.");
      return null;
    }

    try {
      setCreatingSakha(true);
      const { data } = await API.post(
        "/admin/rbac/dropdowns/sakhas",
        {
          name,
          caste_id: Number(form.caste_id),
        },
        h
      );
      const item = data?.item || null;
      const sakhaId = Number(item?.id);
      if (!sakhaId) {
        throw new Error("Sakha was created but no ID was returned.");
      }

      setLookups((prev) => {
        const sakhas = safeArray(prev.sakhas);
        const exists = sakhas.some((sakha) => Number(sakha.id) === sakhaId);
        const nextSakhas = exists ? sakhas : [...sakhas, { ...item, id: sakhaId, name: item?.name || name }];
        return {
          ...prev,
          sakhas: nextSakhas.sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))),
        };
      });
      return String(sakhaId);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to create sakha.");
      return null;
    } finally {
      setCreatingSakha(false);
    }
  };

  function announceSectionSaved(sectionKey, extraMessage = "") {
    setSaveNotice(buildSectionSavedNotice(sectionKey, extraMessage));
    onSaved?.();
  }

// Save
  const handleSave = async (sectionKey = activeSection) => {
    if (!canSaveSection(sectionKey)) {
      setSaveError("Your role does not have permission to update this section.");
      return;
    }
    if (!form) return;

    const requiredError = shouldValidateEditSection(sectionKey) ? validateForSave(form) : "";
    if (requiredError) {
      setSaveError(requiredError);
      return;
    }

    if (["family", "relatives"].includes(String(sectionKey || "").trim().toLowerCase())) {
      const relativeContactError = validateRelativePhoneContacts(form.relatives);
      if (relativeContactError) {
        setSaveError(relativeContactError);
        return;
      }
      const futureProspectError = validateFutureProspectRelatives(form.relatives);
      if (futureProspectError) {
        setSaveError(futureProspectError);
        return;
      }
    }

    if (String(sectionKey || "").trim().toLowerCase() === "contacts") {
      const contactError = validatePhoneContacts(form.contacts);
      if (contactError) {
        setSaveError(contactError);
        return;
      }
    }

    setSaving(true);
    setSaveError("");
    setSaveNotice("");

    try {
      const payload = buildSectionPayload(form, lookups, originalForm, sectionKey);

      let documentUploadSummary = "";

      if (sectionRequiresProfileUpdate(sectionKey)) {
        await API.put(`/profiles/${profileId}`, { ...payload, edit_section: sectionKey }, h);
      }

      if (newImageFiles.length > 0) {
        const uploadItems = newImageFiles.filter(
          (item) =>
            item &&
            item.file instanceof File &&
            item.file.size > 0 &&
            ["image/jpeg", "image/png", "image/webp"].includes(item.file.type)
        );

        if (!uploadItems.length) {
          throw new Error("No valid images selected. Please select JPEG, PNG or WEBP photos again.");
        }

        setUploadingImgs(true);

        const formData = new FormData();
        uploadItems.forEach(({ file }) => {
          formData.append("images", file, file.name);
        });

        const shouldUseNewAsPrimary = primaryFileId === null;
        const primaryIndex =
          newPrimaryIdx !== null
            ? newPrimaryIdx
            : shouldUseNewAsPrimary
              ? 0
              : -1;

        formData.append("primary_image_index", String(primaryIndex));

        // Do NOT manually set Content-Type here.
        // Axios/browser must set multipart/form-data with boundary automatically.
        await API.post(`/profiles/${profileId}/images`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        newImageFiles.forEach(({ previewUrl }) => URL.revokeObjectURL(previewUrl));
        setNewImageFiles([]);
        setNewPrimaryIdx(null);
        setUploadingImgs(false);
      }

      if (newDocumentFiles.length > 0) {
        if (!documentTypeId) {
          throw new Error("Please select a document type before uploading documents.");
        }

        setUploadingDocs(true);
        const uploadedDocuments = await uploadProfileDocuments({
          profileId,
          documentTypeId,
          documentFiles: newDocumentFiles,
          token,
        });
        documentUploadSummary = buildDocumentUploadSummary(uploadedDocuments);

        setNewDocumentFiles([]);
        setDocumentTypeId("");
        setUploadingDocs(false);
      }

      setOriginalForm(cloneFormSnapshot(form));
      resetDuplicateCheck();
      announceSectionSaved(sectionKey, documentUploadSummary);
    } catch (err) {
      console.error("Profile save failed:", err);
      setUploadingImgs(false);
      setUploadingDocs(false);
      setSaveError(
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        err?.message ||
        "Save failed. Please try again."
      );
    } finally {
      setSaving(false);
      setUploadingImgs(false);
      setUploadingDocs(false);
    }
  };

  const handleAddPayment = async () => {
    if (!canEditPayments) {
      setSaveError("Your role does not have permission to add payments.");
      return;
    }

    const amount = Number(paymentDraft.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setSaveError("Enter a valid payment amount before adding payment history.");
      return;
    }

    if (!String(form?.persona?.tier || "").trim()) {
      setSaveError("Save persona/package first, then add payments.");
      return;
    }

    setAddingPayment(true);
    setSaveError("");

    try {
      const response = await API.post(`/profiles/${profileId}/payments`, {
        amount,
        payment_status: paymentDraft.payment_status || "paid",
        payment_method: paymentDraft.payment_method || null,
        transaction_ref: paymentDraft.transaction_ref || null,
        paid_at: paymentDraft.paid_at || null,
      }, h);

      const nextPayment = response.data?.payment;
      if (nextPayment) {
        setPaymentHistory((prev) => [nextPayment, ...prev]);
      }
      setPaymentDraft({ ...emptyPayment });
      setShowAddPayment(false);
      announceSectionSaved("payments");
    } catch (err) {
      console.error("Add payment failed:", err);
      setSaveError(err?.response?.data?.detail || err?.response?.data?.error || "Failed to add payment.");
    } finally {
      setAddingPayment(false);
    }
  };

  if (loading) {
    return (
      <div style={S.center}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={S.spinner} />
        <p style={S.muted}>Loading profile...</p>
      </div>
    );
  }

  if (error || !form) {
    return (
      <div style={S.center}>
        <p style={S.errorText}>{error || "Profile not found."}</p>
            <button style={S.btnSecondary} onClick={onBack}>{"< Back"}</button>
      </div>
    );
  }

  const totalPhotos = existingFiles.length + newImageFiles.length;
  const canAddMore = totalPhotos < 10;
  const isPreparingSelectedFiles = preparingImageFiles || preparingDocumentFiles;
  const savingLabel = uploadingDocs
    ? "Uploading documents..."
    : uploadingImgs
      ? "Uploading photos..."
      : saving
        ? "Saving..."
        : "Save Changes";
  const sectionItems = [
    { id: "duplicate", label: "Duplicate Check", hint: "Safety review before save" },
    { id: "identity", label: "Basic Details", hint: "Name, birth and core identity" },
    { id: "personal", label: "Community", hint: "Marital, caste and sampraday" },
    { id: "contacts", label: "Contacts", hint: "Phones and email details" },
    { id: "addresses", label: "Addresses", hint: "Current, native, family, office" },
    { id: "education", label: "Education & Career", hint: "Degrees and occupations" },
    { id: "family", label: "Family", hint: "Direct family details" },
    { id: "relatives", label: "Relatives", hint: "Paternal and maternal relatives" },
    { id: "preferences", label: "Preferences", hint: "Partner preference setup" },
    { id: "persona", label: "Persona", hint: "Package and validity details" },
    { id: "payments", label: "Payments", hint: "Add payment entries and review history" },
    { id: "tags", label: "Tags", hint: "Tag and badge assignment" },
    { id: "photos", label: "Photos", hint: "Primary photo and gallery" },
    { id: "documents", label: "Documents", hint: "Verification and uploads" },
  ];
  const activeSectionMeta = sectionItems.find((section) => section.id === activeSection) || sectionItems[0];

  return (
    <div style={S.page}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        .img-thumb:hover .img-actions { opacity: 1 !important; }
        @media (max-width: 900px) {
          .edit-hero { flex-direction: column !important; align-items: flex-start !important; }
          .edit-shell { grid-template-columns: 1fr !important; }
          .edit-grid-2, .edit-grid-3 { grid-template-columns: 1fr !important; }
          .edit-duplicate-row { flex-direction: column !important; align-items: flex-start !important; }
        }
      `}</style>

      <div style={S.topBar}>
      <button style={S.backLink} onClick={onBack}>{"< Back to Profile"}</button>
      </div>

      <div style={S.wrap}>
        <div style={S.hero} className="edit-hero">
          <div>
            <div style={S.eyebrow}>Profile #{form.profile_no || profileId}</div>
            <h1 style={S.pageTitle}>Edit Profile</h1>
            <p style={S.pageSub}>
              Update profile identity, education, contacts, relatives, preferences,
              tags and media using the Jain Kalyanam v2 schema.
            </p>
          </div>

          <div style={S.phoneticCard}>
            <span>Phonetic Key</span>
            <b>{phoneticKey || "-"}</b>
          </div>
        </div>

        <div style={S.editorShell} className="edit-shell">
          <aside style={S.sidebar}>
            <div style={S.sidebarCard}>
              <p style={S.sidebarLabel}>Sections</p>
              <div style={S.sidebarMenu}>
                {sectionItems.map((section) => (
                  <button
                    key={section.id}
                    type="button"
                    style={{
                      ...S.sidebarItem,
                      ...(activeSection === section.id ? S.sidebarItemActive : {}),
                    }}
                    onClick={() => setActiveSection(section.id)}
                  >
                    <span
                      style={{
                        ...S.sidebarItemTitle,
                        ...(activeSection === section.id ? S.sidebarItemTitleActive : {}),
                      }}
                    >
                      {section.label}
                    </span>
                    <span
                      style={{
                        ...S.sidebarItemHint,
                        ...(activeSection === section.id ? S.sidebarItemHintActive : {}),
                      }}
                    >
                      {section.hint}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <div style={S.editorMain}>
            <div style={S.sectionHeaderCard}>
              <div>
                <p style={S.sidebarLabel}>Editing Now</p>
                <h2 style={S.activeSectionTitle}>{activeSectionMeta.label}</h2>
                <p style={S.activeSectionHint}>{activeSectionMeta.hint}</p>
              </div>
              <button style={S.btnSecondary} onClick={onBack} disabled={saving}>Back</button>
            </div>

            {policyRestrictions.length > 0 && (
              <div style={S.infoBanner}>
                <b>Access notes:</b> {policyRestrictions.join(" ")}
              </div>
            )}

            {activeSection === "duplicate" && (
        <Section
          title="Duplicate Safety Check"
          actionLabel="Update This Section"
          onAction={() => handleSave("identity")}
          actionDisabled={saving || !canEditBasicDetails}
          actionBusyLabel={savingLabel}
          notice={!canRunDuplicateCheck ? "Duplicate review is read-only for your role." : canEditProfile && !canEditBasicDetails ? "Identity edits are restricted for your role, so duplicate review here is read-only." : ""}
        >
          {!canEditProfile && (
            <div style={S.warnBox}>
              <b>Read-only access.</b>
              <span>Your role can view this profile but cannot save edits.</span>
            </div>
          )}
          <div style={S.duplicateRow} className="edit-duplicate-row">
            <div>
              <b style={S.duplicateTitle}>
                {identityChanged ? "Identity fields changed" : "No identity changes detected"}
              </b>
              <p style={S.duplicateSub}>
                Duplicate check uses first, middle, last name, gender, and date of birth.
                Birth time is optional and only improves matching when provided.
              </p>
            </div>
          </div>

          <div style={S.duplicateActionBar}>
            <button
              style={S.checkBtn}
              onClick={handleDuplicateCheck}
              disabled={duplicateChecking || !canRunDuplicateCheck}
            >
              {duplicateChecking ? "Checking..." : "Run Duplicate Check"}
            </button>
          </div>

          {duplicateChecked && duplicateMatches.length === 0 && (
            <div style={S.safeBox}>OK No duplicate candidate found.</div>
          )}

          {duplicateMatches.length > 0 && (
            <div style={S.dupResults}>
              <div style={S.warnBox}>
                <b>Possible duplicate profiles found.</b>
                <span>Review matches before saving this edit.</span>
              </div>

              {duplicateMatches.map((match) => (
                <div key={match.candidate.id} style={S.dupCard}>
                  <div>
                    <div style={S.dupName}>{match.candidate.full_name}</div>
                    <div style={S.dupMeta}>
                      Profile #{match.candidate.profile_no || "-"} - {match.candidate.gender || "-"} -{" "}
                      {match.candidate.birth_date || match.candidate.dob || "DOB -"} -{" "}
                      {match.candidate.city || match.candidate.location_display_cache || "City -"}
                    </div>
                    <div style={S.reasonList}>
                      {match.reasons.map((reason, index) => (
                        <span key={index} style={S.reasonChip}>{reason}</span>
                      ))}
                    </div>
                  </div>

                  <div style={S.dupCardActions}>
                    <button
                      type="button"
                      style={S.viewProfileBtn}
                      onClick={() => onViewProfile?.(match.candidate.id)}
                    >
                      View Profile
                    </button>
                    <div style={S.scoreBox}>
                      <span>{Math.round(match.score * 100)}%</span>
                      <small>match</small>
                    </div>
                  </div>
                </div>
              ))}

              <label style={S.overrideBox}>
                <input
                  type="checkbox"
                  checked={duplicateOverride}
                  onChange={(e) => setDuplicateOverride(e.target.checked)}
                />
                <span>I reviewed these matches and still want to save this profile.</span>
              </label>
            </div>
          )}
        </Section>
            )}

            {activeSection === "identity" && (
        <Section
          title="Identity & Basic Details"
          actionLabel="Update This Section"
          onAction={() => handleSave("identity")}
          actionDisabled={saving || !canEditBasicDetails}
          actionBusyLabel={savingLabel}
          notice={canEditProfile && !canEditBasicDetails ? "Basic detail updates are blocked by field-level access." : ""}
        >
          <div style={S.grid3} className="edit-grid-3">
            <Field label="First Name *">
              <input style={S.input} value={form.first_name} onChange={set("first_name")} />
            </Field>

            <Field label="Middle Name *">
              <input style={S.input} value={form.middle_name} onChange={set("middle_name")} />
            </Field>

            <Field label="Last Name *">
              <input style={S.input} value={form.last_name} onChange={set("last_name")} />
            </Field>
          </div>

          <div style={S.grid2} className="edit-grid-2">
            <Field label="Gender *">
              <select style={S.select} value={form.gender_id} onChange={set("gender_id")}>
                <option value="">Select gender</option>
                {visibleGenders.map((g) => (
                  <option key={g.id} value={g.id}>{g.name || g.display_label}</option>
                ))}
              </select>
            </Field>

            <Field label="Date of Birth *">
              <input style={S.input} type="date" value={form.birth_date || form.dob} onChange={set("birth_date")} />
            </Field>

            <Field label="Birth Time (Optional)">
              <TimeInput12Hour value={form.birth_time} onChange={set("birth_time")} inputStyle={S.input} />
            </Field>

            <Field label="Height">
              <HeightInputField
                valueCm={form.height_cm || form.height}
                onChangeCm={(value) => set("height_cm")(value)}
              />
            </Field>

            <Field label="Birth Place Location" span2>
              <LocationPicker
                lookups={lookups}
                values={{
                  country: form.birth_country,
                  state_id: form.birth_state_id,
                  district_id: form.birth_district_id,
                  tehsil_id: form.birth_tehsil_id,
                  city_id: form.birth_place_city_id || form.birth_place_id,
                }}
                onChange={{
                  country: set("birth_country"),
                  state_id: set("birth_state_id"),
                  district_id: set("birth_district_id"),
                  tehsil_id: set("birth_tehsil_id"),
                  city_id: set("birth_place_city_id"),
                }}
              />
            </Field>

            <Field label="Birth Coordinates" span2>
              <BirthLocationMapField
                address={form.birth_place}
                latitude={form.birth_latitude}
                longitude={form.birth_longitude}
                onAddressChange={set("birth_place")}
                onLatitudeChange={set("birth_latitude")}
                onLongitudeChange={set("birth_longitude")}
              />
            </Field>
          </div>

          <div style={{ marginTop: 14 }}>
            <Field label="About Me">
              <textarea
                style={{ ...S.input, ...S.textarea }}
                value={form.about_me}
                onChange={set("about_me")}
                placeholder="Write a short bio..."
              />
            </Field>
          </div>
        </Section>
            )}

            {activeSection === "personal" && (
        <Section
          title="Personal & Community Details"
          actionLabel="Update This Section"
          onAction={() => handleSave("personal")}
          actionDisabled={saving || !canEditCommunity}
          actionBusyLabel={savingLabel}
          notice={canEditProfile && !canEditCommunity ? "Personal and community fields are read-only for your role." : ""}
        >
          <div style={S.grid2} className="edit-grid-2">
            <Field label="Marital Status">
              <select style={S.select} value={form.marital_status_id} onChange={set("marital_status_id")}>
                <option value="">Select status</option>
                {lookups.maritalStatuses.map((ms) => (
                  <option key={ms.id} value={ms.id}>{formatDropdownLabel(ms.name || ms.display_label)}</option>
                ))}
              </select>
            </Field>

            {shouldShowChildrenSection(form.marital_status_id, lookups.maritalStatuses) && (
              <Field label="Has Children">
                <select style={S.select} value={form.has_children ? "yes" : "no"} onChange={(e) => set("has_children")(e.target.value === "yes")}>
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </Field>
            )}

            {shouldShowChildrenSection(form.marital_status_id, lookups.maritalStatuses) && (
              <Field label="Marital Field Details" span2>
                <textarea
                  style={{ ...S.input, minHeight: 92, resize: "vertical" }}
                  value={form.marital_field_details}
                  onChange={set("marital_field_details")}
                  placeholder="Mention children, previous marriage details, custody, or other relevant notes"
                />
              </Field>
            )}

            <Field label="Complexion">
              <select style={S.select} value={form.complexion_id} onChange={set("complexion_id")}>
                <option value="">Select complexion</option>
                {lookups.complexions.map((c) => (
                  <option key={c.id} value={c.id}>{c.name || c.display_label}</option>
                ))}
              </select>
            </Field>

            <Field label="Blood Group">
              <select style={S.select} value={form.blood_group_id} onChange={set("blood_group_id")}>
                <option value="">Select blood group</option>
                {lookups.bloodGroups.map((b) => (
                  <option key={b.id} value={b.id}>{b.name || b.display_label}</option>
                ))}
              </select>
            </Field>

            <Field label="Manglik">
              <select style={S.select} value={form.manglik} onChange={set("manglik")}>
                <option value="Do Not Know">Do Not Know</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="Anshik">Anshik</option>
              </select>
            </Field>

            <Field label="Sampraday">
              <SearchableSelect
                value={form.sampraday_id}
                options={lookups.sampradays.map((sampraday) => ({ id: sampraday.id, label: sampraday.name }))}
                onChange={set("sampraday_id")}
                placeholder="Select sampraday"
                fieldShellStyle={S.select}
                inputStyle={S.searchableSelectInput}
                chevronButtonStyle={S.searchableSelectChevronButton}
                menuStyle={S.searchableSelectMenu}
                optionStyle={S.searchableSelectOption}
                optionActiveStyle={S.searchableSelectOptionActive}
              />
            </Field>

            <Field label="Sampraday Details">
              <textarea style={{ ...S.input, minHeight: 92, resize: "vertical" }} value={form.sampraday_details} onChange={set("sampraday_details")} />
            </Field>

            <Field label="Caste">
              <SearchableSelect
                value={form.caste_id}
                options={lookups.castes.map((c) => ({ id: c.id, label: c.name }))}
                onChange={set("caste_id")}
                placeholder="Select caste"
                fieldShellStyle={S.select}
                inputStyle={S.searchableSelectInput}
                chevronButtonStyle={S.searchableSelectChevronButton}
                menuStyle={S.searchableSelectMenu}
                optionStyle={S.searchableSelectOption}
                optionActiveStyle={S.searchableSelectOptionActive}
              />
            </Field>

            <Field label="Referral Source">
              <SearchableSelect
                value={form.referral_source_id || ""}
                options={lookups.referralSources.map((source) => ({ id: source.id, label: source.name }))}
                onChange={set("referral_source_id")}
                placeholder="Select referral source"
                fieldShellStyle={S.select}
                inputStyle={S.searchableSelectInput}
                chevronButtonStyle={S.searchableSelectChevronButton}
                menuStyle={S.searchableSelectMenu}
                optionStyle={S.searchableSelectOption}
                optionActiveStyle={S.searchableSelectOptionActive}
              />
            </Field>

            <Field label="Referral Name">
              <input
                style={S.input}
                value={form.referral_name || ""}
                onChange={set("referral_name")}
                placeholder="Enter referral name"
              />
            </Field>

            <Field label="Sakha" span2>
              <SakhaAssignmentsField
                options={filteredSakhas.map((s) => ({ id: s.id, label: s.name }))}
                values={{
                  sakha_id: form.sakha_id,
                  dadisa_sakha_id: form.dadisa_sakha_id,
                  nanisa_sakha_id: form.nanisa_sakha_id,
                  mamasa_sakha_id: form.mamasa_sakha_id,
                }}
                onChange={(field, value) =>
                  setForm((prev) => ({
                    ...prev,
                    [field]: value,
                  }))
                }
                onCreateOption={form.caste_id ? createSakha : null}
                creatingOption={creatingSakha}
                disabled={!form.caste_id || !canEditCommunity}
              />
            </Field>

            <Field label="Disability">
              <select style={S.select} value={form.has_disability ? "yes" : "no"} onChange={(e) => set("has_disability")(e.target.value === "yes")}>
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </Field>

            {form.has_disability && (
              <Field label="Disability Description">
                <input style={S.input} value={form.disability_description} onChange={set("disability_description")} />
              </Field>
            )}
          </div>
        </Section>
            )}

            {activeSection === "contacts" && (
        <Section
          title="Contacts"
          actionLabel="Update This Section"
          onAction={() => handleSave("contacts")}
          actionDisabled={saving || !canEditContacts}
          actionBusyLabel={savingLabel}
          notice={canEditProfile && !canEditContacts ? "Contact updates are read-only for your role." : ""}
        >
          <SectionHead title="Contact Numbers & Email" onAdd={canEditContacts ? () => addListItem("contacts", { ...emptyContact }) : null} />
          {form.contacts.length === 0 && <p style={S.emptyHint}>No contacts added.</p>}

          {form.contacts.map((contact, index) => (
            <div key={index} style={S.subCard}>
              <RemoveBtn onClick={() => removeListItem("contacts", index)} />

              <div style={S.grid2} className="edit-grid-2">
                <Field label="Type">
                  <select style={S.select} value={contact.contact_type} onChange={setListItem("contacts", index, "contact_type")}>
                    <option value="mobile">Mobile</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="email">Email</option>
                  </select>
                </Field>

                <Field label={getContactValueFieldLabel(contact.contact_type)}>
                  {isIndianPhoneContactType(contact.contact_type) ? (
                    <IndianPhoneInput
                      value={contact.contact_value}
                      onChange={(nextValue) => setListItem("contacts", index, "contact_value")(nextValue)}
                      inputStyle={S.input}
                      selectStyle={S.select}
                    />
                  ) : (
                    <input style={S.input} value={contact.contact_value} onChange={setListItem("contacts", index, "contact_value")} />
                  )}
                </Field>

                <Field label="Contact Person">
                  <input style={S.input} value={getSelfContactName(form)} readOnly />
                </Field>

                <Field label="Relation">
                  <input style={S.input} value="self" readOnly />
                </Field>

                <Field label="Primary">
                  <select style={S.select} value={contact.is_primary ? "yes" : "no"} onChange={(e) => setListItem("contacts", index, "is_primary")(e.target.value === "yes")}>
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </Field>

                <Field label="Verified">
                  <select style={S.select} value={contact.is_verified ? "yes" : "no"} onChange={(e) => setListItem("contacts", index, "is_verified")(e.target.value === "yes")}>
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </Field>
              </div>
            </div>
          ))}
        </Section>
            )}

            {activeSection === "addresses" && (
        <Section title="Addresses" actionLabel="Update This Section" onAction={() => handleSave("addresses")} actionDisabled={saving || !canEditAddresses} actionBusyLabel={savingLabel} notice={canEditProfile && !canEditAddresses ? "Address updates are read-only for your role." : ""}>
          <SectionHead title="Structured Addresses" onAdd={canEditAddresses ? () => addListItem("addresses", { ...emptyAddress }) : null} />
          {form.addresses.length === 0 && <p style={S.emptyHint}>No addresses added.</p>}

          {form.addresses.map((address, index) => (
            <div key={index} style={S.subCard}>
              {form.addresses.length > 1 && <RemoveBtn onClick={() => removeListItem("addresses", index)} />}

              <div style={S.grid2} className="edit-grid-2">
                <Field label="Address Type">
                  <select style={S.select} value={address.address_type} onChange={setAddressField(index, "address_type")}>
                    <option value="current">Current</option>
                    <option value="native">Native</option>
                    <option value="family">Family</option>
                    <option value="office">Office</option>
                  </select>
                </Field>

                <Field label="Address Line 1">
                  <input style={S.input} value={address.address_line1} onChange={setAddressField(index, "address_line1")} />
                </Field>

                <Field label="Address Line 2">
                  <input style={S.input} value={address.address_line2} onChange={setAddressField(index, "address_line2")} />
                </Field>

                <AddressLocationFields
                  lookups={lookups}
                  values={address}
                  onChange={{
                    country_id: setAddressField(index, "country_id"),
                    state_id: setAddressField(index, "state_id"),
                    district_id: setAddressField(index, "district_id"),
                    tehsil_id: setAddressField(index, "tehsil_id"),
                    visa_status_id: setAddressField(index, "visa_status_id"),
                    visa_name_id: setAddressField(index, "visa_name_id"),
                  }}
                />

                <Field label="Pincode">
                  <input style={S.input} value={address.pincode} onChange={setAddressField(index, "pincode")} />
                </Field>
              </div>
            </div>
          ))}
        </Section>
            )}

            {activeSection === "education" && (
        <Section title="Education & Career" actionLabel="Update This Section" onAction={() => handleSave("education")} actionDisabled={saving || !canEditEducationCareer} actionBusyLabel={savingLabel} notice={canEditProfile && !canEditEducationCareer ? "Education and career updates are read-only for your role." : ""}>
          <SectionHead title="Education" onAdd={canEditEducationCareer ? () => addListItem("educations", { ...emptyEducation, sort_order: form.educations.length + 1 }) : null} />
          {form.educations.map((edu, index) => (
            <div key={index} style={S.subCard}>
              {form.educations.length > 1 && <RemoveBtn onClick={() => removeListItem("educations", index)} />}

              <div style={S.grid2} className="edit-grid-2">
                <Field label="Education Category">
                  <select style={S.select} value={edu.education_category_id} onChange={setListItem("educations", index, "education_category_id")}>
                    <option value="">Select category</option>
                    {lookups.educationCategories.map((ec) => (
                      <option key={ec.id} value={ec.id}>{ec.name}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Degree Name">
                  <select
                    style={S.select}
                    value={edu.degree_name}
                    onChange={setListItem("educations", index, "degree_name")}
                    disabled={hasDegreeCategoryMapping(lookups.degreeNames) && !edu.education_category_id}
                  >
                    <option value="">
                      {hasDegreeCategoryMapping(lookups.degreeNames) && !edu.education_category_id
                        ? "Select education category first"
                        : "Select degree"}
                    </option>
                    {buildDegreeNameOptions(lookups.degreeNames, edu.degree_name, edu.education_category_id).map((degree) => (
                      <option key={degree.value} value={degree.value}>{degree.label}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Description">
                  <input style={S.input} value={edu.education_description} onChange={setListItem("educations", index, "education_description")} />
                </Field>

                <Field label="Institution">
                  <input style={S.input} value={edu.institution_name} onChange={setListItem("educations", index, "institution_name")} />
                </Field>

                <Field label="Education Location">
                  <LocationAutocompleteField
                    value={edu.education_city_id}
                    locations={lookups.locations}
                    inputStyle={S.select}
                    placeholder="Search education location"
                    getOptionLabel={getLocationCityName}
                    onChange={setListItem("educations", index, "education_city_id")}
                  />
                </Field>

                <Field label="Pursuing">
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 8, minHeight: 46, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={(edu.education_status || "completed") === "pursuing"}
                      onChange={(e) => setListItem("educations", index, "education_status")(e.target.checked ? "pursuing" : "completed")}
                    />
                    <span>Pursuing this degree</span>
                  </label>
                </Field>

                <Field label="Passing / Expected Year">
                  <input style={S.input} type="number" value={edu.passing_year} onChange={setListItem("educations", index, "passing_year")} />
                </Field>

                <Field label="Highest Degree">
                  <select
                    style={S.select}
                    value={edu.is_highest_degree ? "yes" : "no"}
                    onChange={(e) => {
                      const isHighest = e.target.value === "yes";
                      setForm((prev) => ({
                        ...prev,
                        educations: prev.educations.map((x, i) => ({
                          ...x,
                          is_highest_degree: i === index ? isHighest : isHighest ? false : x.is_highest_degree,
                        })),
                      }));
                    }}
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </Field>
              </div>
            </div>
          ))}

          <div style={{ height: 20 }} />

          <SectionHead title="Occupations" onAdd={canEditEducationCareer ? () => addListItem("occupations", { ...emptyOccupation, sort_order: form.occupations.length + 1 }) : null} />
          {form.occupations.map((occ, index) => (
            <div key={index} style={S.subCard}>
              {form.occupations.length > 1 && <RemoveBtn onClick={() => removeListItem("occupations", index)} />}
              <div style={{ color: "#5f6776", fontSize: 12, marginBottom: 12 }}>
                {index === 0 ? "Primary occupation" : `Additional occupation ${index + 1}`}
              </div>

              <div style={S.grid2} className="edit-grid-2">
                <Field label="Occupation Type">
                  <select style={S.select} value={occ.occupation_type_id || ""} onChange={setOccupationField(index, "occupation_type_id")}>
                    <option value="">Select occupation type</option>
                    {lookups.occupationTypes.map((occupationType) => (
                      <option key={occupationType.id} value={occupationType.id}>{occupationType.name || occupationType.display_label}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Company">
                  <input style={S.input} value={occ.company_name} onChange={setListItem("occupations", index, "company_name")} />
                </Field>

                <Field label="Occupation Details">
                  <input style={S.input} value={occ.occupation_details} onChange={setListItem("occupations", index, "occupation_details")} />
                </Field>

                <Field label="Work Profile">
                  <input style={S.input} value={occ.work_profile} onChange={setListItem("occupations", index, "work_profile")} />
                </Field>

                {isServiceOccupationTypeLabel(
                  getLookupName(lookups.occupationTypes, occ.occupation_type_id) ||
                  occ.occupation_type
                ) ? (
                  <Field label="Annual Income">
                    <CurrencyAmountInput
                      amount={occ.annual_income_amount || ""}
                      currencyCode={occ.annual_income_currency_code || "INR"}
                      onAmountChange={setOccupationField(index, "annual_income_amount")}
                      onCurrencyChange={setOccupationField(index, "annual_income_currency_code")}
                    />
                  </Field>
                ) : (
                  <Field label="Annual Turnover">
                    <CurrencyAmountInput
                      amount={occ.annual_turnover_amount || ""}
                      currencyCode={occ.annual_turnover_currency_code || "INR"}
                      onAmountChange={setOccupationField(index, "annual_turnover_amount")}
                      onCurrencyChange={setOccupationField(index, "annual_turnover_currency_code")}
                    />
                  </Field>
                )}
              </div>
            </div>
          ))}
        </Section>
            )}

            {activeSection === "family" && (
        <Section
          title="Family"
          actionLabel="Update This Section"
          onAction={() => handleSave("family")}
          actionDisabled={saving || !canEditFamilyDetails}
          actionBusyLabel={savingLabel}
          notice={canEditProfile && !canEditFamilyDetails ? "Family updates are blocked by field-level access." : ""}
        >
          <SectionHead onAdd={canEditFamilyDetails ? addFamilyMember : null} />
          {familyEntries.length === 0 && <p style={S.emptyHint}>No family members added.</p>}

          {familyEntries.length > 0 && (
            <div style={S.familyCardGrid}>
              {familyEntries.map(({ relative, index }) => (
                <FamilyMemberCard
                  key={index}
                  relative={relative}
                  lookups={lookups}
                  onEdit={canEditFamilyDetails ? () => openFamilyEditor(index) : null}
                  onDelete={canEditFamilyDetails ? () => removeFamilyMember(index) : null}
                />
              ))}
            </div>
          )}

          {familyEditor && familyEntries
            .filter(({ index }) => index === familyEditor.index)
            .map(({ relative, index }) => (
            <FamilyEditorOverlay
              key={index}
              title={familyEditor.isNew ? "Add Family Member" : "Edit Family Member"}
              subtitle="Update the details, contacts, and education in one focused view."
              onClose={closeFamilyEditor}
              onDone={finalizeFamilyEditor}
            >
            <div style={S.familyEditorForm}>

              <div style={S.grid2} className="edit-grid-2">
                <Field label="Relation Type">
                  <select style={S.select} value={relative.relation_type_id || ""} onChange={setListItem("relatives", index, "relation_type_id")}>
                    <option value="">Select relation type</option>
                    {familyRelationOptions.map((relationType) => (
                      <option key={relationType.id} value={relationType.id}>{formatDropdownLabel(relationType.name || relationType.display_label)}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Gender">
                  <select style={S.select} value={relative.gender} onChange={setListItem("relatives", index, "gender")}>
                    <option value="">Select</option>
                    {visibleGenders.map((g) => (
                      <option key={g.id} value={g.name || g.display_label}>{g.name || g.display_label}</option>
                    ))}
                  </select>
                </Field>

                <Field label="First Name">
                  <input style={S.input} value={relative.first_name} onChange={setListItem("relatives", index, "first_name")} />
                </Field>

                <Field label="Middle Name">
                  <input style={S.input} value={relative.middle_name} onChange={setListItem("relatives", index, "middle_name")} />
                </Field>

                <Field label="Last Name">
                  <input style={S.input} value={relative.last_name} onChange={setListItem("relatives", index, "last_name")} />
                </Field>

                {shouldShowRelativeMaritalStatus(relative, lookups.relationTypes) && (
                  <Field label="Marital Status">
                    <select style={S.select} value={relative.marital_status || ""} onChange={setListItem("relatives", index, "marital_status")}>
                      <option value="">Select marital status</option>
                      <option value="unmarried">Unmarried</option>
                      <option value="married">Married</option>
                    </select>
                  </Field>
                )}

                {shouldShowRelativeMaritalStatus(relative, lookups.relationTypes) && isRelativeMarriedStatus(relative.marital_status) && (
                  <Field label={getRelativePartnerNameLabel(relative, lookups.relationTypes)}>
                    <input style={S.input} value={relative.partner_name || ""} onChange={setListItem("relatives", index, "partner_name")} />
                  </Field>
                )}

                {shouldShowRelativeMaritalStatus(relative, lookups.relationTypes) && isRelativeUnmarriedStatus(relative.marital_status) && (
                  <Field label="Future Prospect">
                    <select style={S.select} value={relative.is_future_prospect ? "yes" : "no"} onChange={(e) => setListItem("relatives", index, "is_future_prospect")(e.target.value === "yes")}>
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </Field>
                )}

                {shouldShowRelativeMaritalStatus(relative, lookups.relationTypes) && isRelativeUnmarriedStatus(relative.marital_status) && relative.is_future_prospect && (
                  <Field label="Eligible Year">
                    <input
                      style={S.input}
                      type="number"
                      value={relative.future_prospect_eligible_year || ""}
                      onChange={setListItem("relatives", index, "future_prospect_eligible_year")}
                      placeholder="e.g. 2028"
                      min={1900}
                      max={9999}
                    />
                  </Field>
                )}


                <Field label="Is Deceased?">
                  <select style={S.select} value={relative.is_deceased ? "yes" : "no"} onChange={(e) => setListItem("relatives", index, "is_deceased")(e.target.value === "yes")}>
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </Field>


                <Field label="Relative Occupations" span2>
                  <RelativeOccupationsEditor
                    relative={relative}
                    lookups={lookups}
                    onAdd={() => addRelativeOccupation(index)}
                    onRemove={(occupationIndex) => removeRelativeOccupation(index, occupationIndex)}
                    onChange={(occupationIndex, field, value) => setRelativeOccupation(index, occupationIndex, field)(value)}
                  />
                </Field>

                <Field label="Family Contacts" span2>
                  <div style={S.relativeEduBox}>
                    <SectionHead title="Contact Numbers & Email" onAdd={() => addRelativeContact(index)} />
                    {safeArray(relative.contacts).length === 0 && <p style={S.emptyHint}>No contacts added.</p>}
                    {safeArray(relative.contacts).map((contact, contactIndex) => (
                      <div key={contactIndex} style={S.miniCard}>
                        <RemoveBtn onClick={() => removeRelativeContact(index, contactIndex)} />
                        <div style={S.grid2} className="edit-grid-2">
                          <Field label="Type">
                            <select style={S.select} value={contact.contact_type || "mobile"} onChange={setRelativeContact(index, contactIndex, "contact_type")}>
                              <option value="mobile">Mobile</option>
                              <option value="whatsapp">WhatsApp</option>
                              <option value="parent_mobile">Parent Mobile</option>
                              <option value="email">Email</option>
                              <option value="other">Other</option>
                            </select>
                          </Field>
                          <Field label={getContactValueFieldLabel(contact.contact_type)}>
                            {isIndianPhoneContactType(contact.contact_type) ? (
                              <IndianPhoneInput
                                value={contact.contact_value || ""}
                                onChange={(nextValue) => setRelativeContact(index, contactIndex, "contact_value")(nextValue)}
                                inputStyle={S.input}
                                selectStyle={S.select}
                              />
                            ) : (
                              <input style={S.input} value={contact.contact_value || ""} onChange={setRelativeContact(index, contactIndex, "contact_value")} />
                            )}
                          </Field>
                          <Field label="Contact Person">
                            <input style={S.input} value={getRelativeDisplayName(relative)} readOnly />
                          </Field>
                          <Field label="Relation">
                            <input style={S.input} value={getRelativeRelationLabel(relative, lookups.relationTypes)} readOnly />
                          </Field>
                        </div>
                      </div>
                    ))}
                  </div>
                </Field>
              </div>

              <div style={S.relativeEduBox}>
                <SectionHead title="Family Education" onAdd={() => addRelativeEducation(index)} />

                {(relative.education || []).map((edu, eduIndex) => (
                  <div key={eduIndex} style={S.miniCard}>
                    {(relative.education || []).length > 1 && (
                      <RemoveBtn onClick={() => removeRelativeEducation(index, eduIndex)} />
                    )}

                    <div style={S.grid2} className="edit-grid-2">
                      <Field label="Category">
                        <select style={S.select} value={edu.education_category_id} onChange={setRelativeEducation(index, eduIndex, "education_category_id")}>
                          <option value="">Select</option>
                          {lookups.educationCategories.map((ec) => (
                            <option key={ec.id} value={ec.id}>{ec.name}</option>
                          ))}
                        </select>
                      </Field>

                      <Field label="Degree">
                        <select
                          style={S.select}
                          value={edu.degree_name}
                          onChange={setRelativeEducation(index, eduIndex, "degree_name")}
                          disabled={hasDegreeCategoryMapping(lookups.degreeNames) && !edu.education_category_id}
                        >
                          <option value="">
                            {hasDegreeCategoryMapping(lookups.degreeNames) && !edu.education_category_id
                              ? "Select education category first"
                              : "Select degree"}
                          </option>
                          {buildDegreeNameOptions(lookups.degreeNames, edu.degree_name, edu.education_category_id).map((degree) => (
                            <option key={degree.value} value={degree.value}>{degree.label}</option>
                          ))}
                        </select>
                      </Field>

                      <Field label="Education Details" span2>
                        <textarea
                          style={{ ...S.input, ...S.textarea }}
                          value={edu.education_description}
                          onChange={setRelativeEducation(index, eduIndex, "education_description")}
                          placeholder="Add details about the relative's education"
                        />
                      </Field>

                      <Field label="Highest Degree">
                        <select style={S.select} value={edu.is_highest_degree ? "yes" : "no"} onChange={(e) => setRelativeEducation(index, eduIndex, "is_highest_degree")(e.target.value === "yes")}>
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                      </Field>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            </FamilyEditorOverlay>
          ))}
        </Section>
            )}

            {activeSection === "relatives" && (
        <Section
          title="Relatives"
          actionLabel="Update This Section"
          onAction={() => handleSave("relatives")}
          actionDisabled={saving || !canEditRelatives}
          actionBusyLabel={savingLabel}
          notice={canEditProfile && !canEditRelatives ? "Relative updates are blocked by field-level access." : ""}
        >
          <SectionHead title="Relatives" onAdd={canEditRelatives ? addSideRelative : null} />
          {sideRelativeEntries.length === 0 && <p style={S.emptyHint}>No relatives added.</p>}

          {sideRelativeEntries.length > 0 && (
            <div style={S.familyCardGrid}>
              {sideRelativeEntries.map(({ relative, index }) => (
                <FamilyMemberCard
                  key={index}
                  relative={relative}
                  lookups={lookups}
                  onEdit={canEditRelatives ? () => openRelativeEditor(index) : null}
                  onDelete={canEditRelatives ? () => removeSideRelative(index) : null}
                />
              ))}
            </div>
          )}

          {relativeEditor && sideRelativeEntries
            .filter(({ index }) => index === relativeEditor.index)
            .map(({ relative, index }) => (
            <FamilyEditorOverlay
              key={index}
              kicker="Relative"
              title={relativeEditor.isNew ? "Add Relative" : "Edit Relative"}
              subtitle="Update the details, contacts, and education in one focused view."
              onClose={closeRelativeEditor}
              onDone={finalizeRelativeEditor}
            >
            <div style={S.familyEditorForm}>

              <div style={S.grid2} className="edit-grid-2">
                <Field label="Relation Type">
                  <select style={S.select} value={relative.relation_type_id || ""} onChange={setListItem("relatives", index, "relation_type_id")}>
                    <option value="">Select relation type</option>
                    {sideRelativeRelationOptions.map((relationType) => (
                      <option key={relationType.id} value={relationType.id}>{formatDropdownLabel(relationType.name || relationType.display_label)}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Family Side">
                  <select style={S.select} value={normalizeRelativeFamilySide(relative.family_side)} onChange={setListItem("relatives", index, "family_side")}>
                    <option value="paternal">Paternal</option>
                    <option value="maternal">Maternal</option>
                  </select>
                </Field>

                <Field label="Gender">
                  <select style={S.select} value={relative.gender} onChange={setListItem("relatives", index, "gender")}>
                    <option value="">Select</option>
                    {visibleGenders.map((g) => (
                      <option key={g.id} value={g.name || g.display_label}>{g.name || g.display_label}</option>
                    ))}
                  </select>
                </Field>

                <Field label="First Name">
                  <input style={S.input} value={relative.first_name} onChange={setListItem("relatives", index, "first_name")} />
                </Field>

                {shouldShowRelativeMaritalStatus(relative, lookups.relationTypes) && (
                  <Field label="Marital Status">
                    <select style={S.select} value={relative.marital_status || ""} onChange={setListItem("relatives", index, "marital_status")}>
                      <option value="">Select marital status</option>
                      <option value="unmarried">Unmarried</option>
                      <option value="married">Married</option>
                    </select>
                  </Field>
                )}

                {shouldShowRelativeMaritalStatus(relative, lookups.relationTypes) && isRelativeMarriedStatus(relative.marital_status) && (
                  <Field label={getRelativePartnerNameLabel(relative, lookups.relationTypes)}>
                    <input style={S.input} value={relative.partner_name || ""} onChange={setListItem("relatives", index, "partner_name")} />
                  </Field>
                )}

                {shouldShowRelativeMaritalStatus(relative, lookups.relationTypes) && isRelativeUnmarriedStatus(relative.marital_status) && (
                  <Field label="Future Prospect">
                    <select style={S.select} value={relative.is_future_prospect ? "yes" : "no"} onChange={(e) => setListItem("relatives", index, "is_future_prospect")(e.target.value === "yes")}>
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </Field>
                )}

                {shouldShowRelativeMaritalStatus(relative, lookups.relationTypes) && isRelativeUnmarriedStatus(relative.marital_status) && relative.is_future_prospect && (
                  <Field label="Eligible Year">
                    <input
                      style={S.input}
                      type="number"
                      value={relative.future_prospect_eligible_year || ""}
                      onChange={setListItem("relatives", index, "future_prospect_eligible_year")}
                      placeholder="e.g. 2028"
                      min={1900}
                      max={9999}
                    />
                  </Field>
                )}

                <Field label="Is Deceased?">
                  <select style={S.select} value={relative.is_deceased ? "yes" : "no"} onChange={(e) => setListItem("relatives", index, "is_deceased")(e.target.value === "yes")}>
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </Field>

                <Field label="Family Occupations" span2>
                  <RelativeOccupationsEditor
                    relative={relative}
                    lookups={lookups}
                    onAdd={() => addRelativeOccupation(index)}
                    onRemove={(occupationIndex) => removeRelativeOccupation(index, occupationIndex)}
                    onChange={(occupationIndex, field, value) => setRelativeOccupation(index, occupationIndex, field)(value)}
                  />
                </Field>

                <Field label="Relative Contacts" span2>
                  <div style={S.relativeEduBox}>
                    <SectionHead title="Contact Numbers & Email" onAdd={() => addRelativeContact(index)} />
                    {safeArray(relative.contacts).length === 0 && <p style={S.emptyHint}>No contacts added.</p>}
                    {safeArray(relative.contacts).map((contact, contactIndex) => (
                      <div key={contactIndex} style={S.miniCard}>
                        <RemoveBtn onClick={() => removeRelativeContact(index, contactIndex)} />
                        <div style={S.grid2} className="edit-grid-2">
                          <Field label="Type">
                            <select style={S.select} value={contact.contact_type || "mobile"} onChange={setRelativeContact(index, contactIndex, "contact_type")}>
                              <option value="mobile">Mobile</option>
                              <option value="whatsapp">WhatsApp</option>
                              <option value="parent_mobile">Parent Mobile</option>
                              <option value="email">Email</option>
                              <option value="other">Other</option>
                            </select>
                          </Field>
                          <Field label={getContactValueFieldLabel(contact.contact_type)}>
                            {isIndianPhoneContactType(contact.contact_type) ? (
                              <IndianPhoneInput
                                value={contact.contact_value || ""}
                                onChange={(nextValue) => setRelativeContact(index, contactIndex, "contact_value")(nextValue)}
                                inputStyle={S.input}
                                selectStyle={S.select}
                              />
                            ) : (
                              <input style={S.input} value={contact.contact_value || ""} onChange={setRelativeContact(index, contactIndex, "contact_value")} />
                            )}
                          </Field>
                          <Field label="Contact Person">
                            <input style={S.input} value={getRelativeDisplayName(relative)} readOnly />
                          </Field>
                          <Field label="Relation">
                            <input style={S.input} value={getRelativeRelationLabel(relative, lookups.relationTypes)} readOnly />
                          </Field>
                        </div>
                      </div>
                    ))}
                  </div>
                </Field>
              </div>

              <div style={S.relativeEduBox}>
                <SectionHead title="Relative Education" onAdd={() => addRelativeEducation(index)} />

                {(relative.education || []).map((edu, eduIndex) => (
                  <div key={eduIndex} style={S.miniCard}>
                    {(relative.education || []).length > 1 && (
                      <RemoveBtn onClick={() => removeRelativeEducation(index, eduIndex)} />
                    )}

                    <div style={S.grid2} className="edit-grid-2">
                      <Field label="Category">
                        <select style={S.select} value={edu.education_category_id} onChange={setRelativeEducation(index, eduIndex, "education_category_id")}>
                          <option value="">Select</option>
                          {lookups.educationCategories.map((ec) => (
                            <option key={ec.id} value={ec.id}>{ec.name}</option>
                          ))}
                        </select>
                      </Field>

                      <Field label="Degree">
                        <select
                          style={S.select}
                          value={edu.degree_name}
                          onChange={setRelativeEducation(index, eduIndex, "degree_name")}
                          disabled={hasDegreeCategoryMapping(lookups.degreeNames) && !edu.education_category_id}
                        >
                          <option value="">
                            {hasDegreeCategoryMapping(lookups.degreeNames) && !edu.education_category_id
                              ? "Select education category first"
                              : "Select degree"}
                          </option>
                          {buildDegreeNameOptions(lookups.degreeNames, edu.degree_name, edu.education_category_id).map((degree) => (
                            <option key={degree.value} value={degree.value}>{degree.label}</option>
                          ))}
                        </select>
                      </Field>

                      <Field label="Education Details" span2>
                        <textarea
                          style={{ ...S.input, ...S.textarea }}
                          value={edu.education_description}
                          onChange={setRelativeEducation(index, eduIndex, "education_description")}
                          placeholder="Add details about the relative's education"
                        />
                      </Field>

                      <Field label="Highest Degree">
                        <select style={S.select} value={edu.is_highest_degree ? "yes" : "no"} onChange={(e) => setRelativeEducation(index, eduIndex, "is_highest_degree")(e.target.value === "yes")}>
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                      </Field>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            </FamilyEditorOverlay>
          ))}
        </Section>
            )}

            {activeSection === "preferences" && (
        <Section
          title="Partner Preferences"
          actionLabel="Update This Section"
          onAction={() => handleSave("preferences")}
          actionDisabled={saving || !canEditPreferences}
          actionBusyLabel={savingLabel}
          notice={canEditProfile && !canEditPreferences ? "Preference updates are restricted for your role." : ""}
        >
          <div style={S.grid2} className="edit-grid-2">
            <Field label="Preferred Age Difference" span2>
              <div style={S.prefRangeGrid}>
                <div style={S.signedInputWrap}>
                  <span style={S.signedInputPrefix}>-</span>
                  <div style={S.prefRangeInputRow}>
                    <input
                      style={{ ...S.input, ...S.signedInput }}
                      type="number"
                      min={0}
                      step="any"
                      value={form.preferences.preferred_age_min}
                      onChange={setNested("preferences", "preferred_age_min")}
                      placeholder="Value"
                    />
                    <select
                      style={S.select}
                      value={form.preferences.preferred_age_min_unit || "months"}
                      onChange={setNested("preferences", "preferred_age_min_unit")}
                    >
                      <option value="months">Months</option>
                      <option value="years">Years</option>
                    </select>
                  </div>
                </div>
                <div style={S.signedInputWrap}>
                  <span style={S.signedInputPrefix}>+</span>
                  <div style={S.prefRangeInputRow}>
                    <input
                      style={{ ...S.input, ...S.signedInput }}
                      type="number"
                      min={0}
                      step="any"
                      value={form.preferences.preferred_age_max}
                      onChange={setNested("preferences", "preferred_age_max")}
                      placeholder="Value"
                    />
                    <select
                      style={S.select}
                      value={form.preferences.preferred_age_max_unit || "months"}
                      onChange={setNested("preferences", "preferred_age_max_unit")}
                    >
                      <option value="months">Months</option>
                      <option value="years">Years</option>
                    </select>
                  </div>
                </div>
              </div>
            </Field>

            <Field label="Preferred Cities" span2>
              <CityPreferenceDropdown
                placeholder="Select preferred cities"
                options={preferredCityOptions}
                tierOptions={lookups.cityTiers}
                selected={form.preferences.preferred_city_ids || []}
                onToggle={(value) => togglePrefValue("preferred_city_ids", value)}
              />
            </Field>

            <Field label="Non Preferred Cities" span2>
              <CityPreferenceDropdown
                placeholder="Select non preferred cities"
                options={preferredCityOptions}
                tierOptions={lookups.cityTiers}
                selected={form.preferences.non_preferred_city_ids || []}
                onToggle={(value) => togglePrefValue("non_preferred_city_ids", value)}
              />
            </Field>

            <Field label="Non Preferred Sakhas" span2>
              <MultiSelectDropdown
                placeholder="Select non preferred sakhas"
                options={preferredSakhaOptions}
                selected={form.preferences.non_preferred_sakha_ids || []}
                onToggle={(value) => togglePrefValue("non_preferred_sakha_ids", value)}
              />
            </Field>

            <Field label="Preferred Education Categories" span2>
              <MultiSelectDropdown
                placeholder="Select preferred education categories"
                options={preferredEducationOptions}
                selected={form.preferences.preferred_education_category_ids || []}
                onToggle={(value) => togglePrefValue("preferred_education_category_ids", value)}
              />
            </Field>

            <Field label="Non Preferred Education Categories" span2>
              <MultiSelectDropdown
                placeholder="Select non preferred education categories"
                options={preferredEducationOptions}
                selected={form.preferences.non_preferred_education_category_ids || []}
                onToggle={(value) => togglePrefValue("non_preferred_education_category_ids", value)}
              />
            </Field>

            <Field label="Preferred Degrees" span2>
              <MultiSelectDropdown
                placeholder="Select preferred degrees"
                options={preferredDegreeOptions}
                selected={form.preferences.preferred_degrees || []}
                onToggle={(value) => togglePrefValue("preferred_degrees", value)}
              />
            </Field>

            <Field label="Non Preferred Degrees" span2>
              <MultiSelectDropdown
                placeholder="Select non preferred degrees"
                options={preferredDegreeOptions}
                selected={form.preferences.non_preferred_degrees || []}
                onToggle={(value) => togglePrefValue("non_preferred_degrees", value)}
              />
            </Field>

            <Field label="Preferred Occupation Types" span2>
              <MultiSelectDropdown
                placeholder="Select preferred occupations"
                options={preferredOccupationOptions}
                selected={form.preferences.preferred_occupation_types || []}
                onToggle={(value) => togglePrefValue("preferred_occupation_types", value)}
              />
            </Field>

            <Field label="Non Preferred Occupation Types" span2>
              <MultiSelectDropdown
                placeholder="Select non preferred occupations"
                options={preferredOccupationOptions}
                selected={form.preferences.non_preferred_occupation_types || []}
                onToggle={(value) => togglePrefValue("non_preferred_occupation_types", value)}
              />
            </Field>

            <Field label="Preference Notes" span2>
              <textarea style={{ ...S.input, ...S.textarea }} value={form.preferences.notes} onChange={setNested("preferences", "notes")} />
            </Field>
          </div>
        </Section>
            )}

            {activeSection === "persona" && (
        <Section title="Persona / Package" actionLabel="Update This Section" onAction={() => handleSave("persona")} actionDisabled={saving || !canEditPersona} actionBusyLabel={savingLabel} notice={canEditProfile && !canEditPersona ? "Persona updates are read-only for your role." : ""}>
          <div style={S.grid2} className="edit-grid-2">
            <Field label="Tier">
              <select style={S.select} value={form.persona?.tier || ""} onChange={setNested("persona", "tier")}>
                {personaTierOptions.map((option) => (
                  <option key={option.value || "none"} value={option.value}>{option.label}</option>
                ))}
              </select>
            </Field>

            <Field label="Payment Amount">
              <input style={S.input} type="number" value={form.persona?.payment_amount || ""} onChange={setNested("persona", "payment_amount")} />
            </Field>

            <Field label="Valid From">
              <input style={S.input} type="date" value={form.persona?.valid_from || ""} onChange={setNested("persona", "valid_from")} />
            </Field>

            <Field label="Valid Until">
              <input style={S.input} type="date" value={form.persona?.valid_until || ""} onChange={setNested("persona", "valid_until")} />
            </Field>
          </div>
        </Section>
            )}

            {activeSection === "payments" && (
        <Section
          title="Payments"
          actionLabel={showAddPayment ? (addingPayment ? "Adding Payment..." : "Add Payment") : ""}
          onAction={showAddPayment ? handleAddPayment : null}
          actionDisabled={addingPayment || !canEditPayments || !showAddPayment}
          actionBusyLabel="Adding Payment..."
          actionBusy={addingPayment}
          notice={!canEditPayments ? "Payment updates are read-only for your role." : !String(form.persona?.tier || "").trim() ? "Save a persona/package first. Payments are linked to the latest persona row." : ""}
        >
          <div style={S.paymentActionRow}>
            <button
              type="button"
              style={S.addBtn}
              onClick={() => {
                setShowAddPayment((prev) => !prev);
                setPaymentDraft({ ...emptyPayment });
              }}
              disabled={!canEditPayments || !String(form.persona?.tier || "").trim()}
            >
              {showAddPayment ? "Cancel Payment" : "+ Add Payment"}
            </button>
          </div>

          {showAddPayment && (
            <div style={S.grid2} className="edit-grid-2">
              <Field label="Amount">
                <input
                  style={S.input}
                  type="number"
                  value={paymentDraft.amount || ""}
                  onChange={(event) => setPaymentDraft((prev) => ({ ...prev, amount: event.target.value }))}
                />
              </Field>

              <Field label="Payment Status">
                <select
                  style={S.select}
                  value={paymentDraft.payment_status || "paid"}
                  onChange={(event) => setPaymentDraft((prev) => ({ ...prev, payment_status: event.target.value }))}
                >
                  <option value="paid">Paid</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                  <option value="refunded">Refunded</option>
                </select>
              </Field>

              <Field label="Payment Method">
                <input
                  style={S.input}
                  value={paymentDraft.payment_method || ""}
                  onChange={(event) => setPaymentDraft((prev) => ({ ...prev, payment_method: event.target.value }))}
                  placeholder="UPI / Cash / Bank Transfer"
                />
              </Field>

              <Field label="Transaction Reference">
                <input
                  style={S.input}
                  value={paymentDraft.transaction_ref || ""}
                  onChange={(event) => setPaymentDraft((prev) => ({ ...prev, transaction_ref: event.target.value }))}
                />
              </Field>

              <Field label="Paid At">
                <input
                  style={S.input}
                  type="date"
                  value={paymentDraft.paid_at || ""}
                  onChange={(event) => setPaymentDraft((prev) => ({ ...prev, paid_at: event.target.value }))}
                />
              </Field>
            </div>
          )}

          <div style={{ height: 18 }} />

          <SectionHead title="Payment History" />
          {paymentHistory.length > 0 ? (
            <div style={S.cardGrid} className="profile-card-grid">
              {paymentHistory.map((payment, index) => (
                <div key={payment.id || index} style={S.subCard}>
                  <div style={S.paymentHistoryTitle}>
                    Rs {payment.amount} - {formatTierLabel(payment.payment_status)}
                  </div>
                  <div style={S.paymentHistoryMeta}>
                    {[payment.payment_method, payment.transaction_ref, formatDisplayDate(payment.paid_at)].filter(Boolean).join(" - ")}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={S.emptyHint}>No payments added yet.</p>
          )}
        </Section>
            )}

            {activeSection === "tags" && (
        <Section title="Tags" actionLabel="Update This Section" onAction={() => handleSave("tags")} actionDisabled={saving || !canEditTags} actionBusyLabel={savingLabel} notice={canEditProfile && !canEditTags ? "Tag updates are read-only for your role." : ""}>
          <Field label="Tags">
            <MultiSelectDropdown
              placeholder="Search or create tags"
              options={tagOptions}
              selected={form.tags || []}
              onToggle={(value) => toggleTag(value)}
              onCreate={createTag}
              createPrefix="Create tag"
            />
          </Field>
        </Section>
            )}

            {activeSection === "photos" && (
        <Section title={`Photos (${totalPhotos}/10)`} actionLabel="Update This Section" onAction={() => handleSave("photos")} actionDisabled={saving || isPreparingSelectedFiles || !canManagePhotos} actionBusyLabel={savingLabel} notice={canEditProfile && !canManagePhotos ? "Photo uploads and deletes are read-only for your role." : ""}>
          {existingFiles.length > 0 && (
            <>
              <p style={S.photoHint}>
                {canSetPrimaryPhoto ? "Click a photo to set as primary. Hover to delete." : "Photo upload and delete are available, but primary-photo changes are restricted."}
              </p>
              <div style={S.photoGrid}>
                {existingFiles.map((file) => {
                  const isPrimary = file.id === primaryFileId;
                  const isDeleting = deletingId === file.id;
                  const isSetting = settingPrimaryId === file.id;
                  return (
                    <div
                      key={file.id}
                      className="img-thumb"
                      style={{
                        ...S.photoThumb,
                        ...(isPrimary ? S.photoThumbPrimary : {}),
                        ...(isDeleting || isSetting ? { opacity: 0.5 } : {}),
                      }}
                      onClick={() => canSetPrimaryPhoto && handleSetExistingPrimary(file.id)}
                    >
                      <img src={file.file_url} alt="" style={S.photoImg} />
                      {isPrimary && <span style={S.photoBadgePrimary}>Primary</span>}
                      {isSetting && <span style={S.photoBadgeUpdating}>Updating...</span>}
                      <div className="img-actions" style={S.photoActions} onClick={(e) => e.stopPropagation()}>
                        <button
                          style={S.photoDeleteBtn}
                          onClick={() => handleDeleteExisting(file.id)}
                          disabled={isDeleting || !canManagePhotos}
                        >
                          {isDeleting ? "..." : "x"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {canManagePhotos && canAddMore && (
            <>
              <div
                style={{
                  ...S.dropZone,
                  marginTop: existingFiles.length ? 20 : 0,
                  ...(preparingImageFiles ? S.processingDropZone : {}),
                }}
                onClick={() => {
                  if (!preparingImageFiles) fileInputRef.current?.click();
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  style={{ display: "none" }}
                  onChange={handleFileSelect}
                  disabled={preparingImageFiles}
                />
                <span style={S.dropIcon}>+</span>
                <span style={S.dropText}>{preparingImageFiles ? "Preparing photos..." : "Add Photos"}</span>
                <span style={S.dropSub}>
                  {newImageFiles.length > 0
                    ? `${newImageFiles.length} new photo${newImageFiles.length > 1 ? "s" : ""} selected`
                    : `JPEG, PNG, WEBP, max ${MAX_UPLOAD_FILE_SIZE_LABEL} after compression, ${totalPhotos}/10`}
                </span>
                {preparingImageFiles && (
                  <span style={S.processingInlineNote}>
                    <span style={S.processingSpinner} />
                    {imagePreparationStatus || "Compressing selected photos..."}
                  </span>
                )}
              </div>

              {newImageFiles.length > 0 && (
                <>
                  <p style={{ ...S.photoHint, marginTop: 14 }}>Click a new photo to mark it as primary.</p>
                  <div style={S.photoGrid}>
                    {newImageFiles.map((img, index) => (
                      <div
                        key={index}
                        style={{ ...S.photoThumb, ...(newPrimaryIdx === index ? S.photoThumbPrimary : {}) }}
                        onClick={() => {
                          setNewPrimaryIdx(index);
                          setPrimaryFileId(null);
                        }}
                      >
                        <img src={img.previewUrl} alt={img.name} style={S.photoImg} />
                        {newPrimaryIdx === index && <span style={S.photoBadgePrimary}>Primary</span>}
                        <span style={S.photoBadgeNew}>New</span>
                        <button
                          type="button"
                          style={S.photoRemoveNew}
                          onClick={(event) => {
                            event.stopPropagation();
                            removeNewImage(index);
                          }}
                        >
                          x
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {totalPhotos === 0 && <p style={S.emptyHint}>No photos yet.</p>}
        </Section>
            )}

            {activeSection === "documents" && (
        <Section title="Documents" actionLabel="Update This Section" onAction={() => handleSave("documents")} actionDisabled={saving || isPreparingSelectedFiles || !canEditDocuments} actionBusyLabel={savingLabel} notice={canEditProfile && !canEditDocuments ? "Document uploads are read-only for your role." : ""}>
          {existingDocuments.length > 0 ? (
            <div style={S.documentGrid}>
              {existingDocuments.map((doc) => {
                const isDeleting = deletingDocumentId === doc.id;
                return (
                <div
                  key={doc.id}
                  style={{
                    ...S.documentCard,
                    ...(isDeleting ? S.documentCardDeleting : {}),
                  }}
                >
                <a
                  href={doc.file_url}
                  target="_blank"
                  rel="noreferrer"
                  style={S.documentCardLink}
                >
                  <div style={S.documentCardType}>{doc.document_type_name || doc.template_key || "Document"}</div>
                  <div style={S.documentCardName}>{doc.original_file_name || doc.file_url?.split("/").pop() || "Open document"}</div>
                  <div style={S.documentCardMeta}>
                    {[doc.mime_type, formatFileSize(doc.file_size_bytes)].filter(Boolean).join(" - ") || "Stored in AWS S3"}
                  </div>
                </a>
                <div style={S.documentCardActions}>
                  <a href={doc.file_url} target="_blank" rel="noreferrer" style={S.documentActionLink}>
                    Open
                  </a>
                  {canEditDocuments && (
                    <button
                      type="button"
                      style={{
                        ...S.documentDeleteBtn,
                        ...(isDeleting ? S.documentDeleteBtnDisabled : {}),
                      }}
                      onClick={() => handleDeleteExistingDocument(doc.id)}
                      disabled={isDeleting}
                    >
                      {isDeleting ? "Deleting..." : "Delete"}
                    </button>
                  )}
                </div>
                </div>
              );
              })}
            </div>
          ) : (
            <p style={S.emptyHint}>No documents uploaded yet.</p>
          )}

          <div style={{ height: 16 }} />

          <div style={S.grid2} className="edit-grid-2">
            <Field label="Document Type">
              <select style={S.select} value={documentTypeId} onChange={(e) => setDocumentTypeId(e.target.value)}>
                <option value="">Select document type</option>
                {lookups.documentTypes.map((docType) => (
                  <option key={docType.id} value={docType.id}>{docType.name}</option>
                ))}
              </select>
              <div style={S.documentUploadHint}>
                Multiple Biodata PDFs are allowed. Each uploaded file is stored as a separate document entry.
              </div>
            </Field>
          </div>

          <div
            style={{
              ...S.dropZone,
              marginTop: 16,
              ...(preparingDocumentFiles ? S.processingDropZone : {}),
            }}
            onClick={() => {
              if (!preparingDocumentFiles) documentInputRef.current?.click();
            }}
          >
            <input
              ref={documentInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,image/jpeg,image/png"
              multiple
              style={{ display: "none" }}
              onChange={handleDocumentSelect}
              disabled={preparingDocumentFiles}
            />
            <span style={S.dropIcon}>+</span>
            <span style={S.dropText}>{preparingDocumentFiles ? "Preparing documents..." : "Add Documents"}</span>
            <span style={S.dropSub}>
              {newDocumentFiles.length > 0
                ? `${newDocumentFiles.length} new document${newDocumentFiles.length > 1 ? "s" : ""} selected`
                : "PDF, DOC, DOCX, XLS, XLSX, TXT, JPEG, PNG, large files are optimized during upload"}
            </span>
            {preparingDocumentFiles && (
              <span style={S.processingInlineNote}>
                <span style={S.processingSpinner} />
                {documentPreparationStatus || "Compressing selected documents..."}
              </span>
            )}
          </div>

          {newDocumentFiles.length > 0 && (
            <>
            <div style={S.docList}>
              {newDocumentFiles.map((doc, index) => (
                <div key={`${doc.name}-${index}`} style={S.docItem}>
                  <div>
                    <div style={S.docName}>{doc.name}</div>
                    <div style={S.docMeta}>{formatFileSize(doc.size)} - {doc.type || "document"}</div>
                  </div>
                  <button type="button" style={S.docRemove} onClick={() => removeNewDocument(index)}>Remove</button>
                </div>
              ))}
            </div>
            <div style={S.documentUploadActions}>
              <button
                type="button"
                style={{
                  ...S.btnPrimary,
                  ...S.documentSaveBtn,
                  ...((saving || uploadingDocs || preparingDocumentFiles || !documentTypeId || !canEditDocuments) ? S.btnDisabled : {}),
                }}
                onClick={() => handleSave("documents")}
                disabled={saving || uploadingDocs || preparingDocumentFiles || !documentTypeId || !canEditDocuments}
              >
                {uploadingDocs ? "Uploading documents..." : preparingDocumentFiles ? "Preparing files..." : "Save Documents"}
              </button>
            </div>
            </>
          )}
        </Section>
            )}

            {saveError && <p style={S.saveError}>{saveError}</p>}
          </div>
        </div>
      </div>
      {saveNotice && <div style={S.snackbar}>{saveNotice}</div>}
    </div>
  );
}

// Small components
function Section({ title, children, actionLabel, onAction, actionDisabled, actionBusyLabel, actionBusy = false, notice = "" }) {
  return (
    <section style={S.section}>
      <p style={S.sectionTitle}>{title}</p>
      {notice ? <div style={S.infoBanner}>{notice}</div> : null}
      {children}
      {actionLabel && onAction && (
        <div style={S.sectionFooter}>
          <button
            type="button"
            style={{ ...S.btnPrimary, ...(actionDisabled ? S.btnDisabled : {}) }}
            onClick={onAction}
            disabled={actionDisabled}
          >
            {actionBusy && actionBusyLabel ? actionBusyLabel : actionLabel}
          </button>
        </div>
      )}
    </section>
  );
}

function Field({ label, children, span2 = false }) {
  return (
    <div style={{ ...S.field, ...(span2 ? S.fieldSpan2 : {}) }}>
      <label style={S.label}>{label}</label>
      {children}
    </div>
  );
}

function FamilyMemberCard({ relative, lookups, onEdit, onDelete }) {
  const relation = getRelativeRelationLabel(relative, lookups.relationTypes) || "Family member";
  const name = getRelativeDisplayName(relative) || "Name not added";
  const location = getRelativeLocationSummary(relative, lookups);
  const contacts = getRelativeContactSummaries(relative);
  const educationItems = getRelativeEducationSummaries(relative, lookups);
  const occupationItems = getRelativeOccupationSummaries(relative, lookups);
  const detailRows = [
    ["Gender", relative.gender],
    String(relative?.family_side || "").trim().toLowerCase() !== "none" && relative.family_side
      ? ["Family Side", labelize(relative.family_side)]
      : null,
    ["Location", location],
    relative.is_deceased ? ["Status", "Deceased"] : null,
    shouldShowRelativeMaritalStatus(relative, lookups.relationTypes) && relative.marital_status
      ? ["Marital Status", labelize(relative.marital_status)]
      : null,
    shouldShowRelativeMaritalStatus(relative, lookups.relationTypes) && isRelativeMarriedStatus(relative.marital_status) && relative.partner_name
      ? [getRelativePartnerNameLabel(relative, lookups.relationTypes), relative.partner_name]
      : null,
    shouldShowRelativeMaritalStatus(relative, lookups.relationTypes) && isRelativeUnmarriedStatus(relative.marital_status) && relative.is_future_prospect
      ? ["Future Prospect", "Yes"]
      : null,
    shouldShowRelativeMaritalStatus(relative, lookups.relationTypes) && isRelativeUnmarriedStatus(relative.marital_status) && relative.is_future_prospect && relative.future_prospect_eligible_year
      ? ["Eligible Year", relative.future_prospect_eligible_year]
      : null,
  ].filter(Boolean);

  return (
    <article style={S.familyMemberCard}>
      <div style={S.familyCardHeader}>
        <div style={S.familyAvatar}>{relation.slice(0, 1).toUpperCase()}</div>
        <div style={S.familyIdentity}>
          <div style={S.familyCardKicker}>{relation}</div>
          <h3 style={S.familyCardTitle}>{name}</h3>
        </div>
      </div>

      <div style={S.familyDetailGrid}>
        {detailRows.map(([label, value]) => (
          <div key={label} style={S.familyDetailItem}>
            <span style={S.familyDetailLabel}>{label}</span>
            <span style={S.familyDetailValue}>{value || "Not added"}</span>
          </div>
        ))}
      </div>

      <FamilyCardList title="Contacts" emptyText="No contact details added" items={contacts} />
      <FamilyCardList title="Occupations" emptyText="No occupation details added" items={occupationItems} />
      <FamilyCardList title="Education" emptyText="No education details added" items={educationItems} />

      {(onEdit || onDelete) && (
        <div style={S.familyCardActions}>
          {onEdit && <button type="button" style={S.familyEditBtn} onClick={onEdit}>Edit</button>}
          {onDelete && <button type="button" style={S.familyDeleteBtn} onClick={onDelete}>Delete</button>}
        </div>
      )}
    </article>
  );
}

function FamilyCardList({ title, emptyText, items }) {
  return (
    <div style={S.familyListBlock}>
      <div style={S.familyDetailLabel}>{title}</div>
      {items.length ? (
        <div style={S.familyList}>
          {items.map((item, index) => {
            if (typeof item === "string") {
              return <div key={`${item}-${index}`} style={S.familyListItem}>{item}</div>;
            }

            const lines = safeArray(item?.lines).filter(Boolean);
            return (
              <div key={`${item?.title || title}-${index}`} style={S.familyListItem}>
                {(item?.title || item?.badge) && (
                  <div style={S.familyListItemHeader}>
                    {item?.title ? <div style={S.familyListItemTitle}>{item.title}</div> : <span />}
                    {item?.badge ? <span style={S.familyListItemBadge}>{item.badge}</span> : null}
                  </div>
                )}
                {lines.map((line, lineIndex) => (
                  <div key={`${line}-${lineIndex}`} style={S.familyListItemLine}>{line}</div>
                ))}
              </div>
            );
          })}
        </div>
      ) : (
        <div style={S.familyCardEmpty}>{emptyText}</div>
      )}
    </div>
  );
}

function RelativeOccupationsEditor({ relative, lookups, onAdd, onRemove, onChange }) {
  const occupations = getEditableRelativeOccupations(relative);

  return (
    <div style={S.relativeEduBox}>
      <SectionHead title="Occupation Details" onAdd={onAdd} />
      {occupations.map((occupation, occupationIndex) => {
        const isServiceOccupation = isServiceOccupationTypeLabel(
          getLookupName(lookups.occupationTypes, occupation.occupation_type_id) ||
          occupation.occupation_type
        );

        return (
          <div key={occupationIndex} style={S.miniCard}>
            {occupations.length > 1 && <RemoveBtn onClick={() => onRemove(occupationIndex)} />}
            <div style={{ color: "#5f6776", fontSize: 12, marginBottom: 12 }}>
              {occupationIndex === 0 ? "Primary occupation" : `Additional occupation ${occupationIndex + 1}`}
            </div>
            <div style={S.grid2} className="edit-grid-2">
              <Field label="Occupation Type">
                <select style={S.select} value={occupation.occupation_type_id || ""} onChange={(event) => onChange(occupationIndex, "occupation_type_id", event.target.value)}>
                  <option value="">Select occupation type</option>
                  {lookups.occupationTypes.map((occupationType) => (
                    <option key={occupationType.id} value={occupationType.id}>{occupationType.name || occupationType.display_label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Company">
                <input style={S.input} value={occupation.company_name || ""} onChange={(event) => onChange(occupationIndex, "company_name", event.target.value)} />
              </Field>
              <Field label="Occupation Details">
                <input style={S.input} value={occupation.occupation_details || ""} onChange={(event) => onChange(occupationIndex, "occupation_details", event.target.value)} />
              </Field>
              <Field label="Work Profile">
                <input style={S.input} value={occupation.work_profile || ""} onChange={(event) => onChange(occupationIndex, "work_profile", event.target.value)} />
              </Field>
              {isServiceOccupation ? (
                <Field label="Annual Income">
                  <CurrencyAmountInput
                    amount={occupation.annual_income_amount || ""}
                    currencyCode={occupation.annual_income_currency_code || "INR"}
                    onAmountChange={(value) => onChange(occupationIndex, "annual_income_amount", value)}
                    onCurrencyChange={(value) => onChange(occupationIndex, "annual_income_currency_code", value)}
                  />
                </Field>
              ) : (
                <Field label="Annual Turnover">
                  <CurrencyAmountInput
                    amount={occupation.annual_turnover_amount || ""}
                    currencyCode={occupation.annual_turnover_currency_code || "INR"}
                    onAmountChange={(value) => onChange(occupationIndex, "annual_turnover_amount", value)}
                    onCurrencyChange={(value) => onChange(occupationIndex, "annual_turnover_currency_code", value)}
                  />
                </Field>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FamilyEditorOverlay({ title, subtitle, kicker = "Family", onClose, onDone, children }) {
  if (typeof document === "undefined") return null;

  return createPortal(
    <div style={S.modalBackdrop}>
      <div style={S.familyModal}>
        <div style={S.familyModalHeader}>
          <div>
            <p style={S.familyModalKicker}>{kicker}</p>
            <h2 style={S.familyModalTitle}>{title}</h2>
            <p style={S.familyModalSubtitle}>{subtitle}</p>
          </div>
          <button type="button" style={S.familyModalClose} onClick={onClose} title="Close">x</button>
        </div>
        <div style={S.familyModalBody}>{children}</div>
        <div style={S.familyModalFooter}>
          <button type="button" style={S.familyCancelBtn} onClick={onClose}>Cancel</button>
          <button type="button" style={S.familyDoneBtn} onClick={onDone}>Done</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function SectionHead({ title, onAdd }) {
  return (
    <div style={S.sectionHead}>
      {title && <p style={S.subSectionLabel}>{title}</p>}
      {onAdd && <button type="button" style={S.addBtn} onClick={onAdd}>+ Add</button>}
    </div>
  );
}

function RemoveBtn({ onClick }) {
  return <button type="button" onClick={onClick} style={S.removeBtn} title="Remove">x</button>;
}

function AddressLocationFields({ lookups, values, onChange }) {
  const locations = safeArray(lookups.locations);
  const showVisaFields = shouldShowAddressVisaFields(values, locations);

  const countries = useMemo(() => {
    return uniqueBy(
      locations
        .map((location) => ({
          id: location.country_id || location.countryId || location.countryID,
          name: location.country || location.country_name || location.countryName || "India",
        }))
        .filter((option) => option.id && option.name),
      "id"
    ).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [locations]);

  const states = useMemo(() => {
    return uniqueBy(
      locations
        .filter((location) => !values.country_id || Number(location.country_id || location.countryId || location.countryID) === Number(values.country_id))
        .map((location) => ({
          id: location.state_id || location.stateId || location.stateID,
          name: location.state || location.state_name || location.stateName,
        }))
        .filter((option) => option.id && option.name),
      "id"
    ).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [locations, values.country_id]);

  const districts = useMemo(() => {
    return uniqueBy(
      locations
        .filter((location) => !values.country_id || Number(location.country_id || location.countryId || location.countryID) === Number(values.country_id))
        .filter((location) => !values.state_id || Number(location.state_id || location.stateId || location.stateID) === Number(values.state_id))
        .map((location) => ({
          id: location.district_id || location.districtId || location.districtID,
          name: location.district || location.district_name || location.districtName,
        }))
        .filter((option) => option.id && option.name),
      "id"
    ).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [locations, values.country_id, values.state_id]);

  const tehsils = useMemo(() => {
    return uniqueBy(
      locations
        .filter((location) => !values.country_id || Number(location.country_id || location.countryId || location.countryID) === Number(values.country_id))
        .filter((location) => !values.state_id || Number(location.state_id || location.stateId || location.stateID) === Number(values.state_id))
        .filter((location) => !values.district_id || Number(location.district_id || location.districtId || location.districtID) === Number(values.district_id))
        .map((location) => ({
          id: location.tehsil_id || location.tehsilId || location.tehsilID,
          name: location.tehsil || location.tehsil_name || location.tehsilName,
        }))
        .filter((option) => option.id && option.name),
      "id"
    ).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [locations, values.country_id, values.state_id, values.district_id]);

  return (
    <>
      <Field label="Country">
        <SearchableSelect
          value={values.country_id || ""}
          options={countries.map((country) => ({ id: country.id, label: country.name }))}
          onChange={onChange.country_id}
          placeholder="Select country"
          fieldShellStyle={S.select}
          inputStyle={S.searchableSelectInput}
          chevronButtonStyle={S.searchableSelectChevronButton}
          menuStyle={S.searchableSelectMenu}
          optionStyle={S.searchableSelectOption}
          optionActiveStyle={S.searchableSelectOptionActive}
        />
      </Field>

      <Field label="State">
        <SearchableSelect
          value={values.state_id || ""}
          options={states.map((state) => ({ id: state.id, label: state.name }))}
          onChange={onChange.state_id}
          placeholder="Select state"
          disabled={!values.country_id}
          fieldShellStyle={S.select}
          fieldShellDisabledStyle={{ background: C.soft, color: C.muted }}
          inputStyle={S.searchableSelectInput}
          chevronButtonStyle={S.searchableSelectChevronButton}
          menuStyle={S.searchableSelectMenu}
          optionStyle={S.searchableSelectOption}
          optionActiveStyle={S.searchableSelectOptionActive}
        />
      </Field>

      <Field label="District">
        <SearchableSelect
          value={values.district_id || ""}
          options={districts.map((district) => ({ id: district.id, label: district.name }))}
          onChange={onChange.district_id}
          placeholder="Select district"
          disabled={!values.state_id}
          fieldShellStyle={S.select}
          fieldShellDisabledStyle={{ background: C.soft, color: C.muted }}
          inputStyle={S.searchableSelectInput}
          chevronButtonStyle={S.searchableSelectChevronButton}
          menuStyle={S.searchableSelectMenu}
          optionStyle={S.searchableSelectOption}
          optionActiveStyle={S.searchableSelectOptionActive}
        />
      </Field>

      <Field label="Tehsil">
        <SearchableSelect
          value={values.tehsil_id || ""}
          options={tehsils.map((tehsil) => ({ id: tehsil.id, label: tehsil.name }))}
          onChange={onChange.tehsil_id}
          placeholder="Select tehsil"
          disabled={!values.district_id}
          fieldShellStyle={S.select}
          fieldShellDisabledStyle={{ background: C.soft, color: C.muted }}
          inputStyle={S.searchableSelectInput}
          chevronButtonStyle={S.searchableSelectChevronButton}
          menuStyle={S.searchableSelectMenu}
          optionStyle={S.searchableSelectOption}
          optionActiveStyle={S.searchableSelectOptionActive}
        />
      </Field>

      {showVisaFields && (
        <>
          <Field label="Visa Status">
            <select style={S.select} value={values.visa_status_id || ""} onChange={onChange.visa_status_id}>
              <option value="">Select visa status</option>
              {safeArray(lookups.visaStatuses).map((visaStatus) => (
                <option key={visaStatus.id} value={visaStatus.id}>{visaStatus.name || visaStatus.display_label}</option>
              ))}
            </select>
          </Field>

          <Field label="Visa Name">
            <select style={S.select} value={values.visa_name_id || ""} onChange={onChange.visa_name_id}>
              <option value="">Select visa name</option>
              {safeArray(lookups.visaNames).map((visaName) => (
                <option key={visaName.id} value={visaName.id}>{visaName.name || visaName.display_label}</option>
              ))}
            </select>
          </Field>
        </>
      )}
    </>
  );
}

// eslint-disable-next-line no-unused-vars
function ChipPicker({ title, items, selected, getLabel, onToggle }) {
  return (
    <div style={S.chipBlock}>
      <p style={S.subSectionLabel}>{title}</p>
      <div style={S.tagGrid}>
        {items.map((item) => (
          <button
            type="button"
            key={item.id}
            style={{ ...S.tagChip, ...(selected.includes(Number(item.id)) ? S.tagActive : {}) }}
            onClick={() => onToggle(item.id)}
          >
            {selected.includes(Number(item.id)) ? "Selected " : ""}
            {getLabel(item)}
          </button>
        ))}
      </div>
    </div>
  );
}

// Mapping / payload helpers
function MultiSelectDropdown({ placeholder, options, selected, onToggle, disabled = false, onCreate = null, createPrefix = "Create" }) {
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const cleanQuery = query.trim();
  const normalizedQuery = cleanQuery.toLowerCase();
  const selectedSet = new Set(safeArray(selected).map((value) => String(value)));
  const selectedOptions = safeArray(options).filter((option) => selectedSet.has(String(option.id)));
  const summaryOptions = selectedOptions.slice(0, 2);
  const remainingSelectedCount = Math.max(0, selectedOptions.length - summaryOptions.length);
  const visibleOptions = safeArray(options).filter((option) =>
    String(option.label || "").toLowerCase().includes(normalizedQuery)
  );
  const hasExactMatch = safeArray(options).some((option) =>
    String(option.label || "").trim().toLowerCase() === normalizedQuery
  );
  const canCreate = Boolean(onCreate && cleanQuery && !hasExactMatch);

  return (
    <details style={{ ...S.multiDropdown, ...(disabled ? S.multiDropdownDisabled : {}) }}>
      <summary style={{ ...S.multiDropdownSummary, ...(disabled ? S.multiDropdownSummaryDisabled : {}) }}>
        <div style={S.multiDropdownSummaryText}>
          {selectedOptions.length === 0 ? (
            <span style={S.multiDropdownPlaceholder}>{placeholder}</span>
          ) : (
            <div style={S.multiDropdownSelectedWrap}>
              {summaryOptions.map((option) => (
                <button
                  key={String(option.id)}
                  type="button"
                  style={S.multiDropdownSelectedChip}
                  disabled={disabled}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (!disabled) onToggle(option.id);
                  }}
                >
                  <span style={S.multiDropdownSelectedChipLabel}>{option.label}</span>
                  {!disabled && <span style={S.multiDropdownSelectedChipRemove}>x</span>}
                </button>
              ))}
              {remainingSelectedCount > 0 && (
                <span style={{ ...S.multiDropdownSelectedChip, ...S.multiDropdownSelectedChipMuted }}>
                  +{remainingSelectedCount} more
                </span>
              )}
            </div>
          )}
        </div>
        <b style={S.multiDropdownCount}>{selectedOptions.length}</b>
      </summary>
      <div style={S.multiDropdownMenu}>
        {!disabled && (
          <input
            type="text"
            style={S.multiDropdownSearch}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={onCreate ? "Search or create..." : "Search options..."}
          />
        )}
        {!disabled && selectedOptions.length > 0 && (
          <div style={S.multiDropdownSelectedPanel}>
            {selectedOptions.map((option) => (
              <button
                key={String(option.id)}
                type="button"
                style={S.multiDropdownSelectedChip}
                onClick={() => onToggle(option.id)}
              >
                <span style={S.multiDropdownSelectedChipLabel}>{option.label}</span>
                <span style={S.multiDropdownSelectedChipRemove}>x</span>
              </button>
            ))}
          </div>
        )}
        {disabled ? (
          <div style={S.multiDropdownEmpty}>{placeholder}</div>
        ) : (
          <div style={S.multiDropdownOptions}>
            {visibleOptions.length === 0 && !canCreate ? (
              <div style={S.multiDropdownEmpty}>No options available.</div>
            ) : (
              <>
                {visibleOptions.map((option) => {
                  const active = selectedSet.has(String(option.id));
                  return (
                    <label
                      key={String(option.id)}
                      style={{ ...S.checkboxOption, ...(active ? S.checkboxOptionActive : {}) }}
                    >
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={() => onToggle(option.id)}
                        style={S.checkboxInput}
                      />
                      <span>{option.label}</span>
                    </label>
                  );
                })}

                {canCreate && (
                  <button
                    type="button"
                    style={{ ...S.checkboxOption, ...S.createOption }}
                    disabled={creating}
                    onClick={async () => {
                      setCreating(true);
                      try {
                        await onCreate(cleanQuery);
                        setQuery("");
                      } finally {
                        setCreating(false);
                      }
                    }}
                  >
                    <span style={S.createOptionIcon}>+</span>
                    <span>{creating ? "Creating..." : `${createPrefix} "${cleanQuery}"`}</span>
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </details>
  );
}

function CityPreferenceDropdown({ placeholder, options, tierOptions, selected, onToggle }) {
  const [query, setQuery] = useState("");
  const [activeTierId, setActiveTierId] = useState("all");
  const normalizedQuery = query.trim().toLowerCase();
  const selectedSet = new Set(safeArray(selected).map((value) => String(value)));
  const selectedOptions = safeArray(options).filter((option) => selectedSet.has(String(option.id)));
  const summaryOptions = selectedOptions.slice(0, 2);
  const remainingSelectedCount = Math.max(0, selectedOptions.length - summaryOptions.length);
  const visibleOptions = safeArray(options).filter((option) => {
    const matchesTier = activeTierId === "all" || safeNumberArray(option.tierIds).includes(Number(activeTierId));
    const matchesQuery = String(option.label || "").toLowerCase().includes(normalizedQuery);
    return matchesTier && matchesQuery;
  });

  return (
    <details style={S.multiDropdown}>
      <summary style={S.multiDropdownSummary}>
        <div style={S.multiDropdownSummaryText}>
          {selectedOptions.length === 0 ? (
            <span style={S.multiDropdownPlaceholder}>{placeholder}</span>
          ) : (
            <div style={S.multiDropdownSelectedWrap}>
              {summaryOptions.map((option) => (
                <button
                  key={String(option.id)}
                  type="button"
                  style={S.multiDropdownSelectedChip}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onToggle(option.id);
                  }}
                >
                  <span style={S.multiDropdownSelectedChipLabel}>{option.label}</span>
                  <span style={S.multiDropdownSelectedChipRemove}>x</span>
                </button>
              ))}
              {remainingSelectedCount > 0 && (
                <span style={{ ...S.multiDropdownSelectedChip, ...S.multiDropdownSelectedChipMuted }}>
                  +{remainingSelectedCount} more
                </span>
              )}
            </div>
          )}
        </div>
        <b style={S.multiDropdownCount}>{selectedOptions.length}</b>
      </summary>
      <div style={S.multiDropdownMenu}>
        <input
          type="text"
          style={S.multiDropdownSearch}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search city, state, country..."
        />
        {selectedOptions.length > 0 && (
          <div style={S.multiDropdownSelectedPanel}>
            {selectedOptions.map((option) => (
              <button
                key={String(option.id)}
                type="button"
                style={S.multiDropdownSelectedChip}
                onClick={() => onToggle(option.id)}
              >
                <span style={S.multiDropdownSelectedChipLabel}>{option.label}</span>
                <span style={S.multiDropdownSelectedChipRemove}>x</span>
              </button>
            ))}
          </div>
        )}
        <div style={S.cityTierFilterRow}>
          <button
            type="button"
            style={{ ...S.cityTierFilterChip, ...(activeTierId === "all" ? S.cityTierFilterChipActive : {}) }}
            onClick={() => setActiveTierId("all")}
          >
            All Tiers
          </button>
          {safeArray(tierOptions).map((tier) => (
            <button
              key={tier.id}
              type="button"
              style={{ ...S.cityTierFilterChip, ...(Number(activeTierId) === Number(tier.id) ? S.cityTierFilterChipActive : {}) }}
              onClick={() => setActiveTierId(String(tier.id))}
            >
              {tier.name || `Tier ${tier.id}`}
            </button>
          ))}
        </div>
        <div style={S.multiDropdownOptions}>
          {visibleOptions.length === 0 ? (
            <div style={S.multiDropdownEmpty}>No cities available.</div>
          ) : (
            visibleOptions.map((option) => {
              const active = selectedSet.has(String(option.id));
              return (
                <label
                  key={String(option.id)}
                  style={{ ...S.checkboxOption, ...(active ? S.checkboxOptionActive : {}) }}
                >
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={() => onToggle(option.id)}
                    style={S.checkboxInput}
                  />
                  <span>{option.label}</span>
                </label>
              );
            })
          )}
        </div>
      </div>
    </details>
  );
}

function LocationPicker({ lookups, values, onChange }) {
  const locations = safeArray(lookups.locations);

  const getCountry = (location) => String(location.country || location.country_name || "India").trim();
  const normalizedCountry = String(values.country || "").trim().toLowerCase();

  const countries = useMemo(() => {
    const set = new Set(locations.map((location) => getCountry(location)).filter(Boolean));
    if (!set.size) set.add("India");
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map((country) => ({ id: country, label: country }));
  }, [locations]);

  const states = useMemo(() => {
    return uniqueBy(
      locations
        .filter((location) => !normalizedCountry || getCountry(location).toLowerCase() === normalizedCountry)
        .map((location) => ({
          id: location.state_id || location.stateId || location.stateID,
          label: location.state || location.state_name || location.stateName,
        }))
        .filter((option) => option.id && option.label),
      "id"
    );
  }, [locations, normalizedCountry]);

  const districts = useMemo(() => {
    return uniqueBy(
      locations
        .filter((location) => !normalizedCountry || getCountry(location).toLowerCase() === normalizedCountry)
        .filter((location) => !values.state_id || Number(location.state_id || location.stateId || location.stateID) === Number(values.state_id))
        .map((location) => ({
          id: location.district_id || location.districtId || location.districtID,
          label: location.district || location.district_name || location.districtName,
        }))
        .filter((option) => option.id && option.label),
      "id"
    );
  }, [locations, normalizedCountry, values.state_id]);

  const tehsils = useMemo(() => {
    return uniqueBy(
      locations
        .filter((location) => !normalizedCountry || getCountry(location).toLowerCase() === normalizedCountry)
        .filter((location) => !values.state_id || Number(location.state_id || location.stateId || location.stateID) === Number(values.state_id))
        .filter((location) => !values.district_id || Number(location.district_id || location.districtId || location.districtID) === Number(values.district_id))
        .map((location) => ({
          id: location.tehsil_id || location.tehsilId || location.tehsilID,
          label: location.tehsil || location.tehsil_name || location.tehsilName,
        }))
        .filter((option) => option.id && option.label),
      "id"
    );
  }, [locations, normalizedCountry, values.state_id, values.district_id]);

  const cities = useMemo(() => {
    return locations
      .filter((location) => !normalizedCountry || getCountry(location).toLowerCase() === normalizedCountry)
      .filter((location) => !values.state_id || Number(location.state_id || location.stateId || location.stateID) === Number(values.state_id))
      .filter((location) => !values.district_id || Number(location.district_id || location.districtId || location.districtID) === Number(values.district_id))
      .filter((location) => !values.tehsil_id || Number(location.tehsil_id || location.tehsilId || location.tehsilID) === Number(values.tehsil_id))
      .map((location) => ({
        id: location.id,
        label: [location.city || location.name, location.tehsil || location.tehsil_name, location.district || location.district_name, location.state || location.state_name]
          .filter(Boolean)
          .join(", "),
      }))
      .slice()
      .sort((a, b) => String(a.label || "").localeCompare(String(b.label || "")));
  }, [locations, normalizedCountry, values.state_id, values.district_id, values.tehsil_id]);

  return (
    <div style={S.locationGrid}>
      <SearchableSelect
        value={values.country || ""}
        options={countries}
        onChange={onChange.country}
        placeholder="Country"
      />

      <SearchableSelect
        value={values.state_id || ""}
        options={states}
        onChange={onChange.state_id}
        placeholder="State"
      />

      <SearchableSelect
        value={values.district_id || ""}
        options={districts}
        onChange={onChange.district_id}
        placeholder="District"
        disabled={!values.state_id}
      />

      <SearchableSelect
        value={values.tehsil_id || ""}
        options={tehsils}
        onChange={onChange.tehsil_id}
        placeholder="Tehsil"
        disabled={!values.district_id}
      />

      <SearchableSelect
        value={values.city_id || ""}
        options={cities}
        onChange={onChange.city_id}
        placeholder="City"
        disabled={!values.tehsil_id && tehsils.length > 0}
        containerStyle={{ gridColumn: "1 / -1" }}
      />
    </div>
  );
}

function mapProfileToForm(p, lookups = {}) {
  const birthLocation = safeArray(lookups.locations).find(
    (location) => Number(location.id) === Number(p.birth_place_city_id || p.birth_place_id)
  );
  const personaRow = safeArray(p.persona)[0] || null;
  const contacts = safeArray(p.contacts).map((c) => ({
    contact_type: normalizeSelfContactType(c.contact_type),
    contact_value: isIndianPhoneContactType(normalizeSelfContactType(c.contact_type))
      ? getIndianPhoneInputValue(c.contact_value || c.normalized_value || "")
      : (c.contact_value || ""),
    normalized_value: c.normalized_value || "",
    contact_person_name: c.contact_person_name || "",
    contact_person_relation: c.contact_person_relation || "",
    is_primary: Boolean(c.is_primary),
    is_verified: Boolean(c.is_verified),
  }));

  const mobileContact = contacts.find((c) => ["mobile", "whatsapp"].includes(c.contact_type));
  const emailContact = contacts.find((c) => c.contact_type === "email");

  return {
    id: p.id,
    profile_no: p.profile_no || "",
    first_name: p.first_name || "",
    middle_name: p.middle_name || "",
    last_name: p.last_name || "",
    gender_id: findConfigIdByLabel(lookups.genders, p.gender),
    gender: p.gender || "",
    birth_date: dateOnly(p.birth_date || p.dob),
    dob: dateOnly(p.birth_date || p.dob),
    birth_time: toTimeInput(p.birth_time),
    birth_country: birthLocation?.country || birthLocation?.country_name || "India",
    birth_state_id: birthLocation?.state_id || "",
    birth_district_id: birthLocation?.district_id || "",
    birth_tehsil_id: birthLocation?.tehsil_id || "",
    birth_place: p.birth_place || "",
    birth_place_city_id: p.birth_place_city_id || p.birth_place_id || "",
    birth_place_id: p.birth_place_city_id || p.birth_place_id || "",
    birth_latitude: p.birth_latitude ?? "",
    birth_longitude: p.birth_longitude ?? "",
    city_id: p.city_id || p.current_location_id || "",
    current_location_id: p.city_id || p.current_location_id || "",
    mobile: mobileContact?.contact_value || "",
    email: emailContact?.contact_value || "",

    marital_status_id: findConfigIdByLabel(lookups.maritalStatuses, p.marital_status),
    marital_status: p.marital_status || "",
    marital_status_description: p.marital_status_description || "",
    has_children: Boolean(p.has_children),
    marital_field_details: p.marital_field_details || p.children_description || "",
    height_cm: p.height_cm || p.height || "",
    height: p.height_cm || p.height || "",
    complexion_id: findConfigIdByLabel(lookups.complexions, p.complexion),
    complexion: p.complexion || "",
    blood_group_id: findConfigIdByLabel(lookups.bloodGroups, p.blood_group),
    blood_group: p.blood_group || "",
    manglik: p.manglik || "Do Not Know",
    sampraday_id: p.sampraday_id || "",
    sampraday_details: p.sampraday_details || "",
    has_disability: Boolean(p.has_disability),
    disability_description: p.disability_description || "",
    about_me: p.about_me || "",

    caste_id: p.caste_id || "",
    referral_source_id: p.referral_source_id || "",
    referral_name: p.referral_name || "",
    sakha_id: p.sakha_id || "",
    dadisa_sakha_id: p.dadisa_sakha_id || "",
    nanisa_sakha_id: p.nanisa_sakha_id || "",
    mamasa_sakha_id: p.mamasa_sakha_id || "",
    state_id: p.state_id || "",
    district_id: p.district_id || "",
    tehsil_id: p.tehsil_id || "",
    village_id: p.village_id || "",

    contacts: contacts.length ? contacts : [],

    addresses: safeArray(p.addresses).length
      ? safeArray(p.addresses).map((address) => ({
          address_type: address.address_type || "current",
          country_id: address.country_id || "",
          address_line1: address.address_line1 || "",
          address_line2: address.address_line2 || "",
          state_id: address.state_id || "",
          district_id: address.district_id || "",
          tehsil_id: address.tehsil_id || "",
          city_id: address.city_id || "",
          village_id: address.village_id || "",
          visa_status_id: address.visa_status_id || "",
          visa_name_id: address.visa_name_id || "",
          pincode: address.pincode || "",
        }))
      : [{ ...emptyAddress }],

    educations: safeArray(p.educations).length
      ? safeArray(p.educations).map((e, index) => ({
          education_category_id: e.education_category_id || "",
          degree_name: e.degree_name || e.degree || "",
          education_description: e.education_description || e.field || "",
          institution_name: e.institution_name || e.institution || "",
          education_country_id: e.education_country_id || "",
          education_city_id: e.education_city_id || "",
          education_location: e.education_location || "",
          passing_year: e.passing_year || e.year || "",
          education_status: e.education_status || "completed",
          is_highest_degree: Boolean(e.is_highest_degree),
          sort_order: e.sort_order || index + 1,
        }))
      : [{ ...emptyEducation, is_highest_degree: true, sort_order: 1 }],

    occupations: safeArray(p.occupations).length
      ? safeArray(p.occupations).map((occupation, index) => ({
          occupation_type_id:
            occupation.occupation_type_id ||
            findConfigIdByLabel(lookups.occupationTypes, occupation.occupation_type),
          occupation_type: occupation.occupation_type || "",
          company_name: occupation.company_name || "",
          occupation_details: occupation.occupation_details || "",
          work_profile: occupation.work_profile || "",
          annual_income_amount: occupation.annual_income_amount ?? "",
          annual_income_currency_code:
            normalizeOccupationCurrencyCode(
              occupation.annual_income_currency_code,
              occupation.annual_income_amount
            ) || "INR",
          annual_turnover_amount: occupation.annual_turnover_amount ?? "",
          annual_turnover_currency_code:
            normalizeOccupationCurrencyCode(
              occupation.annual_turnover_currency_code,
              occupation.annual_turnover_amount
            ) || "INR",
          is_primary: occupation.is_primary === undefined ? index === 0 : Boolean(occupation.is_primary),
          sort_order: occupation.sort_order || index + 1,
          status: occupation.status || "active",
        }))
      : [{
          ...emptyOccupation,
          occupation_type_id: findConfigIdByLabel(lookups.occupationTypes, p.occupation_type),
          occupation_type: p.occupation_type || "",
          company_name: p.company_name || "",
          occupation_details: p.current_occupation_details || "",
          work_profile: p.work_profile || "",
          annual_income_amount: p.annual_income_amount ?? "",
          annual_income_currency_code:
            normalizeOccupationCurrencyCode(p.annual_income_currency_code, p.annual_income_amount) || "INR",
          annual_turnover_amount: p.annual_turnover_amount ?? "",
          annual_turnover_currency_code:
            normalizeOccupationCurrencyCode(p.annual_turnover_currency_code, p.annual_turnover_amount) || "INR",
          is_primary: true,
          sort_order: 1,
        }],

    relatives: safeArray(p.relatives).length
      ? safeArray(p.relatives).map((r, index) => ({
          relation_type: r.relation_type || "",
          relation_type_id: findConfigIdByLabel(lookups.relationTypes, r.relation_type),
          family_side: isFamilySectionRelative(r, lookups.relationTypes) ? "none" : normalizeRelativeFamilySide(r.family_side),
          first_name: r.first_name || "",
          middle_name: r.middle_name || "",
          last_name: r.last_name || "",
          full_name: r.full_name || "",
          gender: r.gender || "",
          phone: r.phone || "",
          email: r.email || "",
          contacts: buildRelativeContacts(r),
          city_id: r.city_id || "",
          marital_status: r.marital_status || "",
          partner_name: r.partner_name || "",
          is_future_prospect: Boolean(r.is_future_prospect),
          future_prospect_birth_date: dateOnly(r.future_prospect_birth_date),
          future_prospect_eligible_year:
            r.future_prospect_eligible_year !== undefined && r.future_prospect_eligible_year !== null
              ? String(r.future_prospect_eligible_year)
              : r.expected_eligible_year !== undefined && r.expected_eligible_year !== null
                ? String(r.expected_eligible_year)
                : "",
          is_deceased: Boolean(r.is_deceased),
          occupation_type_id: findConfigIdByLabel(lookups.occupationTypes, r.occupation_type),
          occupation_type: r.occupation_type || "",
          occupation_details: r.occupation_details || "",
          company_name: r.company_name || "",
          work_profile: r.work_profile || "",
          occupations: safeArray(r.occupations).length
            ? safeArray(r.occupations).map((occupation, occupationIndex) => ({
                occupation_type_id:
                  occupation.occupation_type_id ||
                  findConfigIdByLabel(lookups.occupationTypes, occupation.occupation_type),
                occupation_type: occupation.occupation_type || "",
                company_name: occupation.company_name || "",
                occupation_details: occupation.occupation_details || "",
                work_profile: occupation.work_profile || "",
                annual_income_amount: occupation.annual_income_amount ?? "",
                annual_income_currency_code:
                  normalizeOccupationCurrencyCode(occupation.annual_income_currency_code, occupation.annual_income_amount) || "INR",
                annual_turnover_amount: occupation.annual_turnover_amount ?? "",
                annual_turnover_currency_code:
                  normalizeOccupationCurrencyCode(occupation.annual_turnover_currency_code, occupation.annual_turnover_amount) || "INR",
                is_primary: occupation.is_primary === undefined ? occupationIndex === 0 : Boolean(occupation.is_primary),
                sort_order: occupation.sort_order || occupationIndex + 1,
                status: occupation.status || "active",
              }))
            : getRelativeOccupationRows({
                occupation_type_id: findConfigIdByLabel(lookups.occupationTypes, r.occupation_type),
                occupation_type: r.occupation_type || "",
                occupation_details: r.occupation_details || "",
                company_name: r.company_name || "",
                work_profile: r.work_profile || "",
                status: r.status || "active",
              }),
          status: r.status || "active",
          sort_order: r.sort_order || index + 1,
          education: safeArray(r.education).length
            ? safeArray(r.education).map((e, eduIndex) => ({
                education_category_id: e.education_category_id || "",
                degree_name: e.degree_name || "",
                education_description: e.education_description || [e.institution_name, e.passing_year].filter(Boolean).join(" | "),
                institution_name: "",
                education_country_id: e.education_country_id || "",
                education_city_id: e.education_city_id || "",
                education_location: e.education_location || "",
                passing_year: "",
                education_status: e.education_status || "completed",
                is_highest_degree: Boolean(e.is_highest_degree),
                sort_order: e.sort_order || eduIndex + 1,
              }))
            : [{ ...emptyRelativeEducation }],
        }))
      : [],

    preferences: {
      preferred_age_min: resolvePreferredAgeInputValue(p.preferences, "min"),
      preferred_age_max: resolvePreferredAgeInputValue(p.preferences, "max"),
      preferred_age_min_unit: resolvePreferredAgeInputUnit(p.preferences, "min"),
      preferred_age_max_unit: resolvePreferredAgeInputUnit(p.preferences, "max"),
      preferred_age_gap_months: p.preferences?.preferred_age_gap_months ?? "",
      preferred_caste_ids: safeNumberArray(p.preferences?.preferred_caste_ids),
      non_preferred_sakha_ids: safeNumberArray(p.preferences?.non_preferred_sakha_ids),
      preferred_city_ids: safeNumberArray(
        p.preferences?.preferred_city_ids?.length
          ? p.preferences.preferred_city_ids
          : (
              safeNumberArray(p.preferences?.preferred_city_tier_ids).length
                ? expandPreferredCityIds(p.preferences?.preferred_city_tier_ids, lookups.cityTiers)
                : (p.preferences?.preferred_location_id ? [p.preferences.preferred_location_id] : [])
            )
      ),
      non_preferred_city_ids: safeNumberArray(p.preferences?.non_preferred_city_ids),
      preferred_city_tier_ids: safeNumberArray(p.preferences?.preferred_city_tier_ids),
      preferred_education_category_ids: safeNumberArray(p.preferences?.preferred_education_category_ids),
      non_preferred_education_category_ids: safeNumberArray(p.preferences?.non_preferred_education_category_ids),
      preferred_degrees: safeArray(p.preferences?.preferred_degrees),
      non_preferred_degrees: safeArray(p.preferences?.non_preferred_degrees),
      preferred_occupation_types: safeArray(p.preferences?.preferred_occupation_types),
      non_preferred_occupation_types: safeArray(p.preferences?.non_preferred_occupation_types),
      notes: p.preferences?.notes || "",
    },

    persona: {
      ...emptyPersona,
      tier: personaRow?.tier || p.persona_tier_cache || "",
      status: personaRow?.status || "active",
      completeness_pct: personaRow?.completeness_pct ?? "",
      payment_amount: personaRow?.payment_amount ?? "",
      valid_from: dateOnly(personaRow?.valid_from),
      valid_until: dateOnly(personaRow?.valid_until),
    },

    payment: {
      ...emptyPayment,
      amount: "",
      payment_status: "paid",
      payment_method: "",
      transaction_ref: "",
      paid_at: "",
    },

    tags: safeArray(p.tags).map((t) => Number(typeof t === "object" ? t.id : t)).filter(Boolean),
  };
}

function buildPayload(form, lookups, originalForm = null) {
  const gender = getLookupName(lookups.genders, form.gender_id) || form.gender;
  const marital_status = getLookupName(lookups.maritalStatuses, form.marital_status_id) || form.marital_status || "single";
  const complexion = getLookupName(lookups.complexions, form.complexion_id) || form.complexion;
  const blood_group = getLookupName(lookups.bloodGroups, form.blood_group_id) || form.blood_group;
  const maritalStatusChanged =
    originalForm &&
    String(form.marital_status_id || "") !== String(originalForm.marital_status_id || "");
  const marital_status_description = maritalStatusChanged
    ? null
    : form.marital_status_description || null;

  const highestEdu =
    form.educations.find((e) => e.is_highest_degree) ||
    form.educations.find((e) => e.degree_name || e.institution_name) ||
    form.educations[0];

  const occupations = safeArray(form.occupations)
    .filter(occupationHasContent)
    .map((occupation, index) => normalizeOccupationPayloadRow(occupation, lookups.occupationTypes, index));
  const occupation =
    occupations[0] ||
    form.occupations[0] ||
    {};
  const occupation_type =
    getLookupName(lookups.occupationTypes, occupation.occupation_type_id) ||
    occupation.occupation_type ||
    null;

  const contacts = form.contacts
    .filter((contact) => String(contact.contact_value || "").trim())
    .map((contact) => normalizeSelfContact(contact, form));
  const profileContactValues = deriveProfileContactValues(contacts);

  return {
    first_name: form.first_name,
    middle_name: form.middle_name || null,
    last_name: form.last_name,
    gender_id: form.gender_id || null,
    gender,
    birth_date: form.birth_date || form.dob,
    dob: form.birth_date || form.dob,
    birth_time: form.birth_time || null,
    birth_country: form.birth_country || "India",
    birth_state_id: form.birth_state_id || null,
    birth_district_id: form.birth_district_id || null,
    birth_tehsil_id: form.birth_tehsil_id || null,
    birth_place: form.birth_place || null,
    birth_place_city_id: form.birth_place_city_id || form.birth_place_id || null,
    birth_place_id: form.birth_place_city_id || form.birth_place_id || null,
    birth_latitude: form.birth_latitude || null,
    birth_longitude: form.birth_longitude || null,
    city_id: form.city_id || form.current_location_id || null,
    current_location_id: form.city_id || form.current_location_id || null,

    marital_status_id: form.marital_status_id || null,
    marital_status,
    marital_status_description,
    has_children: Boolean(form.has_children),
    marital_field_details: form.marital_field_details || null,
    height_cm: form.height_cm || form.height || null,
    height: form.height_cm || form.height || null,
    complexion_id: form.complexion_id || null,
    complexion,
    blood_group_id: form.blood_group_id || null,
    blood_group,
    manglik: form.manglik || "Do Not Know",
    sampraday_id: form.sampraday_id || null,
    sampraday_details: form.sampraday_details || null,
    has_disability: Boolean(form.has_disability),
    disability_description: form.disability_description || null,
    about_me: form.about_me || null,

    caste_id: form.caste_id || null,
    referral_source_id: form.referral_source_id || null,
    referral_name: String(form.referral_name || "").trim() || null,
    sakha_id: form.sakha_id || null,
    dadisa_sakha_id: form.dadisa_sakha_id || null,
    nanisa_sakha_id: form.nanisa_sakha_id || null,
    mamasa_sakha_id: form.mamasa_sakha_id || null,
    village_id: form.village_id || null,

    education_category_id: highestEdu?.education_category_id || null,
    education_summary:
      [highestEdu?.degree_name, highestEdu?.education_description].filter(Boolean).join(" - ") || null,

    occupation_type_id: occupation.occupation_type_id || null,
    occupation_type,
    company_name: occupation.company_name || null,
    current_occupation_details: occupation.occupation_details || null,
    work_profile: occupation.work_profile || null,
    annual_income_amount: occupation.annual_income_amount || null,
    annual_income_currency_code: normalizeOccupationCurrencyCode(
      occupation.annual_income_currency_code,
      occupation.annual_income_amount
    ),
    annual_turnover_amount: occupation.annual_turnover_amount || null,
    annual_turnover_currency_code: normalizeOccupationCurrencyCode(
      occupation.annual_turnover_currency_code,
      occupation.annual_turnover_amount
    ),

    contacts,
    mobile: profileContactValues.mobile,
    email: profileContactValues.email,

    educations: form.educations
      .filter((e) => e.degree_name || e.institution_name || e.education_description)
      .map((e, index) => {
        const educationLocation = safeArray(lookups.locations).find((location) => Number(location.id) === Number(e.education_city_id));
        return {
          ...e,
          education_country_id: e.education_country_id || getLocationCountryId(educationLocation),
          education_location: e.education_location || getLocationOptionLabel(educationLocation),
          education_status: e.education_status || "completed",
          sort_order: e.sort_order || index + 1,
          is_highest_degree: Boolean(e.is_highest_degree),
        };
      }),

    occupations,

    relatives: form.relatives
      .map((r, index) => {
        const normalizedRelative = normalizeRelativeForSection(r, lookups.relationTypes);
        const relativeContactValues = deriveRelativeContactValues(r);
        const showRelativeMaritalStatus = shouldShowRelativeMaritalStatus(normalizedRelative, lookups.relationTypes);
        const isUnmarriedRelative = showRelativeMaritalStatus && isRelativeUnmarriedStatus(normalizedRelative.marital_status);
        const relativeOccupations = getRelativeOccupationRows(normalizedRelative)
          .filter(occupationHasContent)
          .map((occupation, occupationIndex) => normalizeOccupationPayloadRow(occupation, lookups.occupationTypes, occupationIndex));
        const primaryRelativeOccupation = relativeOccupations.find((occupation) => occupation.is_primary) || relativeOccupations[0] || null;
        const relativeBase = { ...normalizedRelative };
        delete relativeBase.is_relative;
        delete relativeBase.sakha_id;
        return {
          ...relativeBase,
          phone: relativeContactValues.phone || null,
          email: relativeContactValues.email || null,
          relation_type:
            getLookupName(lookups.relationTypes, normalizedRelative.relation_type_id) ||
            normalizedRelative.relation_type ||
            null,
          occupation_type:
            primaryRelativeOccupation?.occupation_type ||
            null,
          occupation_type_id: primaryRelativeOccupation?.occupation_type_id || null,
          company_name: primaryRelativeOccupation?.company_name || null,
          occupation_details: primaryRelativeOccupation?.occupation_details || null,
          work_profile: primaryRelativeOccupation?.work_profile || null,
          occupations: relativeOccupations,
          marital_status: showRelativeMaritalStatus ? (normalizedRelative.marital_status || null) : null,
          partner_name: showRelativeMaritalStatus && isRelativeMarriedStatus(normalizedRelative.marital_status) ? (normalizedRelative.partner_name || null) : null,
          is_future_prospect: isUnmarriedRelative && Boolean(normalizedRelative.is_future_prospect),
          future_prospect_birth_date: isUnmarriedRelative && normalizedRelative.is_future_prospect ? (normalizedRelative.future_prospect_birth_date || null) : null,
          future_prospect_eligible_year: isUnmarriedRelative && normalizedRelative.is_future_prospect ? (normalizedRelative.future_prospect_eligible_year || null) : null,
          full_name: normalizedRelative.full_name || [normalizedRelative.first_name, normalizedRelative.middle_name, normalizedRelative.last_name].filter(Boolean).join(" "),
          sort_order: normalizedRelative.sort_order || index + 1,
          education: safeArray(normalizedRelative.education).filter((e) => e.degree_name || e.education_category_id || e.education_description),
        };
      })
      .filter((r) => r.full_name || r.first_name || r.relation_type),

    addresses: form.addresses
      .filter((a) => a.country_id || a.address_line1 || a.address_line2 || a.city_id || a.pincode || a.visa_status_id || a.visa_name_id)
      .map((a) => ({
        ...a,
        country_id: a.country_id || null,
        state_id: a.state_id || null,
        district_id: a.district_id || null,
        tehsil_id: a.tehsil_id || null,
        city_id: a.city_id || null,
        village_id: a.village_id || null,
        visa_status_id: a.visa_status_id || null,
        visa_name_id: a.visa_name_id || null,
        pincode: a.pincode || null,
        is_primary: false,
      })),

    preferences: {
      ...form.preferences,
      preferred_age_gap_months: null,
      preferred_city_tier_ids: [],
      preferred_city_ids: safeNumberArray(form.preferences.preferred_city_ids),
      non_preferred_city_ids: safeNumberArray(form.preferences.non_preferred_city_ids),
      preferred_education_category_ids: safeNumberArray(form.preferences.preferred_education_category_ids),
      non_preferred_education_category_ids: safeNumberArray(form.preferences.non_preferred_education_category_ids),
      preferred_degrees: safeArray(form.preferences.preferred_degrees).map((value) => String(value || "").trim()).filter(Boolean),
      non_preferred_degrees: safeArray(form.preferences.non_preferred_degrees).map((value) => String(value || "").trim()).filter(Boolean),
      preferred_occupation_types: safeArray(form.preferences.preferred_occupation_types).map((value) => String(value || "").trim()).filter(Boolean),
      non_preferred_occupation_types: safeArray(form.preferences.non_preferred_occupation_types).map((value) => String(value || "").trim()).filter(Boolean),
      preferred_age_min_unit:
        String(form.preferences.preferred_age_min_unit || "").trim().toLowerCase() === "years" ? "years" : "months",
      preferred_age_max_unit:
        String(form.preferences.preferred_age_max_unit || "").trim().toLowerCase() === "years" ? "years" : "months",
      preferred_age_min: convertPreferredAgeToMonths(form.preferences.preferred_age_min, form.preferences.preferred_age_min_unit),
      preferred_age_max: convertPreferredAgeToMonths(form.preferences.preferred_age_max, form.preferences.preferred_age_max_unit),
    },
    persona: normalizePersonaPayload(form.persona),
    tags: form.tags,
    status: "active",
  };
}

// Duplicate scoring helpers
function pickPayloadFields(payload, keys = []) {
  return keys.reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(payload, key)) {
      acc[key] = payload[key];
    }
    return acc;
  }, {});
}

function shouldValidateEditSection(sectionKey) {
  return String(sectionKey || "").trim().toLowerCase() === "identity";
}

function sectionRequiresProfileUpdate(sectionKey) {
  return !["photos", "documents"].includes(String(sectionKey || "").trim().toLowerCase());
}

function buildSectionPayload(form, lookups, originalForm = null, sectionKey = "") {
  const payload = buildPayload(form, lookups, originalForm);
  const normalizedSection = String(sectionKey || "").trim().toLowerCase();

  switch (normalizedSection) {
    case "identity":
      return pickPayloadFields(payload, [
        "first_name",
        "middle_name",
        "last_name",
        "gender_id",
        "gender",
        "birth_date",
        "dob",
        "birth_time",
        "birth_country",
        "birth_state_id",
        "birth_district_id",
        "birth_tehsil_id",
        "birth_place_city_id",
        "birth_place_id",
        "birth_latitude",
        "birth_longitude",
        "height_cm",
        "height",
        "about_me",
      ]);
    case "personal":
      return pickPayloadFields(payload, [
        "marital_status_id",
        "marital_status",
        "marital_status_description",
        "has_children",
        "marital_field_details",
        "complexion_id",
        "complexion",
        "blood_group_id",
        "blood_group",
        "manglik",
        "sampraday_id",
        "sampraday_details",
        "has_disability",
        "disability_description",
        "caste_id",
        "referral_name",
        "referral_source_id",
        "sakha_id",
        "dadisa_sakha_id",
        "nanisa_sakha_id",
        "mamasa_sakha_id",
        "village_id",
      ]);
    case "contacts":
      return pickPayloadFields(payload, ["contacts", "mobile", "email"]);
    case "addresses":
      return pickPayloadFields(payload, ["addresses"]);
    case "education":
      return pickPayloadFields(payload, [
        "educations",
        "occupations",
        "education_category_id",
        "education_summary",
        "occupation_type_id",
        "occupation_type",
        "company_name",
        "current_occupation_details",
        "work_profile",
        "annual_income_amount",
        "annual_turnover_amount",
      ]);
    case "family":
    case "relatives":
      return pickPayloadFields(payload, ["relatives"]);
    case "preferences":
      return pickPayloadFields(payload, ["preferences"]);
    case "persona":
      return pickPayloadFields(payload, ["persona"]);
    case "tags":
      return pickPayloadFields(payload, ["tags"]);
    default:
      return payload;
  }
}

function normalizePersonaPayload(persona = {}) {
  const tier = String(persona?.tier || "").trim();
  if (!tier) return null;

  return {
    tier,
    status: persona?.status || "active",
    completeness_pct: persona?.completeness_pct || null,
    payment_amount: persona?.payment_amount || null,
    valid_from: persona?.valid_from || null,
    valid_until: persona?.valid_until || null,
  };
}

function scoreDuplicate(source, candidate) {
  let matchedWeight = 0;
  const reasons = [];
  const strictMatch = hasStrictDuplicateEvidence(source, candidate);

  const sDob = source.birth_date || source.dob;
  const cDob = candidate.birth_date || candidate.dob;
  const totalSignals = 5 + (source.birth_time ? 1 : 0);

  if (
    sDob &&
    cDob &&
    String(sDob).slice(0, 10) === String(cDob).slice(0, 10)
  ) {
    matchedWeight += 1;
    reasons.push("Same date of birth");
  }

  if (
    source.birth_time &&
    candidate.birth_time &&
    String(source.birth_time).slice(0, 5) ===
      String(candidate.birth_time).slice(0, 5)
  ) {
    matchedWeight += 1;
    reasons.push("Same birth time");
  }

  if (
    source.gender &&
    candidate.gender &&
    normalizeText(source.gender) === normalizeText(candidate.gender)
  ) {
    matchedWeight += 1;
    reasons.push("Same gender");
  }

  matchedWeight += scoreNameSignal(source.first_name, candidate.first_name, "first name", reasons);
  matchedWeight += scoreNameSignal(source.middle_name, candidate.middle_name, "middle name", reasons);
  matchedWeight += scoreNameSignal(source.last_name, candidate.last_name, "last name", reasons);

  return {
    score: Math.min(Number((matchedWeight / Math.max(totalSignals, 1)).toFixed(3)), 1),
    reasons,
    candidate,
    strictMatch,
  };
}

function hasStrictDuplicateEvidence(source, candidate) {
  const sourceFirst = normalizeIndianNamePart(source.first_name);
  const sourceMiddle = normalizeIndianNamePart(source.middle_name);
  const sourceLast = normalizeIndianNamePart(source.last_name);
  const candidateFirst = normalizeIndianNamePart(candidate.first_name);
  const candidateMiddle = normalizeIndianNamePart(candidate.middle_name);
  const candidateLast = normalizeIndianNamePart(candidate.last_name);
  const dobMatch =
    !!(source.birth_date || source.dob) &&
    !!(candidate.birth_date || candidate.dob) &&
    String(source.birth_date || source.dob).slice(0, 10) === String(candidate.birth_date || candidate.dob).slice(0, 10);
  const genderMatch =
    !!source.gender &&
    !!candidate.gender &&
    normalizeText(source.gender) === normalizeText(candidate.gender);
  const firstMatch = !!sourceFirst && !!candidateFirst && sourceFirst === candidateFirst;
  const middleMatch = !!sourceMiddle && !!candidateMiddle && sourceMiddle === candidateMiddle;
  const lastMatch = !!sourceLast && !!candidateLast && sourceLast === candidateLast;
  const sourceBirthTime = normalizeDuplicateBirthTime(source.birth_time);
  const candidateBirthTime = normalizeDuplicateBirthTime(candidate.birth_time);
  const birthTimeMatch = !sourceBirthTime || (candidateBirthTime && sourceBirthTime === candidateBirthTime);

  return firstMatch && middleMatch && lastMatch && genderMatch && dobMatch && birthTimeMatch;
}

function normalizeDuplicateBirthTime(value) {
  const normalized = String(value || "").trim();
  return normalized ? normalized.slice(0, 5) : "";
}

function scoreNameSignal(sourceValue, candidateValue, label, reasons) {
  const sourceName = normalizeIndianNamePart(sourceValue);
  const candidateName = normalizeIndianNamePart(candidateValue);
  if (!sourceName || !candidateName) return 0;

  if (sourceName === candidateName) {
    reasons.push(`Same ${label}`);
    return 1;
  }

  const sourcePhonetic = indianPhoneticCode(sourceName, "primary");
  const candidatePhonetic = indianPhoneticCode(candidateName, "primary");
  if (sourcePhonetic && candidatePhonetic && sourcePhonetic === candidatePhonetic) {
    reasons.push(`Similar ${label}`);
    return 0.8;
  }

  if (
    sourceName.length >= 3 &&
    candidateName.length >= 3 &&
    (sourceName.includes(candidateName) || candidateName.includes(sourceName))
  ) {
    reasons.push(`Close ${label}`);
    return 0.35;
  }

  const overlap = tokenOverlapRatio(sourceName, candidateName);
  if (overlap > 0.2) {
    reasons.push(`Related ${label}`);
    return Number((overlap * 0.45).toFixed(3));
  }

  return 0;
}

function tokenOverlapRatio(left, right) {
  const leftTokens = normalizeIndianNamePart(left).split(/\s+/).filter(Boolean);
  const rightTokens = normalizeIndianNamePart(right).split(/\s+/).filter(Boolean);
  if (!leftTokens.length || !rightTokens.length) return 0;

  const rightTokenSet = new Set(rightTokens);
  const matches = leftTokens.filter((token) => rightTokenSet.has(token)).length;
  return matches / Math.max(leftTokens.length, rightTokens.length);
}

function simpleProfilePhoneticKey(firstName, middleName, lastName, fullName = "") {
  const parts = resolveIndianNameParts(firstName, middleName, lastName, fullName);
  if (!parts.length) return "";

  const primary = parts.map((part) => indianPhoneticCode(part, "primary")).filter(Boolean).join("-");
  return primary || "";
}

function resolveIndianNameParts(firstName, middleName, lastName, fullName = "") {
  let first = firstName || "";
  let middle = middleName || "";
  let last = lastName || "";

  if ((!first || !last) && fullName) {
    const tokens = normalizeText(fullName).split(/\s+/).filter(Boolean);
    first = first || tokens[0] || "";

    if (!middle && tokens.length > 2) {
      middle = tokens.slice(1, -1).join(" ");
    }

    last = last || tokens[tokens.length - 1] || "";
  }

  return [first, middle, last]
    .map(normalizeIndianNamePart)
    .filter(Boolean);
}

function normalizeIndianNamePart(value) {
  return normalizeText(value)
    .split(/\s+/)
    .filter(Boolean)
    .join(" ");
}

function indianPhoneticCode(part, mode = "primary") {
  return normalizeIndianNamePart(part)
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => indianPhoneticToken(token, mode))
    .filter(Boolean)
    .join("");
}

function indianPhoneticToken(token, mode = "primary") {
  let value = normalizeText(token).replace(/[^a-z0-9]/g, "");
  if (!value) return "";

  value = value
    .replace(/^kn/, "n")
    .replace(/^gn/, "n")
    .replace(/^pn/, "n")
    .replace(/^wr/, "r")
    .replace(/^x/, "s")
    .replace(/aa+/g, "a")
    .replace(/ee+|ii+/g, "i")
    .replace(/oo+|uu+/g, "u")
    .replace(/ou/g, "u")
    .replace(/ae/g, "e")
    .replace(/ck/g, "k")
    .replace(/q/g, "k")
    .replace(/tch/g, "ch")
    .replace(/chh/g, "ch")
    .replace(/sch/g, "s")
    .replace(/sh/g, "s")
    .replace(/zh/g, mode === "secondary" ? "j" : "s")
    .replace(/ph/g, "f")
    .replace(/bh/g, "b")
    .replace(/dh/g, "d")
    .replace(/th/g, "t")
    .replace(/kh/g, "k")
    .replace(/gh/g, "g")
    .replace(/jh/g, "j")
    .replace(/wh/g, mode === "secondary" ? "w" : "v")
    .replace(/w/g, mode === "secondary" ? "w" : "v")
    .replace(/v/g, mode === "secondary" ? "w" : "v")
    .replace(/z/g, mode === "secondary" ? "s" : "j")
    .replace(/c(?=[eiy])/g, mode === "secondary" ? "s" : "k")
    .replace(/c/g, "k")
    .replace(/y/g, "i")
    .replace(/h$/g, "")
    .replace(/(.)\1+/g, "$1");

  if (!value) return "";

  const mapped = mapIndianPhoneticChars(value, mode);
  if (!mapped) return "";

  const first = mapped[0];
  const tail = mapped
    .slice(1)
    .replace(/[AEIOU]/g, "")
    .replace(/(.)\1+/g, "$1");

  return `${first}${tail}`.slice(0, 8);
}

function mapIndianPhoneticChars(value, mode = "primary") {
  const map = {
    a: "A",
    b: "B",
    c: "K",
    d: "T",
    e: "E",
    f: "F",
    g: "G",
    h: "",
    i: "I",
    j: "J",
    k: "K",
    l: "L",
    m: "M",
    n: "N",
    o: "O",
    p: "P",
    q: "K",
    r: "R",
    s: "S",
    t: "T",
    u: "U",
    v: mode === "secondary" ? "W" : "V",
    w: mode === "secondary" ? "W" : "V",
    x: "KS",
    y: "I",
    z: mode === "secondary" ? "S" : "J",
  };

  return value
    .split("")
    .map((char) => map[char] ?? char.toUpperCase())
    .join("");
}

// General helpers
function validateDuplicateFields(form) {
  if (!form.first_name?.trim()) return "First name is required.";
  if (!form.middle_name?.trim()) return "Middle name is required.";
  if (!form.last_name?.trim()) return "Last name is required.";
  if (!form.gender_id && !form.gender) return "Gender is required.";
  if (!form.birth_date && !form.dob) return "Date of birth is required.";
  return "";
}

function validateForSave(form) {
  if (!form.first_name?.trim()) return "First name is required.";
  if (!form.middle_name?.trim()) return "Middle name is required.";
  if (!form.last_name?.trim()) return "Last name is required.";
  if (!form.gender_id && !form.gender) return "Gender is required.";
  if (!form.birth_date && !form.dob) return "Date of birth is required.";
  return "";
}

function validateFutureProspectRelatives(relatives) {
  const items = Array.isArray(relatives) ? relatives : [];
  const invalidIndex = items.findIndex((relative) => {
    if (!relative?.is_future_prospect || !isRelativeUnmarriedStatus(relative?.marital_status)) return false;
    const rawYear = String(relative?.future_prospect_eligible_year || "").trim();
    if (!rawYear) return false;
    return !/^\d{4}$/.test(rawYear);
  });
  return invalidIndex >= 0 ? `Eligible year must be a 4-digit year for relative #${invalidIndex + 1}.` : "";
}

function validatePhoneContacts(contacts, label = "Contact") {
  const items = Array.isArray(contacts) ? contacts : [];
  for (let index = 0; index < items.length; index += 1) {
    const contact = items[index];
    if (!isIndianPhoneContactType(contact?.contact_type)) continue;
    const contactValue = String(contact?.contact_value || "").trim();
    if (!contactValue) continue;
    const error = validateIndianPhoneValue(contactValue);
    if (error) {
      return `${label} #${index + 1}: ${error}`;
    }
  }
  return "";
}

function validateRelativePhoneContacts(relatives) {
  const items = Array.isArray(relatives) ? relatives : [];
  for (let index = 0; index < items.length; index += 1) {
    const error = validatePhoneContacts(items[index]?.contacts, `Relative #${index + 1} contact`);
    if (error) return error;
  }
  return "";
}

function isRelativeUnmarriedStatus(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "unmarried" || normalized === "single";
}

function getLocationCountryId(location) {
  return location?.country_id || location?.countryId || location?.countryID || "";
}

function getCompactLocationOptionLabel(location) {
  if (!location) return "";
  return [
    location.city || location.name,
    location.state || location.state_name || location.stateName,
    String(location.country || location.country_name || location.countryName || "India").trim(),
  ]
    .filter(Boolean)
    .join(", ");
}

function getLocationOptionLabel(location) {
  if (!location) return "";
  return [
    location.city || location.name,
    location.tehsil || location.tehsil_name || location.tehsilName,
    location.district || location.district_name || location.districtName,
    location.state || location.state_name || location.stateName,
  ]
    .filter(Boolean)
    .join(", ");
}

function buildCityTierMap(cityTiers) {
  const map = new Map();
  safeArray(cityTiers).forEach((tier) => {
    safeNumberArray(tier?.city_ids).forEach((cityId) => {
      const existing = map.get(Number(cityId)) || [];
      existing.push(Number(tier.id));
      map.set(Number(cityId), existing);
    });
  });
  return map;
}

function LocationAutocompleteField({
  value,
  locations,
  onChange,
  inputStyle,
  placeholder = "Search location",
  getOptionLabel = getLocationOptionLabel,
}) {
  const wrapperRef = useRef(null);
  const safeLocations = useMemo(() => safeArray(locations), [locations]);
  const selectedLocation = useMemo(
    () => safeLocations.find((location) => Number(location.id) === Number(value)) || null,
    [safeLocations, value]
  );
  const selectedLabel = useMemo(() => getOptionLabel(selectedLocation), [getOptionLabel, selectedLocation]);
  const [query, setQuery] = useState(selectedLabel);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setQuery(selectedLabel);
  }, [selectedLabel]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false);
        const normalizedQuery = String(query || "").trim().toLowerCase();
        if (!normalizedQuery) {
          onChange("");
          setQuery("");
          return;
        }
        const exactMatch = safeLocations.find(
          (location) => getOptionLabel(location).toLowerCase() === normalizedQuery
        );
        if (exactMatch) {
          onChange(String(exactMatch.id));
          setQuery(getOptionLabel(exactMatch));
          return;
        }
        if (selectedLocation) {
          setQuery(selectedLabel);
          return;
        }
        setQuery("");
        onChange("");
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [getOptionLabel, open, onChange, query, safeLocations, selectedLabel, selectedLocation]);

  const filteredLocations = useMemo(() => {
    const normalizedQuery = String(query || "").trim().toLowerCase();
    const sortedLocations = safeLocations
      .slice()
      .sort((a, b) => getOptionLabel(a).localeCompare(getOptionLabel(b)));

    if (!normalizedQuery) return sortedLocations.slice(0, 50);
    return sortedLocations
      .filter((location) => getOptionLabel(location).toLowerCase().includes(normalizedQuery))
      .slice(0, 50);
  }, [getOptionLabel, query, safeLocations]);

  const handleSelect = (location) => {
    onChange(String(location.id));
    setQuery(getOptionLabel(location));
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} style={S.autocompleteWrap}>
      <input
        style={inputStyle}
        value={query}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
      />
      {open && (
        <div style={S.autocompleteMenu}>
          {filteredLocations.length ? (
            filteredLocations.map((location) => (
              <button
                key={location.id}
                type="button"
                style={S.autocompleteOption}
                onMouseDown={(event) => {
                  event.preventDefault();
                  handleSelect(location);
                }}
              >
                {getOptionLabel(location)}
              </button>
            ))
          ) : (
            <div style={S.autocompleteEmpty}>No cities found.</div>
          )}
        </div>
      )}
    </div>
  );
}

function getLocationCityName(location) {
  return String(location?.city || location?.name || "").trim();
}

function normalizeRelativeRelationLabel(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

const RELATION_GENDER_MAP = {
  father: "Male",
  mother: "Female",
  brother: "Male",
  sister: "Female",
  chachaji: "Male",
  bua: "Female",
  mamaji: "Male",
  masi: "Female",
  dada: "Male",
  dadi: "Female",
  nana: "Male",
  nani: "Female",
};

function isFamilyRelationType(value) {
  return FAMILY_RELATION_TYPES.has(normalizeRelativeRelationLabel(value));
}

function normalizeRelativeFamilySide(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "maternal" ? "maternal" : normalized === "paternal" ? "paternal" : "paternal";
}

function isFamilySectionRelative(relative, relationTypes = []) {
  const relationLabel = getRelativeRelationLabel(relative, relationTypes);
  if (isFamilyRelationType(relationLabel)) return true;
  return String(relative?.family_side || "").trim().toLowerCase() === "none";
}

function normalizeRelativeForSection(relative, relationTypes = []) {
  if (!relative || typeof relative !== "object") return { ...emptyRelative };
  const relationLabel = getRelativeRelationLabel(relative, relationTypes);
  if (isFamilyRelationType(relationLabel) || String(relative.family_side || "").trim().toLowerCase() === "none") {
    if (String(relative.family_side || "").trim().toLowerCase() === "none") return relative;
    return { ...relative, family_side: "none" };
  }
  const normalizedFamilySide = normalizeRelativeFamilySide(relative.family_side);
  const middleName = String(relative.middle_name || "").trim();
  const lastName = String(relative.last_name || "").trim();
  if (String(relative.family_side || "").trim().toLowerCase() === normalizedFamilySide && !middleName && !lastName) {
    return relative;
  }
  return {
    ...relative,
    family_side: normalizedFamilySide,
    middle_name: "",
    last_name: "",
  };
}

function applyRelativeGenderMapping(relative, relationTypes = []) {
  const relationLabel = normalizeRelativeRelationLabel(getRelativeRelationLabel(relative, relationTypes));
  const mappedGender = RELATION_GENDER_MAP[relationLabel];
  if (!mappedGender) return relative;
  return {
    ...relative,
    gender: mappedGender,
  };
}

function getRelativeNameParts(relative) {
  const explicitFirst = String(relative?.first_name || "").trim();
  const explicitMiddle = String(relative?.middle_name || "").trim();
  const explicitLast = String(relative?.last_name || "").trim();
  const fullNameParts = String(relative?.full_name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  return {
    first_name: explicitFirst || fullNameParts[0] || "",
    middle_name: explicitMiddle || "",
    last_name: explicitLast || (fullNameParts.length > 1 ? fullNameParts[fullNameParts.length - 1] : ""),
  };
}

function getFatherNameDefaults(relatives, currentIndex, relationTypes = []) {
  const father = safeArray(relatives).find((relative, index) => index !== currentIndex && normalizeRelativeRelationLabel(getRelativeRelationLabel(relative, relationTypes)) === "father");
  if (!father) return null;
  const fatherName = getRelativeNameParts(father);
  return {
    middle_name: fatherName.first_name,
    last_name: fatherName.last_name,
  };
}

function applyProfileFamilyNameDefaultsToRelatives(relatives, profile, relationTypes = []) {
  let changed = false;
  const nextRelatives = safeArray(relatives).map((relative) => {
    const nextRelative = applyProfileFamilyNameDefaults(relative, profile, relationTypes);
    if (nextRelative !== relative) changed = true;
    return nextRelative;
  });
  return changed ? nextRelatives : relatives;
}

function applyProfileFamilyNameDefaults(relative, profile, relationTypes = []) {
  if (!isFamilySectionRelative(relative, relationTypes)) return relative;
  const relationLabel = normalizeRelativeRelationLabel(getRelativeRelationLabel(relative, relationTypes));
  const profileMiddleName = String(profile?.middle_name || "").trim();
  const profileLastName = String(profile?.last_name || "").trim();
  if (!profileMiddleName && !profileLastName) return relative;

  const defaults = {};
  if (relationLabel === "father") {
    if (!String(relative?.first_name || "").trim() && profileMiddleName) defaults.first_name = profileMiddleName;
    if (!String(relative?.last_name || "").trim() && profileLastName) defaults.last_name = profileLastName;
  }

  if (relationLabel === "mother" || relationLabel === "brother" || relationLabel === "sister" || relationLabel === "sibling") {
    if (!String(relative?.middle_name || "").trim() && profileMiddleName) defaults.middle_name = profileMiddleName;
    if (!String(relative?.last_name || "").trim() && profileLastName) defaults.last_name = profileLastName;
  }

  return Object.keys(defaults).length ? { ...relative, ...defaults } : relative;
}

function applySiblingNameDefaults(relative, relatives, currentIndex, relationTypes = []) {
  if (!isFamilySectionRelative(relative, relationTypes)) return relative;
  const relationLabel = normalizeRelativeRelationLabel(getRelativeRelationLabel(relative, relationTypes));
  if (relationLabel !== "brother" && relationLabel !== "sister" && relationLabel !== "sibling") return relative;
  if (String(relative?.middle_name || "").trim() && String(relative?.last_name || "").trim()) return relative;

  const fatherDefaults = getFatherNameDefaults(relatives, currentIndex, relationTypes);
  if (!fatherDefaults) return relative;

  return {
    ...relative,
    middle_name: String(relative?.middle_name || "").trim() || fatherDefaults.middle_name || "",
    last_name: String(relative?.last_name || "").trim() || fatherDefaults.last_name || "",
  };
}

function normalizeRelativeSections(relatives, relationTypes = []) {
  let changed = false;
  const nextRelatives = safeArray(relatives).map((relative) => {
    const nextRelative = normalizeRelativeForSection(relative, relationTypes);
    if (nextRelative !== relative) changed = true;
    return nextRelative;
  });
  return changed ? nextRelatives : relatives;
}

function shouldShowRelativeMaritalStatus(relative, relationTypes = []) {
  const normalizedRelationType = normalizeRelativeRelationLabel(getRelativeRelationLabel(relative, relationTypes));
  return normalizedRelationType === "sister" || normalizedRelationType === "brother" || normalizedRelationType === "sibling";
}

function isRelativeMarriedStatus(value) {
  return String(value || "").trim().toLowerCase() === "married";
}

function getRelativePartnerNameLabel(relative, relationTypes = []) {
  const normalizedRelationType = normalizeRelativeRelationLabel(getRelativeRelationLabel(relative, relationTypes));
  if (normalizedRelationType === "sister") return "Husband Name";
  if (normalizedRelationType === "sibling") {
    const normalizedGender = String(relative?.gender || "").trim().toLowerCase();
    if (normalizedGender === "female") return "Husband Name";
  }
  return "Spouse Name";
}

function clearRelativePartnerField(relative) {
  return {
    ...relative,
    partner_name: "",
  };
}

function clearRelativeFutureProspectFields(relative) {
  return {
    ...relative,
    is_future_prospect: false,
    future_prospect_birth_date: "",
    future_prospect_eligible_year: "",
  };
}

function clearRelativeMaritalFields(relative) {
  return {
    ...clearRelativeFutureProspectFields(clearRelativePartnerField(relative)),
    marital_status: "",
  };
}

function getSelfContactName(form) {
  return [form?.first_name, form?.middle_name, form?.last_name]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ");
}

function getRelativeDisplayName(relative) {
  return (
    relative?.full_name ||
    [relative?.first_name, relative?.middle_name, relative?.last_name]
      .map((part) => String(part || "").trim())
      .filter(Boolean)
      .join(" ")
  );
}

function getRelativeRelationLabel(relative, relationTypes = []) {
  return getLookupName(relationTypes, relative?.relation_type_id) || relative?.relation_type || "";
}

function getRelativeLocationSummary(relative, lookups = {}) {
  const city = safeArray(lookups.locations).find((location) => Number(location.id) === Number(relative?.city_id));
  if (city) return getLocationOptionLabel(city);
  return String(relative?.country || "").trim();
}

function hasRelativeEducationValue(education) {
  return [
    education?.education_category_id,
    education?.degree_name,
    education?.education_description,
  ].some((value) => String(value || "").trim());
}

function getRelativeContactSummaries(relative) {
  return safeArray(relative?.contacts)
    .map((contact) => {
      const rawValue = String(contact?.contact_value || contact?.normalized_value || "").trim();
      if (!rawValue) return "";
      const type = String(contact?.contact_type || "mobile").trim();
      const value = isIndianPhoneContactType(type) ? toStoredIndianPhoneValue(rawValue) : rawValue;
      return {
        title: getContactValueFieldLabel(type),
        lines: [value],
      };
    })
    .filter(Boolean);
}

function getRelativeEducationSummaries(relative, lookups = {}) {
  return safeArray(relative?.education)
    .filter(hasRelativeEducationValue)
    .map((education) => {
      const category = getLookupName(lookups.educationCategories, education.education_category_id);
      return {
        title: education.degree_name || category || "Education",
        lines: [
          education.education_description ? `Details: ${education.education_description}` : "",
        ].filter(Boolean),
      };
    })
    .filter(Boolean);
}

function splitRelativeContactValues(value) {
  return String(value || "")
    .split(/[,\n;]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function buildRelativeContacts(relative) {
  const existingContacts = safeArray(relative?.contacts)
    .filter((contact) => String(contact?.contact_value || "").trim())
    .map((contact) => ({
      ...contact,
      contact_value: isIndianPhoneContactType(contact?.contact_type)
        ? getIndianPhoneInputValue(contact?.contact_value || contact?.normalized_value || "")
        : String(contact?.contact_value || "").trim(),
    }));
  if (existingContacts.length) return existingContacts;

  return [
    ...splitRelativeContactValues(relative?.phone).map((value) => ({ contact_type: "mobile", contact_value: getIndianPhoneInputValue(value) })),
    ...splitRelativeContactValues(relative?.email).map((value) => ({ contact_type: "email", contact_value: value })),
  ];
}

function deriveRelativeContactValues(relative) {
  const contacts = buildRelativeContacts(relative);
  const phoneValues = contacts
    .filter((contact) => isIndianPhoneContactType(contact?.contact_type))
    .map((contact) => toStoredIndianPhoneValue(contact?.contact_value || ""))
    .filter(Boolean);
  const emailValues = contacts
    .filter((contact) => String(contact?.contact_type || "").toLowerCase() === "email")
    .map((contact) => String(contact?.contact_value || "").trim())
    .filter(Boolean);

  return {
    phone: phoneValues.join(", "),
    email: emailValues.join(", "),
  };
}

function isProfilePhoneContactType(type) {
  return isIndianPhoneContactType(type);
}

function normalizeSelfContactType(contactType) {
  const normalized = String(contactType || "").trim().toLowerCase();
  return ["mobile", "whatsapp", "email"].includes(normalized) ? normalized : "mobile";
}

function normalizeSelfContact(contact, form) {
  const contactType = normalizeSelfContactType(contact?.contact_type);
  const contactValue = String(contact?.contact_value || "").trim();
  const storedContactValue = isProfilePhoneContactType(contactType)
    ? toStoredIndianPhoneValue(contactValue)
    : contactValue;

  return {
    ...contact,
    contact_type: contactType,
    contact_value: storedContactValue,
    normalized_value: isProfilePhoneContactType(contactType)
      ? normalizeIndianPhone(contactValue)
      : String(contactValue).toLowerCase(),
    contact_person_name: getSelfContactName(form),
    contact_person_relation: "self",
  };
}

function deriveProfileContactValues(contacts) {
  const mobile =
    contacts.find((contact) => contact.contact_type === "mobile")?.contact_value ||
    contacts.find((contact) => contact.contact_type === "whatsapp")?.contact_value ||
    contacts.find((contact) => contact.contact_type === "parent_mobile")?.contact_value ||
    "";
  const email = contacts.find((contact) => contact.contact_type === "email")?.contact_value || "";

  return {
    mobile: mobile || null,
    email: email || null,
  };
}

function isDuplicateSignalField(field) {
  return [
    "first_name",
    "middle_name",
    "last_name",
    "gender",
    "gender_id",
    "birth_date",
    "dob",
    "birth_time",
    "birth_place_city_id",
    "birth_place_id",
  ].includes(field);
}

function formatTierLabel(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function hasDegreeCategoryMapping(items) {
  return safeArray(items).some((item) => Number(item?.education_category_id) > 0);
}

function buildDegreeNameOptions(items, currentValue = "", educationCategoryId = "") {
  const normalizedItems = safeArray(items);
  const categoryId = Number(educationCategoryId);
  const hasCategoryMapping = hasDegreeCategoryMapping(normalizedItems);
  const scopedItems =
    hasCategoryMapping && Number.isFinite(categoryId) && categoryId > 0
      ? normalizedItems.filter((item) => Number(item?.education_category_id) === categoryId)
      : normalizedItems;

  const baseItems = scopedItems
    .map((item) => ({
      value: String(item?.name || item?.display_label || "").trim(),
      label: String(item?.name || item?.display_label || "").trim(),
    }))
    .filter((item) => item.value);

  const current = String(currentValue || "").trim();
  if (current && !baseItems.some((item) => item.value === current)) {
    baseItems.unshift({ value: current, label: `${current} (Current)` });
  }

  return baseItems;
}

function isSingleMaritalStatusId(value, items) {
  const label = getLookupName(items, value);
  const normalized = String(label || "").trim().toLowerCase();
  return normalized === "single" || normalized === "never married";
}

function shouldShowChildrenSection(maritalStatusId, items) {
  if (!maritalStatusId) return false;
  return !isSingleMaritalStatusId(maritalStatusId, items);
}

function getAddressCountryName(address, locations) {
  const selectedCountryId = Number(address?.country_id);
  if (!selectedCountryId) return "";

  const match = safeArray(locations).find(
    (location) => Number(location.country_id || location.countryId || location.countryID) === selectedCountryId
  );

  return String(match?.country || match?.country_name || match?.countryName || "").trim();
}

function shouldShowAddressVisaFields(address, locations) {
  const countryName = getAddressCountryName(address, locations);
  return Boolean(countryName) && countryName.toLowerCase() !== "india";
}

function applyLocationCascade(next, field, value, locations) {
  if (field === "country") {
    next.country = value;
    next.state_id = "";
    next.district_id = "";
    next.tehsil_id = "";
    next.city_id = "";
    next.current_location_id = "";
  }

  if (field === "state_id") {
    next.state_id = value;
    next.district_id = "";
    next.tehsil_id = "";
    next.city_id = "";
    next.current_location_id = "";
  }

  if (field === "district_id") {
    next.district_id = value;
    next.tehsil_id = "";
    next.city_id = "";
    next.current_location_id = "";
  }

  if (field === "tehsil_id") {
    next.tehsil_id = value;
    next.city_id = "";
    next.current_location_id = "";
  }

  if (field === "city_id" || field === "current_location_id") {
    next.city_id = value;
    next.current_location_id = value;
    const city = locations.find((location) => Number(location.id) === Number(value));
    if (city) {
      next.country = city.country || "India";
      next.state_id = city.state_id || "";
      next.district_id = city.district_id || "";
      next.tehsil_id = city.tehsil_id || "";
    }
  }

  if (field === "birth_country") {
    next.birth_country = value;
    next.birth_state_id = "";
    next.birth_district_id = "";
    next.birth_tehsil_id = "";
    next.birth_place_city_id = "";
    next.birth_place_id = "";
  }

  if (field === "birth_state_id") {
    next.birth_state_id = value;
    next.birth_district_id = "";
    next.birth_tehsil_id = "";
    next.birth_place_city_id = "";
    next.birth_place_id = "";
  }

  if (field === "birth_district_id") {
    next.birth_district_id = value;
    next.birth_tehsil_id = "";
    next.birth_place_city_id = "";
    next.birth_place_id = "";
  }

  if (field === "birth_tehsil_id") {
    next.birth_tehsil_id = value;
    next.birth_place_city_id = "";
    next.birth_place_id = "";
  }

  if (field === "birth_place_city_id" || field === "birth_place_id") {
    next.birth_place_city_id = value;
    next.birth_place_id = value;
    const city = locations.find((location) => Number(location.id) === Number(value));
    if (city) {
      next.birth_country = city.country || "India";
      next.birth_state_id = city.state_id || "";
      next.birth_district_id = city.district_id || "";
      next.birth_tehsil_id = city.tehsil_id || "";
    }
  }
}

function syncAddressVisaFields(address, locations) {
  if (shouldShowAddressVisaFields(address, locations)) return;
  address.visa_status_id = "";
  address.visa_name_id = "";
}

function uniqueBy(items, key) {
  const seen = new Set();
  const out = [];

  items.forEach((item) => {
    const value = item[key];
    if (value === null || value === undefined || value === "" || seen.has(String(value))) return;
    seen.add(String(value));
    out.push(item);
  });

  return out;
}

function safeNumberArray(value) {
  if (!Array.isArray(value)) return [];
  return value.map((x) => Number(x)).filter(Boolean);
}

function convertPreferredAgeToMonths(value, unit = "months") {
  if (value === "" || value === null || value === undefined) return null;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;
  return unit === "years" ? numericValue * 12 : numericValue;
}

function inferPreferredAgeUnit(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return "months";
  return numericValue % 12 === 0 ? "years" : "months";
}

function getStoredPreferredAgeUnit(preferences, side) {
  const unitValue =
    side === "min"
      ? preferences?.preferred_age_min_unit
      : preferences?.preferred_age_max_unit;
  const normalized = String(unitValue || "").trim().toLowerCase();
  return normalized === "years" || normalized === "months" ? normalized : "";
}

function resolvePreferredAgeStoredValue(preferences, side) {
  const directValue =
    side === "min"
      ? preferences?.preferred_age_min ?? preferences?.min_age
      : preferences?.preferred_age_max ?? preferences?.max_age;
  if (directValue !== null && directValue !== undefined && directValue !== "") {
    return directValue;
  }
  const gapValue = preferences?.preferred_age_gap_months;
  if (gapValue !== null && gapValue !== undefined && gapValue !== "") {
    return gapValue;
  }
  return null;
}

function resolvePreferredAgeInputValue(preferences, side) {
  const storedValue = resolvePreferredAgeStoredValue(preferences, side);
  if (storedValue === null || storedValue === undefined || storedValue === "") return "";
  const numericValue = Number(storedValue);
  if (!Number.isFinite(numericValue)) return "";
  const resolvedUnit = getStoredPreferredAgeUnit(preferences, side) || inferPreferredAgeUnit(storedValue);
  return resolvedUnit === "years" ? String(numericValue / 12) : String(numericValue);
}

function resolvePreferredAgeInputUnit(preferences, side) {
  const storedValue = resolvePreferredAgeStoredValue(preferences, side);
  if (storedValue === null || storedValue === undefined || storedValue === "") return "months";
  return getStoredPreferredAgeUnit(preferences, side) || inferPreferredAgeUnit(storedValue);
}

function expandPreferredCityIds(selectedTierIds, cityTiers) {
  const tierIdSet = new Set(safeNumberArray(selectedTierIds).map((value) => Number(value)));
  const cityIds = [];
  safeArray(cityTiers).forEach((tier) => {
    if (!tierIdSet.has(Number(tier.id))) return;
    cityIds.push(...safeNumberArray(tier.city_ids));
  });
  return Array.from(new Set(cityIds));
}

function dateOnly(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function formatDisplayDate(value) {
  const raw = dateOnly(value);
  if (!raw) return "";
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function toTimeInput(value) {
  if (!value) return "";
  return String(value).slice(0, 5);
}

function normalizeText(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ");
}

function isServiceOccupationTypeLabel(value) {
  return /\bservice\b/i.test(String(value || "").trim());
}

function getLookupName(list, id) {
  if (!id) return "";
  const item = list.find((x) => Number(x.id) === Number(id));
  return item?.name || item?.display_label || "";
}

function formatDropdownLabel(value) {
  return String(value || "")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function labelize(value) {
  if (!value) return "";
  return String(value || "")
    .trim()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatFileSize(bytes) {
  const size = Number(bytes || 0);
  if (!size) return "";
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(size / 1024))} KB`;
}

function buildDocumentUploadSummary(uploadedFiles) {
  const processedPdfFiles = safeArray(uploadedFiles).filter(
    (file) =>
      file?.mime_type === "application/pdf" &&
      Number(file?.original_size_bytes || 0) > MAX_UPLOAD_FILE_SIZE_BYTES
  );
  if (!processedPdfFiles.length) return "";

  const compressedCount = processedPdfFiles.filter((file) => Boolean(file?.compressed)).length;
  const totalOriginalBytes = processedPdfFiles.reduce(
    (sum, file) => sum + Number(file?.original_size_bytes || 0),
    0
  );
  const totalStoredBytes = processedPdfFiles.reduce(
    (sum, file) => sum + Number((file?.stored_size_bytes ?? file?.file_size_bytes) || 0),
    0
  );

  return `Backend PDF optimization processed ${processedPdfFiles.length} file${processedPdfFiles.length === 1 ? "" : "s"} above ${MAX_UPLOAD_FILE_SIZE_LABEL}. Reduced ${compressedCount} file${compressedCount === 1 ? "" : "s"}, total ${formatFileSize(totalOriginalBytes)} to ${formatFileSize(totalStoredBytes)}.`;
}

// These IDs are filled after lookups load using label matching in render/payload.
// Keeping fallback blank avoids wrong IDs if config_options order changes.
function findConfigIdByLabel(list, label) {
  const normalizedLabel = String(label || "").trim().toLowerCase();
  if (!normalizedLabel) return "";
  const match = safeArray(list).find((item) => String(item?.name || item?.display_label || "").trim().toLowerCase() === normalizedLabel);
  return match?.id ? String(match.id) : "";
}

function cloneFormSnapshot(value) {
  return JSON.parse(JSON.stringify(value));
}

function humanizeEditSection(sectionKey) {
  switch (String(sectionKey || "").trim().toLowerCase()) {
    case "identity":
      return "Identity & Basic Details";
    case "personal":
      return "Personal";
    case "contacts":
      return "Contacts";
    case "addresses":
      return "Addresses";
    case "education":
      return "Education & Career";
    case "family":
      return "Family";
    case "relatives":
      return "Relatives";
    case "preferences":
      return "Preferences";
    case "persona":
      return "Persona / Package";
    case "payments":
      return "Payments";
    case "tags":
      return "Tags";
    case "photos":
      return "Photos";
    case "documents":
      return "Documents";
    default:
      return "Section";
  }
}

function buildSectionSavedNotice(sectionKey, extraMessage = "") {
  const base = `${humanizeEditSection(sectionKey)} updated successfully.`;
  return extraMessage ? `${base} ${extraMessage}` : base;
}

function occupationHasContent(occupation) {
  return Boolean(
    occupation?.occupation_type_id ||
    occupation?.occupation_type ||
    occupation?.company_name ||
    occupation?.occupation_details ||
    occupation?.work_profile ||
    occupation?.annual_income_amount ||
    occupation?.annual_turnover_amount
  );
}

function normalizeOccupationPayloadRow(occupation, occupationTypes, index) {
  const occupation_type =
    getLookupName(occupationTypes, occupation.occupation_type_id) ||
    occupation.occupation_type ||
    null;
  const isServiceOccupation = isServiceOccupationTypeLabel(occupation_type);

  return {
    ...occupation,
    occupation_type_id: occupation.occupation_type_id || null,
    occupation_type,
    company_name: occupation.company_name || null,
    occupation_details: occupation.occupation_details || null,
    work_profile: occupation.work_profile || null,
    annual_income_amount: isServiceOccupation ? occupation.annual_income_amount || null : null,
    annual_income_currency_code: isServiceOccupation
      ? normalizeOccupationCurrencyCode(occupation.annual_income_currency_code, occupation.annual_income_amount)
      : null,
    annual_turnover_amount: isServiceOccupation ? null : occupation.annual_turnover_amount || null,
    annual_turnover_currency_code: isServiceOccupation
      ? null
      : normalizeOccupationCurrencyCode(occupation.annual_turnover_currency_code, occupation.annual_turnover_amount),
    is_primary: index === 0,
    sort_order: occupation.sort_order || index + 1,
    status: occupation.status || "active",
  };
}

function getRelativeOccupationRows(relative) {
  const occupations = safeArray(relative?.occupations);
  if (occupations.length) return occupations;
  if (!occupationHasContent(relative)) return [];
  return [{
    occupation_type_id: relative.occupation_type_id || "",
    occupation_type: relative.occupation_type || "",
    company_name: relative.company_name || "",
    occupation_details: relative.occupation_details || "",
    work_profile: relative.work_profile || "",
    annual_income_amount: relative.annual_income_amount || "",
    annual_income_currency_code: relative.annual_income_currency_code || "INR",
    annual_turnover_amount: relative.annual_turnover_amount || "",
    annual_turnover_currency_code: relative.annual_turnover_currency_code || "INR",
    is_primary: true,
    sort_order: 1,
    status: relative.status || "active",
  }];
}

function getEditableRelativeOccupations(relative) {
  const occupations = getRelativeOccupationRows(relative);
  return occupations.length ? normalizeRelativeOccupationRows(occupations) : [{ ...emptyRelativeOccupation }];
}

function normalizeRelativeOccupationRows(occupations) {
  return safeArray(occupations).map((occupation, index) => ({
    ...occupation,
    is_primary: index === 0,
    sort_order: index + 1,
    status: occupation.status || "active",
  }));
}

function getRelativeOccupationSummaries(relative, lookups = {}) {
  return getRelativeOccupationRows(relative)
    .filter(occupationHasContent)
    .map((occupation, occupationIndex, occupations) => {
      const occupationType = getLookupName(lookups.occupationTypes, occupation.occupation_type_id) || occupation.occupation_type;
      const amount = occupation.annual_income_amount
        ? `Annual Income: ${(occupation.annual_income_currency_code || "INR")} ${occupation.annual_income_amount}`
        : occupation.annual_turnover_amount
          ? `Annual Turnover: ${(occupation.annual_turnover_currency_code || "INR")} ${occupation.annual_turnover_amount}`
          : "";
      return {
        title: occupationType || `Occupation ${occupationIndex + 1}`,
        badge: occupations.length > 1 ? (occupationIndex === 0 ? "Primary" : `#${occupationIndex + 1}`) : "",
        lines: [
          occupation.company_name ? `Company: ${occupation.company_name}` : "",
          occupation.occupation_details ? `Details: ${occupation.occupation_details}` : "",
          occupation.work_profile ? `Work Profile: ${occupation.work_profile}` : "",
          amount,
        ].filter(Boolean),
      };
    })
    .filter(Boolean);
}

// Styles
const C = {
  bg: "#fff7f5",
  card: "#ffffff",
  border: "rgba(185,52,58,0.14)",
  borderStrong: "rgba(185,52,58,0.20)",
  borderBold: "rgba(185,52,58,0.28)",
  borderFocus: "rgba(185,52,58,0.18)",
  borderSoft: "rgba(185,52,58,0.10)",
  accent: "#D73C2E",
  accentDark: "#B9343A",
  accentLight: "#fff2ef",
  blue: "#B9343A",
  blueLight: "#fff2ef",
  text: "#252935",
  muted: "#6b6f7d",
  soft: "#fff7f5",
  error: "#D73C2E",
  green: "#B9343A",
  shadow: "rgba(37,41,53,0.07)",
  shadowLight: "rgba(37,41,53,0.04)",
  shadowStrong: "rgba(37,41,53,0.10)",
  shadowAccent: "rgba(185,52,58,0.18)",
};

const S = {
  page: {
    minHeight: "calc(100vh - 60px)",
    padding: "28px 18px 72px",
    background:
      `linear-gradient(135deg, #ffffff 0%, ${C.bg} 48%, ${C.soft} 100%)`,
    fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
  },

  center: {
    minHeight: "62vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 14,
  },

  spinner: {
    width: 38,
    height: 38,
    border: `3px solid ${C.borderSoft}`,
    borderTopColor: C.accent,
    borderRadius: "50%",
    animation: "spin 0.9s linear infinite",
  },

  muted: { color: C.muted, fontSize: 14, fontWeight: 800 },
  errorText: { color: C.error, fontSize: 14, fontWeight: 800 },

  topBar: { maxWidth: 1120, margin: "0 auto 18px" },

  backLink: {
    background: "none",
    border: "none",
    color: C.accent,
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
    padding: 0,
  },

  wrap: {
    maxWidth: 1120,
    margin: "0 auto",
    animation: "fadeUp 0.3s ease both",
  },

  editorShell: {
    display: "grid",
    gridTemplateColumns: "280px minmax(0, 1fr)",
    gap: 18,
    alignItems: "start",
  },

  sidebar: {
    position: "sticky",
    top: 18,
  },

  sidebarCard: {
    background: "rgba(255,255,255,0.94)",
    border: `1px solid ${C.border}`,
    borderRadius: 26,
    padding: 18,
    boxShadow: `0 16px 44px ${C.shadow}`,
  },

  sidebarLabel: {
    margin: 0,
    color: C.accent,
    fontSize: 11,
    fontWeight: 950,
    letterSpacing: 1.8,
    textTransform: "uppercase",
  },

  sidebarMenu: {
    marginTop: 14,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  sidebarItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 4,
    width: "100%",
    padding: "14px 16px",
    borderRadius: 18,
    border: `1px solid ${C.border}`,
    background: "#fff",
    cursor: "pointer",
    textAlign: "left",
  },

  sidebarItemActive: {
    background: `linear-gradient(135deg, ${C.accent}, ${C.accentDark})`,
    borderColor: C.accent,
    boxShadow: `0 14px 30px ${C.shadowAccent}`,
  },

  sidebarItemTitle: {
    fontSize: 14,
    fontWeight: 900,
    color: C.text,
  },

  sidebarItemTitleActive: {
    color: "#fff",
  },

  sidebarItemHint: {
    fontSize: 12,
    lineHeight: 1.4,
    color: C.muted,
  },

  sidebarItemHintActive: {
    color: "rgba(255,255,255,0.82)",
  },

  editorMain: {
    minWidth: 0,
  },

  sectionHeaderCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    padding: "20px 22px",
    marginBottom: 16,
    borderRadius: 26,
    background: "rgba(255,255,255,0.94)",
    border: `1px solid ${C.border}`,
    boxShadow: `0 16px 44px ${C.shadow}`,
  },

  activeSectionTitle: {
    margin: "6px 0 0",
    color: C.text,
    fontSize: 28,
    fontWeight: 950,
    letterSpacing: -0.6,
  },

  activeSectionHint: {
    margin: "6px 0 0",
    color: C.muted,
    fontSize: 13,
    lineHeight: 1.6,
  },

  hero: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 20,
    padding: 26,
    borderRadius: 30,
    background: "rgba(255,255,255,0.9)",
    border: `1px solid ${C.border}`,
    boxShadow: `0 24px 70px ${C.shadow}`,
    marginBottom: 18,
  },

  eyebrow: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 2.5,
    color: C.accent,
    fontWeight: 950,
    marginBottom: 8,
  },

  pageTitle: {
    margin: 0,
    color: C.text,
    fontSize: 34,
    fontWeight: 950,
    letterSpacing: -1,
  },

  pageSub: {
    margin: "8px 0 0",
    color: C.muted,
    lineHeight: 1.6,
    maxWidth: 720,
    fontSize: 14,
  },

  phoneticCard: {
    display: "none",
    minWidth: 170,
    padding: 14,
    borderRadius: 18,
    background: C.soft,
    border: `1px solid ${C.border}`,
    textAlign: "center",
  },

  section: {
    background: "rgba(255,255,255,0.94)",
    border: `1px solid ${C.border}`,
    borderRadius: 26,
    padding: "22px 24px",
    marginBottom: 16,
    boxShadow: `0 16px 44px ${C.shadow}`,
  },

  sectionTitle: {
    margin: "0 0 16px",
    color: C.text,
    fontSize: 12,
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: 1.8,
    paddingBottom: 10,
    borderBottom: `1px solid ${C.border}`,
  },

  sectionFooter: {
    marginTop: 22,
    paddingTop: 16,
    borderTop: `1px solid ${C.border}`,
    display: "flex",
    justifyContent: "flex-end",
  },

  subSectionLabel: {
    margin: 0,
    color: C.text,
    fontSize: 11,
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: 1.5,
  },

  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "14px 20px",
  },

  grid3: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "14px 20px",
    marginBottom: 14,
  },

  field: { display: "flex", flexDirection: "column", gap: 6 },
  fieldSpan2: { gridColumn: "1 / -1" },

  label: {
    fontSize: 11,
    fontWeight: 950,
    letterSpacing: 0.8,
    color: C.text,
    textTransform: "uppercase",
  },

  input: {
    width: "100%",
    minHeight: 44,
    padding: "0 14px",
    borderRadius: 14,
    border: `1px solid ${C.borderFocus}`,
    fontSize: 14,
    color: C.text,
    background: "#fff",
    outline: "none",
    boxSizing: "border-box",
  },

  select: {
    width: "100%",
    minHeight: 44,
    borderRadius: 14,
    border: `1px solid ${C.borderFocus}`,
    fontSize: 14,
    color: C.text,
    background: "#fff",
    outline: "none",
    boxSizing: "border-box",
    cursor: "pointer",
  },
  searchableSelectInput: {
    minHeight: 44,
    padding: "0 14px",
    fontSize: 14,
    color: C.text,
  },
  searchableSelectChevronButton: {
    height: 44,
    color: C.muted,
  },
  searchableSelectMenu: {
    borderRadius: 16,
    border: `1px solid ${C.borderFocus}`,
    background: "#fff",
  },
  searchableSelectOption: {
    borderRadius: 12,
    border: `1px solid ${C.border}`,
    color: C.text,
    fontSize: 14,
  },
  searchableSelectOptionActive: {
    borderColor: C.accent,
    background: C.accentLight,
    color: C.accentDark,
  },

  prefRangeGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  },

  signedInputWrap: {
    position: "relative",
  },

  prefRangeInputRow: {
    display: "grid",
    gridTemplateColumns: "1fr 120px",
    gap: 12,
    alignItems: "stretch",
  },

  signedInputPrefix: {
    position: "absolute",
    top: "50%",
    left: 14,
    transform: "translateY(-50%)",
    color: C.accent,
    fontSize: 18,
    fontWeight: 900,
    lineHeight: 1,
    pointerEvents: "none",
  },

  signedInput: {
    paddingLeft: 38,
  },

  autocompleteWrap: {
    position: "relative",
  },

  autocompleteMenu: {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: 0,
    right: 0,
    zIndex: 20,
    maxHeight: 240,
    overflowY: "auto",
    background: "#fff",
    border: `1px solid ${C.borderStrong}`,
    borderRadius: 14,
    boxShadow: "0 12px 32px rgba(37,41,53,0.14)",
    padding: 6,
  },

  autocompleteOption: {
    width: "100%",
    border: 0,
    background: "transparent",
    textAlign: "left",
    padding: "10px 12px",
    borderRadius: 10,
    color: C.text,
    cursor: "pointer",
  },

  autocompleteEmpty: {
    padding: "10px 12px",
    color: C.muted,
    fontSize: 13,
  },

  textarea: {
    minHeight: 92,
    paddingTop: 12,
    resize: "vertical",
    lineHeight: 1.6,
  },

  sectionHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
  },

  subCard: {
    position: "relative",
    padding: 18,
    marginBottom: 14,
    borderRadius: 22,
    background: C.card,
    border: `1px solid ${C.border}`,
  },

  familyCardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), 1fr))",
    gap: 16,
    marginBottom: 14,
  },

  familyMemberCard: {
    display: "grid",
    gap: 16,
    padding: 20,
    borderRadius: 28,
    background: `linear-gradient(155deg, #ffffff 0%, #fffaf8 58%, ${C.soft} 100%)`,
    border: `1px solid ${C.border}`,
    boxShadow: `0 24px 60px ${C.shadowLight}`,
  },

  familyCardHeader: {
    display: "flex",
    gap: 14,
    alignItems: "center",
  },

  familyAvatar: {
    width: 48,
    height: 48,
    borderRadius: 18,
    display: "grid",
    placeItems: "center",
    background: C.accent,
    color: "#fff",
    fontSize: 20,
    fontWeight: 950,
    boxShadow: `0 14px 28px ${C.shadowAccent}`,
  },

  familyIdentity: {
    minWidth: 0,
    flex: 1,
  },

  familyCardKicker: {
    color: C.accent,
    fontSize: 11,
    fontWeight: 950,
    letterSpacing: 1.4,
    textTransform: "uppercase",
  },

  familyCardTitle: {
    margin: "6px 0 0",
    color: C.text,
    fontSize: 23,
    lineHeight: 1.2,
    fontWeight: 950,
  },

  familyDetailGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
  },

  familyDetailItem: {
    minWidth: 0,
    padding: "11px 12px",
    borderRadius: 16,
    background: "rgba(255,255,255,0.78)",
    border: `1px solid ${C.border}`,
  },

  familyDetailLabel: {
    display: "block",
    color: C.muted,
    fontSize: 10,
    fontWeight: 950,
    letterSpacing: 1.1,
    textTransform: "uppercase",
  },

  familyDetailValue: {
    display: "block",
    marginTop: 5,
    color: C.text,
    fontSize: 14,
    fontWeight: 850,
    lineHeight: 1.35,
    overflowWrap: "anywhere",
  },

  familyListBlock: {
    display: "grid",
    gap: 8,
  },

  familyList: {
    display: "grid",
    gap: 7,
  },

  familyListItem: {
    padding: "10px 12px",
    borderRadius: 15,
    background: C.card,
    border: `1px solid ${C.borderSoft}`,
    color: C.text,
    fontSize: 13,
    fontWeight: 800,
    lineHeight: 1.4,
    overflowWrap: "anywhere",
  },
  familyListItemHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 8,
    marginBottom: 4,
  },
  familyListItemTitle: {
    color: C.text,
    fontSize: 13,
    fontWeight: 900,
    lineHeight: 1.35,
  },
  familyListItemBadge: {
    padding: "3px 8px",
    borderRadius: 999,
    background: C.accentLight,
    color: C.accentDark,
    fontSize: 10,
    fontWeight: 950,
    whiteSpace: "nowrap",
  },
  familyListItemLine: {
    color: C.muted,
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1.45,
    overflowWrap: "anywhere",
  },

  familyCardEmpty: {
    padding: "10px 12px",
    borderRadius: 15,
    background: "rgba(255,255,255,0.58)",
    border: `1px dashed ${C.borderStrong}`,
    color: C.muted,
    fontSize: 13,
    fontWeight: 800,
  },

  familyCardActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: "auto",
  },

  familyEditBtn: {
    padding: "9px 14px",
    borderRadius: 999,
    border: `1px solid ${C.borderStrong}`,
    background: C.card,
    color: C.accentDark,
    cursor: "pointer",
    fontWeight: 950,
  },

  familyDeleteBtn: {
    padding: "9px 14px",
    borderRadius: 999,
    border: `1px solid ${C.borderStrong}`,
    background: C.accentLight,
    color: C.accent,
    cursor: "pointer",
    fontWeight: 950,
  },

  modalBackdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    padding: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(37,41,53,0.42)",
    backdropFilter: "blur(10px)",
  },

  familyModal: {
    width: "min(1320px, calc(100vw - 32px))",
    maxHeight: "94vh",
    display: "grid",
    gridTemplateRows: "auto 1fr auto",
    overflow: "hidden",
    borderRadius: 30,
    background: C.card,
    border: `1px solid ${C.borderStrong}`,
    boxShadow: `0 30px 90px ${C.shadowStrong}`,
  },

  familyModalHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    padding: "22px 24px",
    background: `linear-gradient(135deg, ${C.soft} 0%, #ffffff 100%)`,
    borderBottom: `1px solid ${C.border}`,
  },

  familyModalKicker: {
    margin: 0,
    color: C.accent,
    fontSize: 11,
    fontWeight: 950,
    letterSpacing: 1.8,
    textTransform: "uppercase",
  },

  familyModalTitle: {
    margin: "6px 0 0",
    color: C.text,
    fontSize: 26,
    lineHeight: 1.15,
    fontWeight: 950,
  },

  familyModalSubtitle: {
    margin: "8px 0 0",
    color: C.muted,
    fontSize: 13,
    fontWeight: 750,
  },

  familyModalClose: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    border: "none",
    background: C.accentLight,
    color: C.accent,
    cursor: "pointer",
    fontSize: 20,
    fontWeight: 950,
  },

  familyModalBody: {
    overflowY: "auto",
    padding: 22,
    background: "#fffdfc",
  },

  familyEditorForm: {
    position: "relative",
    padding: 0,
  },

  familyModalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    padding: "16px 22px",
    background: C.card,
    borderTop: `1px solid ${C.border}`,
  },

  familyCancelBtn: {
    padding: "11px 18px",
    borderRadius: 999,
    border: `1px solid ${C.borderStrong}`,
    background: C.card,
    color: C.muted,
    cursor: "pointer",
    fontWeight: 950,
  },

  familyDoneBtn: {
    padding: "11px 22px",
    borderRadius: 999,
    border: "none",
    background: C.accent,
    color: "#fff",
    cursor: "pointer",
    fontWeight: 950,
    boxShadow: `0 12px 26px ${C.shadowAccent}`,
  },

  paymentHistoryTitle: {
    color: C.text,
    fontSize: 16,
    fontWeight: 900,
  },

  paymentHistoryMeta: {
    marginTop: 6,
    color: C.muted,
    fontSize: 13,
    lineHeight: 1.5,
  },

  miniCard: {
    position: "relative",
    padding: 14,
    marginTop: 10,
    borderRadius: 18,
    background: "#fff",
    border: `1px solid ${C.border}`,
  },

  relativeEduBox: {
    marginTop: 16,
    paddingTop: 14,
    borderTop: `1px dashed ${C.borderStrong}`,
  },

  addBtn: {
    marginLeft: "auto",
    padding: "8px 14px",
    borderRadius: 999,
    border: `1px dashed ${C.accent}`,
    background: "#fff",
    color: C.accent,
    cursor: "pointer",
    fontWeight: 900,
  },

  paymentActionRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: 8,
  },

  removeBtn: {
    position: "absolute",
    top: 10,
    right: 12,
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "none",
    background: C.accentLight,
    color: C.accent,
    cursor: "pointer",
    fontWeight: 950,
    fontSize: 18,
  },

  duplicateRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
  },

  duplicateTitle: { color: C.text, fontSize: 16 },
  duplicateSub: { margin: "6px 0 0", color: C.muted, lineHeight: 1.6, fontSize: 13 },

  duplicateActionBar: {
    marginTop: 14,
    display: "flex",
    alignItems: "center",
    gap: 12,
  },

  checkBtn: {
    height: 44,
    whiteSpace: "nowrap",
    padding: "0 18px",
    border: "none",
    borderRadius: 999,
    background: `linear-gradient(135deg, ${C.accent}, ${C.accentDark})`,
    color: "#fff",
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: `0 14px 30px ${C.shadowAccent}`,
  },

  safeBox: {
    marginTop: 14,
    padding: 12,
    borderRadius: 16,
    background: C.accentLight,
    color: C.accent,
    fontWeight: 900,
  },

  dupResults: { marginTop: 16, display: "flex", flexDirection: "column", gap: 12 },

  warnBox: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    padding: 14,
    borderRadius: 18,
    background: C.accentLight,
    border: `1px solid ${C.borderStrong}`,
    color: C.accent,
    fontSize: 14,
  },

  dupCard: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    padding: 16,
    borderRadius: 20,
    background: "#fff",
    border: `1px solid ${C.border}`,
  },

  dupCardActions: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexShrink: 0,
  },

  dupName: { fontSize: 16, fontWeight: 950, color: C.text },
  dupMeta: { marginTop: 4, color: C.muted, fontSize: 13 },
  reasonList: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 },

  reasonChip: {
    padding: "5px 8px",
    borderRadius: 999,
    background: C.soft,
    color: C.muted,
    fontSize: 11,
    fontWeight: 800,
  },

  scoreBox: {
    width: 72,
    height: 72,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    alignContent: "center",
    background: C.accentLight,
    color: C.accent,
    fontWeight: 950,
    flexShrink: 0,
  },

  overrideBox: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: 14,
    borderRadius: 18,
    background: C.soft,
    border: `1px solid ${C.border}`,
    color: C.text,
    fontSize: 14,
    fontWeight: 800,
  },

  chipBlock: { marginTop: 16 },

  multiDropdown: {
    border: `1px solid ${C.border}`,
    borderRadius: 18,
    background: "#fff",
    overflow: "hidden",
  },

  multiDropdownDisabled: {
    opacity: 0.7,
  },

  multiDropdownSummary: {
    listStyle: "none",
    cursor: "pointer",
    padding: "12px 14px",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    color: C.text,
    fontSize: 13,
    fontWeight: 800,
  },

  multiDropdownSummaryText: {
    flex: 1,
    minWidth: 0,
    paddingTop: 1,
  },

  multiDropdownPlaceholder: {
    color: C.text,
  },

  multiDropdownSummaryDisabled: {
    cursor: "not-allowed",
  },

  multiDropdownCount: {
    minWidth: 24,
    height: 24,
    padding: "0 7px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 999,
    background: C.accentLight,
    color: C.accent,
    fontSize: 11,
    fontWeight: 950,
  },

  multiDropdownMenu: {
    display: "grid",
    gap: 10,
    padding: "0 14px 14px",
    borderTop: `1px solid ${C.borderSoft}`,
  },

  multiDropdownSelectedPanel: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 10,
  },

  multiDropdownSelectedWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    minWidth: 0,
  },

  multiDropdownSelectedChip: {
    maxWidth: "100%",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    borderRadius: 999,
    border: `1px solid ${C.borderStrong}`,
    background: C.accentLight,
    color: C.text,
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1.2,
    cursor: "pointer",
  },

  multiDropdownSelectedChipMuted: {
    cursor: "default",
    background: C.soft,
    color: C.muted,
  },

  multiDropdownSelectedChipLabel: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 240,
  },

  multiDropdownSelectedChipRemove: {
    color: C.accent,
    fontSize: 12,
    fontWeight: 950,
    lineHeight: 1,
    flexShrink: 0,
  },

  multiDropdownOptions: {
    display: "grid",
    gap: 10,
    maxHeight: 220,
    overflowY: "auto",
    paddingRight: 4,
  },

  multiDropdownEmpty: {
    paddingTop: 10,
    color: C.muted,
    fontSize: 12,
    fontWeight: 800,
  },

  multiDropdownSearch: {
    marginTop: 12,
    minHeight: 40,
    borderRadius: 12,
    border: `1px solid ${C.border}`,
    background: "#fff",
    padding: "0 12px",
    color: C.text,
    fontSize: 13,
    fontWeight: 700,
    outline: "none",
  },

  cityTierFilterRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 10,
    marginBottom: 10,
  },

  cityTierFilterChip: {
    border: `1px solid ${C.borderStrong}`,
    background: "#fff",
    color: C.text,
    borderRadius: 999,
    padding: "7px 12px",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
  },

  cityTierFilterChipActive: {
    background: C.accentLight,
    color: C.accent,
    borderColor: C.accent,
  },

  checkboxOption: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 14,
    border: `1px solid ${C.border}`,
    background: "#fff",
    color: C.text,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 800,
  },

  checkboxOptionActive: {
    background: C.accentLight,
    borderColor: C.accent,
    boxShadow: `0 8px 18px ${C.borderSoft}`,
  },

  createOption: {
    width: "100%",
    textAlign: "left",
    borderStyle: "dashed",
    background: C.accentLight,
    color: C.accent,
  },

  createOptionIcon: {
    width: 18,
    height: 18,
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#fff",
    color: C.accent,
    fontWeight: 950,
  },

  checkboxInput: {
    width: 16,
    height: 16,
    accentColor: C.accent,
    margin: 0,
    flexShrink: 0,
  },

  tagGrid: { display: "flex", flexWrap: "wrap", gap: 10 },

  tagChip: {
    padding: "8px 14px",
    border: `1px solid ${C.borderFocus}`,
    borderRadius: 999,
    background: "#fff",
    cursor: "pointer",
    color: C.muted,
    fontWeight: 800,
    fontSize: 12,
  },

  tagActive: {
    background: C.accentDark,
    color: "#fff",
    borderColor: C.accentDark,
  },

  emptyHint: {
    color: C.muted,
    fontSize: 13,
    fontStyle: "italic",
    margin: 0,
  },

  photoHint: { fontSize: 12, color: C.muted, marginBottom: 12 },

  documentGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 },
  documentCard: {
    padding: 16,
    borderRadius: 20,
    border: `1px solid ${C.border}`,
    background: "#fff",
    color: C.text,
    boxShadow: `0 10px 22px ${C.shadowLight}`,
  },
  documentCardDeleting: {
    opacity: 0.6,
  },
  documentCardLink: {
    display: "block",
    textDecoration: "none",
    color: C.text,
  },
  documentCardType: {
    fontSize: 11,
    fontWeight: 950,
    letterSpacing: 1.2,
    textTransform: "uppercase",
    color: C.accent,
    marginBottom: 8,
  },
  documentCardName: {
    fontSize: 14,
    fontWeight: 900,
    color: C.text,
    wordBreak: "break-word",
  },
  documentCardMeta: {
    marginTop: 6,
    fontSize: 12,
    color: C.muted,
    fontWeight: 700,
  },
  documentCardActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginTop: 12,
  },
  documentActionLink: {
    fontSize: 12,
    fontWeight: 900,
    color: C.accent,
    textDecoration: "none",
  },
  documentDeleteBtn: {
    border: "none",
    borderRadius: 999,
    background: C.error,
    color: "#fff",
    padding: "8px 14px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 900,
  },
  documentDeleteBtnDisabled: {
    opacity: 0.7,
    cursor: "wait",
  },
  documentUploadHint: {
    marginTop: 8,
    fontSize: 12,
    color: C.muted,
    fontWeight: 700,
    lineHeight: 1.45,
  },
  documentUploadActions: {
    marginTop: 14,
    display: "flex",
    justifyContent: "flex-end",
  },
  documentSaveBtn: {
    minWidth: 180,
  },
  docList: { display: "grid", gap: 10, marginTop: 14 },
  docItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    padding: "14px 16px",
    borderRadius: 18,
    background: "#fff",
    border: `1px solid ${C.border}`,
  },
  docName: { fontSize: 13, fontWeight: 900, color: C.text, wordBreak: "break-word" },
  docMeta: { marginTop: 4, fontSize: 11, color: C.muted, fontWeight: 700 },
  docRemove: {
    border: "none",
    borderRadius: 999,
    background: C.text,
    color: "#fff",
    padding: "8px 14px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 900,
    flexShrink: 0,
  },

  viewProfileBtn: {
    height: 38,
    padding: "0 14px",
    borderRadius: 999,
    border: `1px solid ${C.border}`,
    background: "#fff",
    color: C.accentDark,
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },

  photoGrid: { display: "flex", flexWrap: "wrap", gap: 12 },

  photoThumb: {
    position: "relative",
    borderRadius: 16,
    overflow: "hidden",
    border: `2px solid ${C.borderStrong}`,
    cursor: "pointer",
    transition: "border-color .15s, opacity .15s",
    background: C.soft,
  },

  photoThumbPrimary: {
    borderColor: C.accent,
    boxShadow: `0 0 0 4px ${C.borderSoft}`,
  },

  photoImg: { width: 132, height: 132, objectFit: "cover", display: "block" },

  photoBadgePrimary: {
    position: "absolute",
    bottom: 8,
    left: 8,
    background: C.accent,
    color: "#fff",
    fontSize: 10,
    fontWeight: 900,
    padding: "4px 8px",
    borderRadius: 999,
  },

  photoBadgeUpdating: {
    position: "absolute",
    bottom: 8,
    left: 8,
    background: C.text,
    color: "#fff",
    fontSize: 10,
    fontWeight: 900,
    padding: "4px 8px",
    borderRadius: 999,
  },

  photoBadgeNew: {
    position: "absolute",
    top: 8,
    left: 8,
    background: C.green,
    color: "#fff",
    fontSize: 10,
    fontWeight: 900,
    padding: "4px 8px",
    borderRadius: 999,
  },

  photoActions: {
    position: "absolute",
    inset: 0,
    background: "rgba(37,41,53,0.46)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0,
    transition: "opacity .15s",
  },

  photoDeleteBtn: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    border: "none",
    background: C.error,
    color: "#fff",
    cursor: "pointer",
    fontWeight: 950,
    fontSize: 18,
  },

  photoRemoveNew: {
    position: "absolute",
    top: 8,
    right: 8,
    width: 26,
    height: 26,
    borderRadius: "50%",
    border: "none",
    background: "rgba(37,41,53,0.72)",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 950,
    fontSize: 17,
    lineHeight: "26px",
  },

  dropZone: {
    border: `2px dashed ${C.borderBold}`,
    borderRadius: 22,
    padding: "28px 0",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 7,
    cursor: "pointer",
    background: C.blueLight,
  },

  dropIcon: { fontSize: 34, color: C.accent, fontWeight: 900 },
  dropText: { fontSize: 15, color: C.text, fontWeight: 950 },
  dropSub: { fontSize: 12, color: C.muted, fontWeight: 700 },
  processingDropZone: {
    opacity: 0.78,
    cursor: "progress",
  },
  processingInlineNote: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    marginTop: 4,
    fontSize: 12,
    color: C.accent,
    fontWeight: 900,
  },
  processingSpinner: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    border: `2px solid ${C.border}`,
    borderTopColor: C.accent,
    display: "inline-block",
    animation: "spin 0.8s linear infinite",
  },

  infoBanner: {
    padding: 12,
    borderRadius: 16,
    background: C.accentLight,
    border: `1px solid ${C.borderStrong}`,
    color: C.accent,
    fontSize: 13,
    fontWeight: 850,
    marginBottom: 12,
  },

  saveError: {
    color: C.error,
    background: C.accentLight,
    borderRadius: 16,
    padding: 14,
    fontWeight: 900,
    marginBottom: 14,
  },

  saveNotice: {
    color: C.accentDark,
    background: "#eef8ef",
    border: `1px solid ${C.border}`,
    borderRadius: 16,
    padding: 14,
    fontWeight: 800,
    marginBottom: 14,
  },
  snackbar: {
    position: "fixed",
    right: 24,
    bottom: 24,
    maxWidth: 420,
    zIndex: 1000,
    color: "#fff",
    background: "linear-gradient(135deg, #1b8f4b, #13763d)",
    borderRadius: 18,
    padding: "14px 18px",
    fontWeight: 800,
    boxShadow: "0 20px 40px rgba(19, 118, 61, 0.28)",
  },

  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
    paddingTop: 4,
  },

  btnPrimary: {
    height: 44,
    padding: "0 24px",
    borderRadius: 999,
    border: "none",
    background: `linear-gradient(135deg, ${C.accent}, ${C.accentDark})`,
    color: "#fff",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 950,
    boxShadow: `0 14px 30px ${C.shadowAccent}`,
  },

  btnSecondary: {
    height: 44,
    padding: "0 22px",
    borderRadius: 999,
    border: `1px solid ${C.borderFocus}`,
    background: "#fff",
    color: C.muted,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 900,
  },

  btnDisabled: { opacity: 0.5, cursor: "not-allowed" },
};
